import { Diagnostics } from "@base/diagnostics/Diagnostics";
import { IntersectionUtil } from "@base/math/IntersectionUtil";
import { getLineIntersection } from "@base/math/Line2";
import { Line3 } from "@base/math/Line3";
import { IMat4, Mat4 } from "@base/math/Mat4";
import { MathUtil, Radian } from "@base/math/MathUtil";
import { Plane } from "@base/math/Plane";
import { Ray3 } from "@base/math/Ray3";
import { IVec2, Vec2 } from "@base/math/Vec2";
import { IVec3, Vec3 } from "@base/math/Vec3";
import { cached, CacheUtil } from "@base/util/Cache";
import { gec, gtc, ltc } from "@base/util/Comma";
import { ImmHelper } from "@base/util/Immutable";
import { getKeys, mapRecordToArray, PartialRecord } from "@base/util/TypeUtil";
import { RopeHelper } from "@biz/articles/RopeHelper";
import { Debug } from "@biz/Debug";
import { Access } from "@biz/definitions/AccessDefinitions";
import { ArticleChooser } from "@biz/definitions/ArticleChooser";
import { IArticleAffiliation, IArticleWithCount, IMountArticle, isPoleArticleId } from "@biz/definitions/ArticleDefinitions";
import { ArticleFeatureMap } from "@biz/definitions/ArticleFeatureMap";
import { FL } from "@biz/definitions/FL";
import { kederInfos, KederSailMountType, KederSize, kederSizes } from "@biz/definitions/KederDefinitions";
import { heightChangeAlternativeMap, heightChangeMaterialMap, MaterialMap } from "@biz/definitions/MaterialMap";
import { IMinMax, MinMax } from "@biz/definitions/MinMax";
import {
    allFallStoppArticles, allSnapWincheArticleNumbers, backenklemmeLoseArticleId, calcFallstoppArticles, crankRadiusReserve, fallstoppMap, IAxlePos, ICoilUpSizeInfo, IExtendedMountInfo, IKnotPoints, INetPositionOptions, IPointMountInfo, MountArticle,
    RemoteChannelCount, seilKlemmeMap, seilStoppWinchArticleId, sicherheitsKlemmenMap, snapWinches, SunSailTopologyOld, winchInfos
} from "@biz/definitions/MountDefinitons";
import { getPoleMountArticleInfo, PoleMountArticle } from "@biz/definitions/PoleMountDefinitions";
import { Product } from "@biz/definitions/Product";
import { FeatureLevel } from "@biz/definitions/ProductDefinitions";
import { ProductVersion, Pvi } from "@biz/definitions/ProductVersion";
import { SST } from "@biz/definitions/SST";
import { StandardTemplate } from "@biz/definitions/StandardTemplate";
import {
    AxleColor, axleInfos, AxleMeasureType, AxlePoleWallMountType, AxleType, ClothTextureTypes, ClothType, ConcavityType, CornerType, CSPositioning, DoubleMountType, DroppoleLengthMode, EdgeMountItemType, EdgeName, EdgePositioningMode, EdgeSeamType,
    GrommetType, HeightChangeSlideMeasures, IAxleInfo, IEdgeMountItemReal, IRevolvingRopeInfo, IRingschraube, IRollShackleConfig, ISunSailData, MaterialGroup, materialInfos, MaterialType, PointName, PointType, PoleId, PoleIndex, PoleMountType,
    PoleType, PositionMode, PylonType, pylonTypes, RemoteType, Ringschrauben, SailBordersType, SailMount, StandardTemplateId, SunSailSides, SunSailTextureType, SunSailType, SystemAxleVersionOld, TwistingSetType, WaterDirection,
    WinchHeightChangePriority
} from "@biz/definitions/SunSailDefinitions";
import { getArticleForCornerType, getEdgeName, getEdgePoleIds, getPointName, getPointNameSmall, getPoleId, getSideName } from "@biz/definitions/SunSailDefinitions.util";
import { IndirectPylonLogic } from "@biz/items/sunSail/IndirectPylonLogic";
import { AxleLogic } from "@biz/items/sunSail/logic/AxleLogic";
import { ClampLogic } from "@biz/items/sunSail/logic/ClampLogic";
import { EdgePositioningLogic, IEdgePositionOptions } from "@biz/items/sunSail/logic/EdgePositioningLogic";
import { FlexLogic } from "@biz/items/sunSail/logic/FlexLogic";
import { HeightChangeLogic } from "@biz/items/sunSail/logic/HeightChangeLogic";
import { LogicAMCX4 } from "@biz/items/sunSail/logic/LogicAMCX4";
import { LogicFixed } from "@biz/items/sunSail/logic/LogicFixed";
import { LogicS } from "@biz/items/sunSail/logic/LogicS";
import { IPoleLengthPosOptions, IPoleSegmentsExtended, PoleHelper } from "@biz/items/sunSail/logic/PoleHelper";
import { PoleLogic } from "@biz/items/sunSail/logic/PoleLogic";
import { Poles } from "@biz/items/sunSail/logic/Poles";
import { SandyLogic } from "@biz/items/sunSail/logic/SandyLogic";
import { StandardLogic } from "@biz/items/sunSail/logic/StandardLogic";
import { SunSailEdges } from "@biz/items/sunSail/logic/SunSailEdges";
import { SunSailMath } from "@biz/items/sunSail/logic/SunSailMath";
import { SunSailTopology } from "@biz/items/sunSail/logic/SunSailTopology";
import { SunSailGroup, SunSailGroup3, SunSailGroup4 } from "@biz/items/sunSail/logic/SunSailTriangle";
import { TriangleHelper } from "@biz/items/sunSail/logic/TriangleHelper";
import { OnePylonSystemLogic } from "@biz/items/sunSail/OnePylonSystemLogic";
import { PoleReducers, SunSailReducers } from "@biz/items/sunSail/SunSailReducers";
import { LanguageId } from "@biz/language/Languages";
import { ClothData, IClothData } from "@biz/logic/ClothData";
import { getChecked } from "@biz/logic/getChecked";
import { MotorLogic } from "@biz/logic/MotorLogic";
import { ISail2dForm } from "@biz/models/Sail2dDefinitions";
import { PD } from "@biz/PD";
import { IProductDescriptor } from "@biz/ProductDescriptor";
import { ProjectCheckErrorType } from "@biz/projectCalculation/ProjectDefinitions";
import { calcMaxKederPriority } from "@biz/reducers/EdgeReducer";
import { ISCSunSailData } from "@biz/sc/sunSail/ISCSunSailData";

export const possibleProfilePoleLengths = [2.7, 3.7];

export const ONE_AXLE_WALL_PLATES = ["00E13002080", "00E13002081"];
export const THREAD_TENSIONER_DIST = 0.22;

export interface ITotalMaxDistInOut {
  totalMaxDistIn: number;
  totalMaxDistOut: number
}

export interface IArticleAccessory {
  articleId: string;
  optional: boolean;
}

export class SunSailLogic {
  static getMinAngleDeg(sunSailType: SunSailType, _featureLevel: FeatureLevel): number {
    if (sunSailType === SunSailType.Fixed)
      return 15;
    return 0;
  }

  static getMotorArticle(sunSail: ISunSailData | ISCSunSailData | ISail2dForm): string | undefined {
    const sunSailType = PD.productToSunSailType(sunSail);
    if (!MotorLogic.hasMotor(sunSailType))
      return "";
    return sunSail.articleSlots?.motor;
  }

  static getMotorCable(sunSail: ISunSailData | ISCSunSailData | ISail2dForm): string | undefined {
    if (!SunSailLogic.getMotorArticle(sunSail))
      return "";
    return getChecked({
      selection: sunSail.articleSlots?.motorCable,
      validItems: SunSailLogic.calcPossibleMotorCables(sunSail),
      emptySelection: "__selectionRequired__"
    })
  }


  static calcPossibleMotorCables(sunSail: ISunSailData | ISCSunSailData | ISail2dForm): string[] {
    if (!SunSailLogic.getMotorArticle(sunSail))
      return [];
    return ArticleFeatureMap.getShopFeatureArticles("motorCable");
  }


  static getMotorRemoteType(ssd: ISunSailData): RemoteType | undefined {
    if (!MotorLogic.hasMotor(ssd.sunSailType))
      return undefined;
    return MotorLogic.getMotorInfo(ssd.articleSlots.motor)?.remoteType
  }

  static calcRemoteChannelCount(product: ISunSailData): RemoteChannelCount {
    let heightChangeChannels = 0;
    const motorRemoteType = SunSailLogic.getMotorRemoteType(product);
    const motorChannels = motorRemoteType ? 1 : 0; // sunSailData.GetWishListCount("71003A014", -1) + sunSailData.GetWishListCount("00E13001384", -1);// Rohrmoter mit Funkempfänger

    for (let i = 0; i < Poles.getCount(product); i++) {
      const heightChangeArticle = SunSailLogic.getHeightChangeArticle(product, i);
      if (heightChangeArticle && HeightChangeLogic.getHeightChangeRemoteType(heightChangeArticle))
        heightChangeChannels++;
    }
    return {
      motorChannels,
      totalChannels: motorChannels + heightChangeChannels,
      sunWindSensorCount: 0,
      motorRemoteType
    }
  }

  static calcPylonRopeLength(sunSailData: ISunSailData, poleId: PoleId): number {
    if (SunSailLogic.getPoleType(sunSailData, poleId)) {
      const maxDistInOut = SunSailLogic.calcTotalMaxDistInOut(sunSailData, poleId, { forPoleOnly: true })
      return Math.ceil(maxDistInOut.totalMaxDistIn - 7)
    }
    return 0;
  }

  static calcPressureForPole(sunSailData: ISunSailData, poleId: PoleId): number {
    if (!Poles.isCornerPole(poleId))
      return 0;

    return SunSailLogic.calcPressureForPylon(sunSailData.sunSailType, poleId, !!SunSailLogic.getPolePylonType(sunSailData, poleId), SunSailLogic.calcPylonCount(sunSailData))
  }

  static calcPressureForPylon(sunSailType: SunSailType, poleId: PoleId, isPylon: boolean, pylonCount: number): number {
    if (pylonCount === 0 || !isPylon)
      return 0;

    if (sunSailType === SunSailType.SolidayC) {
      if (pylonCount === 2) {
        return (poleId === 0 || poleId === 2) ? 85 : 95;
      } else if (pylonCount === 1) {
        return (poleId === 0 || poleId === 2) ? 0 : 120;
      }
    } else if (sunSailType === SunSailType.SolidayTwin) {
      return 75;
    } else if (sunSailType === SunSailType.SolidayCS) {
      return 75;
    }
    return 0;
  }

  static hasCornerShackle(sunSail: ISunSailData, oppositePoleIndex: PoleId): boolean {
    return SunSailLogic.getCornerArticleId(sunSail, oppositePoleIndex) === "00E13001416";
  }

  static hasTwoDistInSides(sunSailType: SunSailType, poleId: PoleId): boolean {
    if (sunSailType === SunSailType.SolidayFlex)
      return false;
    if (PoleLogic.isAxle(sunSailType, poleId) && !SST.is(sunSailType, SST.CsXs))
      return true;
    return false;
  }

  static hasDistOut(sunSailType: SunSailType, poleId: PoleId): boolean {
    if (AxleLogic.hasAxle(sunSailType) && !PoleLogic.isAxle(sunSailType, poleId))
      return true;
    return false;
  }

  static isAxleValid(axleVector: IVec3): boolean {
    return Vec3.len(axleVector) >= 0.005;
  }

  static calcTopAxlePointsDistance(sunSailType: SunSailType, mountPositions: IVec3[]): number {
    const topology = SunSailTopology.calcTopology(sunSailType, mountPositions.length);
    if (topology.axleIndices?.length !== 2)
      return 0;
    if (topology.groups.length !== 2)
      return 0;
    const axleFirst = mountPositions[topology.axleIndices[0]];
    const axleSecond = mountPositions[topology.axleIndices[1]];
    const a = SunSailMath.calcNearestAxlePoint(axleFirst, axleSecond, mountPositions[topology.groups[0].nonAxleIndices[0]]);
    const b = SunSailMath.calcNearestAxlePoint(axleFirst, axleSecond, mountPositions[topology.groups[1].nonAxleIndices[0]]);
    return Vec3.len(a, b);
  }

  static calcFactorZ(sunSail: ISunSailData): number {
    const mountPositions = SunSailLogic.calcMountPositions(sunSail, 0, undefined);
    if (mountPositions.length !== 4)
      return 0;
    const topAxlePointsDistance = SunSailLogic.calcTopAxlePointsDistance(sunSail.sunSailType, mountPositions);
    const logicalAxleLength = SunSailLogic.calcAxleLength(sunSail, "logical");
    return 100 * topAxlePointsDistance / logicalAxleLength;
  }

  static calcDeltaH(mountPositions: IVec3[]): number {
    if (mountPositions.length !== 4)
      return 0;

    const rayAC = Ray3.fromPoints(mountPositions[0], mountPositions[2]);
    const rayBD = Ray3.fromPoints(mountPositions[1], mountPositions[3]);
    const points = Ray3.calcClosestPointOnTwoRays(rayAC, rayBD);
    const deltaH = (points ? Vec3.len(points[0], points[1]) : 0) * 100; // cm
    return deltaH;
  }

  static calcTopgraphyOld(sunSailType: SunSailType, poleCount: number): SunSailTopologyOld {
    const t = SunSailTopology.calcTopology(sunSailType, poleCount);
    return {
      netGroups: t.groups.map((g) => g.poleIndices.length === 3 ? new SunSailGroup3(g.poleIndices) : new SunSailGroup4(g.poleIndices)),
      outerGroups: t.groups.map((g) => g.poleIndices.length === 3 ? new SunSailGroup3(g.poleIndices) : new SunSailGroup4(g.poleIndices)),
    };
  }

  static calcSunSailAreaCloth(sunSail: ISunSailData | ISCSunSailData): number {
    const sunSailType = PD.productToSunSailType(sunSail);
    if (SST.is(sunSailType, SST.Sandy)) {
      if (Product.isProductSC(sunSail)) {
        throw new Error("Not supported for ISCSunSailData")
      }

      return SandyLogic.calcSandyClothArea(sunSail) || 0;
    }
    return SunSailLogic.calcSunSailAreaTop(sunSail);
  }

  static calcSunSailAreaToPay(sunSail: ISunSailData | ISCSunSailData): number {
    const sunSailType = PD.productToSunSailType(sunSail);
    if (SST.is(sunSailType, SST.Sandy)) {
      if (Product.isProductSC(sunSail)) {
        throw new Error("Not supported for ISCSunSailData")
      }

      return SandyLogic.calcSandyObbArea(sunSail)?.area || 0;
    }
    return SunSailLogic.calcSunSailAreaTop(sunSail);
  }

  static calcSunSailAreaTop(sunSail: ISunSailData | ISCSunSailData): number {

    if (Product.isProductSC(sunSail)) {
      console.warn("CalcSunSailARe for ISCDATA todo")
      return 30;
    }

    const sunSailType = PD.productToSunSailType(sunSail)
    Debug.assert(SST.isSail(sunSailType), "calcSunSailAreaFromPoles required a sunSail")
    if (sunSailType === SunSailType.Standard) {
      const standardSize = SunSailLogic.calcStandardSize(sunSail)
      return StandardTemplate.getArea(standardSize);
    }
    const poleCount = Poles.getCount(sunSail);
    const topology = SunSailTopology.calcTopology(sunSailType, poleCount);

    let area = 0;
    for (let iGroup = 0; iGroup < topology.groups.length; iGroup++) {
      area += SunSailLogic.calcSunSailAreaForGroup(sunSail, iGroup);
    }
    return area;
  }

  static calcSunSailGroupAreas(sunSail: ISunSailData): number[] {
    Debug.assert(SST.isSail(sunSail.sunSailType), "calcSunSailAreaFromPoles required a sunSail")
    const { sunSailType } = sunSail;
    const poleCount = Poles.getCount(sunSail);
    const topology = SunSailTopology.calcTopology(sunSailType, poleCount);

    const areas: number[] = [];
    for (let iGroup = 0; iGroup < topology.groups.length; iGroup++) {
      areas.push(SunSailLogic.calcSunSailAreaForGroup(sunSail, iGroup));
    }
    return areas;
  }

  static calcSunSailAreaForGroup(sunSail: ISunSailData, groupIndex: number): number {
    const sunSailType = PD.productToSunSailType(sunSail)
    const topology = SunSailTopology.calcTopology(sunSailType, Poles.getCount(sunSail));
    const g = topology.groups[groupIndex];
    const netPositions = SunSailLogic.calcNetPositions(sunSail, groupIndex);

    const groupArea = SunSailLogic.calcSunSailAreaForNetGroup(netPositions, g.poleIndices);
    return groupArea;
  }

  static calcSunSailTeflonArea(sunSail: ISunSailData): number {
    const netPositionGroups = SunSailLogic.calcNetPositionGroups(sunSail, {});

    if (sunSail.sunSailType === SunSailType.SolidayC && Poles.getCount(sunSail) === 4) {
      const ab = Vec3.len(netPositionGroups[0][0], netPositionGroups[0][1])
      const bc = Vec3.len(netPositionGroups[0][1], netPositionGroups[0][2])
      const cd = Vec3.len(netPositionGroups[1][2], netPositionGroups[1][3])
      const ad = Vec3.len(netPositionGroups[1][0], netPositionGroups[1][3])

      const area = (ab + bc + cd + ad) * 0.4 + 5;
      return area;
    } else {
      throw new Error("Not supported!")
    }
  }


  static calcSunSailCircumference(sunSail: ISunSailData): number {
    const netPositionGroups = SunSailLogic.calcNetPositionGroups(sunSail, {});
    const topology = SunSailTopology.calcTopology(sunSail.sunSailType, Poles.getCount(sunSail));

    let l = 0;
    const sides = topology.sides.filter((s) => s.netBorder);
    for (const side of sides) {
      for (const gi of side.groupIndices) {
        const fi = netPositionGroups[gi][side.first]
        const si = netPositionGroups[gi][side.second]
        l += Vec3.len(fi, si);
      }
    }
    return l;
  }

  static calcPylonCount(sunSail: ISunSailData | ISCSunSailData): number {
    let pylonCount = 0;
    for (let poleIndex = 0; poleIndex < Poles.getCount(sunSail); poleIndex++) {
      const pylonType = SunSailLogic.getPolePylonType(sunSail, poleIndex);
      if (pylonType)
        pylonCount++;
    }
    return pylonCount;
  }

  static calcAxlePylonCount(sunSail: ISunSailData | ISCSunSailData | ISail2dForm): number {
    const sunSailType = PD.productToSunSailType(sunSail)
    let pylonCount = 0;
    for (let i = 0; i < Poles.getCount(sunSail); i++) {
      const pylonType = SunSailLogic.getPolePylonType(sunSail, i);
      if (pylonType && PoleLogic.isAxle(sunSailType, i))
        pylonCount++;
    }
    return pylonCount;
  }

  static calcPylonPoles(sunSail: ISunSailData): PoleIndex[] {
    const pylonPoles: number[] = [];
    for (let i = 0; i < Poles.getCount(sunSail); i++) {
      const pylonType = SunSailLogic.getPolePylonType(sunSail, i);
      if (pylonType)
        pylonPoles.push(i);
    }
    return pylonPoles;
  }

  static getAxleColor(sunSail: ISunSailData): AxleColor | undefined {
    return getChecked({
      selection: sunSail.axleColor,
      validItems: SunSailLogic.getAvailableAxleColors(sunSail.sunSailType),
      emptySelection: "__selectionRequired__"
    });
  }

  static solidayCAxleColors: AxleColor[] = ["metal", "black"];
  static emptyArray: AxleColor[] = [];
  static getAvailableAxleColors(sunSailType: SunSailType): AxleColor[] {
    if (sunSailType === SunSailType.SolidayC)
      return SunSailLogic.solidayCAxleColors;
    return SunSailLogic.emptyArray
  }

  static getSailMountArticle(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId): string | undefined {
    let selection = Poles.getPole(sunSail, poleId).articleSlots?.sailMount;
    const validItems = SunSailLogic.getPossibleSailMountArticles(sunSail, poleId);

    // upgrade backenklemme
    if (selection === "00E13000057" && !validItems.includes(selection)) {
      selection = this.getBackenklemmenSet(sunSail, poleId);
    }

    return getChecked({
      selection,
      validItems,
      emptySelection: "__selectionRequired__"
    });
  }

  static getSailMountAccessories(sunSail: ISunSailData, poleIndex: number): IArticleAccessory[] {
    const accesories: IArticleAccessory[] = [];

    const poleType = SunSailLogic.getPoleType(sunSail, poleIndex);
    const material = SunSailLogic.getPoleMaterial(sunSail, poleIndex);

    const mount = SunSailLogic.getSailMount(sunSail, poleIndex);
    if (mount === "snap") {
      if (poleType === PoleType.Wall)
        accesories.push({ articleId: "00E13000777", optional: true }) // Grundplatte Wandbefestigung aus Edelstahl für Snapsystem
    }

    if (mount === "snap" || mount === "rope") {
      accesories.push({ articleId: "00E13000134", optional: true }) // Seilhalterung für Bedienseil
    }

    const mountArticle = SunSailLogic.getSailMountArticle(sunSail, poleIndex);
    if (mountArticle === backenklemmeLoseArticleId) { // Backenklemme lose
      if (poleType === PoleType.Wall) {
        accesories.push({ articleId: "00E13001146", optional: true }); // accesories.push("00E13000088"); https://plaspack.atlassian.net/jira/software/projects/APP/issues/APP-1323
      }
      if (PoleLogic.isAlu(material)) {
        accesories.push({ articleId: "00E13000376", optional: true }); // https://plaspack.atlassian.net/browse/APP-1323
        // accesories.push("00E13001905"); // https://plaspack.atlassian.net/browse/APP-1323
      } else if (PoleLogic.isSteel(material)) {
        accesories.push({ articleId: "00E13000375", optional: true }); // https://plaspack.atlassian.net/browse/APP-1323
        // accesories.push("00E13001906"); // https://plaspack.atlassian.net/browse/APP-1323
      }
      return accesories;
    }
    return accesories;
  }

  static getSailPulleyArticle(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): string | undefined {
    return getChecked({
      selection: Poles.getPole(sunSail, poleIndex).articleSlots?.sailPulley,
      validItems: SunSailLogic.getPossibleSailPulleyArticles(sunSail, poleIndex),
      emptySelection: "__selectionRequired__"
    });
  }

  static getSailMountWinch(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId): string | undefined {
    return getChecked(
      {
        selection: Poles.getPole(sunSail, poleId).articleSlots?.sailMountWinch,
        validItems: SunSailLogic.getPossibleSailMountWinches(sunSail, poleId),
        emptySelection: ""
      }
    );
  }

  static getPossibleSailMountWinches(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId): string[] {
    const sailMount = SunSailLogic.getSailMount(sunSail, poleId);
    if (sailMount === "snap")
      return snapWinches;
    return [];
  }

  static calcHeightChangeCount(sunSail: ISunSailData): number {
    let count = 0;
    for (let i = 0; i < Poles.getCount(sunSail); i++) {
      if (SunSailLogic.getHeightChangeArticle(sunSail, i))
        count++;
    }
    return count;
  }

  static calcSailCoilUpCount(sunSail: ISunSailData): number {
    let count = 0;
    for (let i = 0; i < Poles.getCount(sunSail); i++) {
      if (SunSailLogic.getSailCoilUpArticle(sunSail, i))
        count++;
    }
    return count;
  }

  static calcTriangleAreaByIndices(netPositions: IVec3[], ia: number, ib: number, ic: number): number {
    const a = netPositions[ia];
    const b = netPositions[ib];
    const c = netPositions[ic];
    return TriangleHelper.calcArea(Vec3.len(a, b), Vec3.len(b, c), Vec3.len(a, c));
  }

  static calcSunSailAreaForNetGroup(netPositions: IVec3[], poleIndices: number[]): number {
    let area = 0;
    for (let i = 1; i < poleIndices.length - 1; i++) {
      area += SunSailLogic.calcTriangleAreaByIndices(netPositions, poleIndices[0], poleIndices[i], poleIndices[i + 1]);
    }
    return area;
  }

  static calcHeightC(a: number, b: number, c: number): number {
    const area = TriangleHelper.calcArea(a, b, c);
    if (isNaN(area)) return Number.NaN;
    return (2 * area) / c;
  }

  static getStaticNarrowFactor(sunSailType: SunSailType): number {
    switch (sunSailType) {
      case SunSailType.SolidayXS:
        return 0.025; // 180 / 6000; // 0.025; ?
      case SunSailType.SolidayCS: {
        return 507.0 / 7000.0;
      }
      case SunSailType.SolidayTwin: {
        return 507.0 / 7000.0;
      }
    }
    return 0.0;
  }

  static getAxleTypeInternal(sunSailType: SunSailType, version: ProductVersion | undefined, axleType: SystemAxleVersionOld | AxleType | undefined): AxleType | undefined {
    switch (sunSailType) {
      case SunSailType.SolidayA:
        return Pvi.is(version, "2024") ? "One_65" : "A_50";
      case SunSailType.SolidayOne:
        return "One_65";
      case SunSailType.SolidayFlex:
        return "Flex";
      case SunSailType.SolidayM:
        return Pvi.eq(version, "2024") ? "M_30" : "M_23";
      case SunSailType.SolidayX:
      case SunSailType.SolidayXS:
        return "X_84";
      case SunSailType.SolidayC:
        if (axleType) {
          switch (axleType) {
            case "pre2022": return "C_84";
            case "2022": return "C_90";
            default: return axleType;
          }
        }
        return !version ? "C_84" : "C_90";
      case SunSailType.SolidayCS:
      case SunSailType.SolidayTwin:
        if (axleType) {
          switch (axleType) {
            case "pre2022": return "CS_110";
            case "2022": return "CS_110_02";
            default: return axleType;
          }
        }
        return "CS_110_02";
    }
    return undefined;
  }

  static getAxleType(sunSail: ISunSailData | ISail2dForm): AxleType | undefined {
    return SunSailLogic.getAxleTypeInternal(sunSail.sunSailType, sunSail.version, sunSail.axleType);
  }


  static getAxleInfo(sunSail: ISunSailData | ISail2dForm): IAxleInfo | undefined {
    const axleType = SunSailLogic.getAxleType(sunSail);
    if (!axleType)
      return undefined;
    return axleInfos[axleType];
  }

  static getAxle(sunSail: ISunSailData | ISail2dForm): { type: AxleType, info: IAxleInfo } | undefined {
    const axleType = SunSailLogic.getAxleType(sunSail);
    if (!axleType)
      return undefined;
    return { type: axleType, info: axleInfos[axleType] }
  }

  static getAxleDiameter(sunSail: ISunSailData): number {
    return this.getAxleInfo(sunSail)?.diameter || 0;
  }

  /** @deprecated */
  static getAxleDiameterOld(sunSailType: SunSailType, version: ProductVersion | undefined): number {
    switch (sunSailType) {
      case SunSailType.SolidayA:
        return Pvi.is(version, "2024") ? 65 : 50;
      case SunSailType.SolidayOne:
        return 65;
      case SunSailType.SolidayFlex:
        return 13;
      case SunSailType.SolidayM:
        return 35;
      case SunSailType.SolidayC:
        return !version ? 84 : 90; // 84?
      case SunSailType.SolidayCS:
      case SunSailType.SolidayTwin:
        return 110;
      case SunSailType.SolidayX:
        return 84;
      case SunSailType.SolidayXS:
        return 84;
    }
    return 110;
  }

  static getAxleDiameterForRender(sunSail: ISunSailData): number {
    if (sunSail.sunSailType === SunSailType.SolidayFlex)
      return SunSailLogic.getAxleDiameter(sunSail) * 0.001;
    return SunSailLogic.getAxleDiameter(sunSail) * 0.001 + 0.01
  }

  static calcAxleDeltaY(sunSail: ISunSailData, isTop: boolean): number {
    if (sunSail.sunSailType === SunSailType.SolidayFlex)
      return SunSailLogic.getAxleDiameter(sunSail) * 0.5 * 0.001 * (isTop ? 1 : -1);
    return (SunSailLogic.getAxleDiameter(sunSail) * 0.5 * 0.001) * (isTop ? 1 : -1);
  }


  static needsSailCoilUp(sunSailType: SunSailType): boolean {
    switch (sunSailType) {
      case SunSailType.SolidayA:
      case SunSailType.SolidayM:
      case SunSailType.SolidayX:
      case SunSailType.SolidayXS:
        return true;
    }
    return false;
  }

  static getAxleSystemBaseArticleId(sunSailType: SunSailType, poleCount: number, version: ProductVersion, pylonCount?: number, axleColor?: AxleColor): string {
    switch (sunSailType) {
      case SunSailType.SolidayA:
        if (Pvi.eq(version, "2024"))
          return "73001B0";
        return "73001A0";
      case SunSailType.SolidayOne:
        return poleCount === 3 ? "77V1ONE" : "78V1ONE";
      case SunSailType.SolidayFlex:
        return poleCount === 3 ? "79V1FLEX" : "79V1FLEX"; // todo: Soliday-Flex
      case SunSailType.SolidayM:
        if (Pvi.eq(version, "2024"))
          return "70001B0";
        return "70001A0";
      case SunSailType.SolidayX:
        return "74001A0";
      case SunSailType.SolidayXS:
        return "76001A0";
      case SunSailType.SolidayC: {
        const baseId = `7100${axleColor === "black" ? "2" : "1"}${Pvi.is(version, "2022") ? "B" : "A"}`;
        if (Pvi.is(version, "2022"))
          return baseId + "1"
        return baseId + (pylonCount === 2 ? "2" : "0");
      }
      case SunSailType.SolidayCS:
        if (Pvi.is(version, "2022")) {
          if (poleCount === 3)
            return "72001B30";
          return "72001B00";
        }
        return "72001A0" + MathUtil.clamp(pylonCount || 1, 1, 2);
      case SunSailType.SolidayTwin:
        if (Pvi.is(version, "2022")) {
          if (poleCount === 5)
            return "72001B50";
          return "72001B20";
        }
        return "720001A" + MathUtil.clamp(pylonCount || 3, 3, 4) + "1";
      default:
        throw new Error("Not implemented so far!");
    }
  }
  static fixCornerTypeSize(sunsailType: SunSailType, clothType: ClothType, cornerType: CornerType): CornerType {
    // https://plaspack.atlassian.net/browse/APPSK-178
    if (SST.is(sunsailType, SST.SF)) {
      if (ClothData.isAustronet(clothType)) {
        if (cornerType === CornerType.Strap25)
          return CornerType.Strap40;
        if (cornerType === CornerType.D_Ring25)
          return CornerType.D_Ring40;
      }
    }
    return cornerType;
  }

  static getPossibleClothCornerTypes(sunSailType: SunSailType, clothType: ClothType, isOrca: boolean): CornerType[] {
    if (isOrca) {
      switch (clothType) {
        case ClothType.Austronet950: return [CornerType.OrcaRing];
        case ClothType.Austronet960: return [CornerType.OrcaRing];
        case ClothType.ChillSail: return [];
        case ClothType.Soltis92: return [CornerType.OrcaRing, CornerType.OrcaPlate];
        case ClothType.Austrosail_PES: return [CornerType.OrcaRing];
        case ClothType.Lotus: return [CornerType.OrcaRing, CornerType.OrcaPlate];
      }
      return [CornerType.OrcaRing, CornerType.OrcaPlate];
    } else {
      switch (clothType) {
        case ClothType.Austronet950: return [SunSailLogic.fixCornerTypeSize(sunSailType, ClothType.Austronet950, CornerType.D_Ring25), SunSailLogic.fixCornerTypeSize(sunSailType, ClothType.Austronet950, CornerType.Strap25)]
        case ClothType.Austronet960: return [SunSailLogic.fixCornerTypeSize(sunSailType, ClothType.Austronet950, CornerType.D_Ring25), SunSailLogic.fixCornerTypeSize(sunSailType, ClothType.Austronet950, CornerType.Strap25)]
        case ClothType.ChillSail: return [CornerType.ChillSail];
        case ClothType.Soltis92: return [CornerType.D_Ring25, CornerType.Strap25, CornerType.BigPlate];
        case ClothType.Austrosail_PES: return [CornerType.D_Ring25, CornerType.Strap25];
        case ClothType.Lotus: return [CornerType.D_Ring25, CornerType.Strap25, CornerType.BigPlate]
      }
      return [CornerType.D_Ring25, CornerType.Strap25, CornerType.BigPlate]
    }
  }

  static _calcCornerType(poleCornerType: CornerType | undefined, sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): CornerType {
    const sunSailType = PD.productToSunSailType(sunSail);
    const poleCount = PD.productToPoleCount(sunSail);
    const clothType = SunSailLogic.getCloth(sunSail)?.clothType;
    const fixCorner = SunSailLogic.getFixCornerTypes(sunSailType, poleCount, poleId, clothType)
    if (fixCorner !== CornerType.None)
      return fixCorner;

    const availableCornerTypes = SunSailLogic.getAvailableSelectableCornerTypes(sunSail, poleId)
    if (availableCornerTypes.length === 0)
      return CornerType.None;

    if (poleCornerType && availableCornerTypes.includes(poleCornerType))
      return poleCornerType;

    return SunSailLogic.getDefaultCornerType(sunSail, poleId);
  }

  static getCornerType(sunSail: ISunSailData | ISail2dForm | ISCSunSailData, poleId: PoleId): CornerType {
    if (!sunSail) {
      throw new Error("getCornerType: SunSail is null!")
    }
    if (Product.isProduct2d(sunSail))
      return SunSailLogic._calcCornerType(sunSail.cornerTypes?.[poleId], sunSail, poleId)
    else {
      return SunSailLogic._calcCornerType(Poles.getPole(sunSail, poleId)?.cornerType, sunSail, poleId)
    }
  }

  static getCornerArticleId(sunSail: ISunSailData, poleIndex: number): string {
    const cornerType = SunSailLogic.getCornerType(sunSail, poleIndex);
    const articleId = getArticleForCornerType(cornerType); //  (pole.articleSlots as any).corner)
    return articleId || "";
  }

  static getFixCornerTypes(sunSailType: SunSailType, poleCount: number, poleIndex: number, clothType: ClothType | undefined): CornerType {
    switch (sunSailType) {
      case SunSailType.SolidayC:
        return poleIndex === 1 || poleIndex === 3 ? CornerType.BigPlate : CornerType.None;
      case SunSailType.SolidayOne:
        return CornerType.None;
      case SunSailType.SolidayFlex:
        return CornerType.None;
      case SunSailType.SolidayCS: {
        if (poleCount === 3 && poleIndex === 2) {
          return CornerType.BigPlate;
        }
        return CornerType.None;
      }
      case SunSailType.SolidayM:
      case SunSailType.SolidayA:
      case SunSailType.SolidayX: {
        if (poleIndex === 1 || poleIndex === 3) {
          if (clothType === undefined)
            return CornerType.None;
          return SunSailLogic.fixCornerTypeSize(sunSailType, clothType, CornerType.Strap25)
        }
        return CornerType.None;
      }
    }
    return CornerType.None;
  }

  static getAvailableSelectableCornerTypes(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): CornerType[] {
    const clothType = SunSailLogic.getCloth(sunSail)?.clothType;
    if (!clothType)
      return [];

    const sunSailType = PD.productToSunSailType(sunSail);

    const isKeder = SunSailLogic.hasKederOnPole(sunSail, poleId);
    if (isKeder)
      return [SunSailLogic.fixCornerTypeSize(sunSailType, clothType, CornerType.Strap25)];

    const isOrca = SunSailLogic.isOrca(sunSail);

    let corners: CornerType[] = [];
    if (SST.is(sunSailType, SST.CoSF)) {
      corners = SunSailLogic.getPossibleClothCornerTypes(sunSailType, clothType, isOrca);

      const featureLevel = FL.fromProduct(sunSail);
      if (!isOrca && FL.le(featureLevel, "user"))
        corners = corners.filter((c) => [CornerType.D_Ring25, CornerType.Strap25, CornerType.D_Ring40, CornerType.Strap40].includes(c))

    } else if (SST.is(sunSailType, SST.One)) {
      if (!PoleLogic.isAxle(sunSailType, poleId)) {
        const allowedCornerTypes = [CornerType.Strap25, CornerType.Strap40];
        if (clothType === ClothType.Austrosail_Nano && SunSailLogic.calcAlignDistOut(sunSail)) {
          allowedCornerTypes.push(CornerType.BigPlate);
        }
        corners = SunSailLogic.getPossibleClothCornerTypes(sunSailType, clothType, isOrca).filter((c) => allowedCornerTypes.includes(c))
      }
    }


    return corners;
  }

  static getDefaultCornerType(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): CornerType {
    const sunSailType = PD.productToSunSailType(sunSail);
    const poleCount = PD.productToPoleCount(sunSail);
    const clothType = SunSailLogic.getCloth(sunSail)?.clothType;
    const fixCorner = SunSailLogic.getFixCornerTypes(sunSailType, poleCount, poleId, clothType)
    if (fixCorner !== CornerType.None)
      return fixCorner;
    const availableCornerTypes = SunSailLogic.getAvailableSelectableCornerTypes(sunSail, poleId)
    if (availableCornerTypes.length > 0) {
      if (sunSailType === SunSailType.SolidayOne && availableCornerTypes.includes(CornerType.BigPlate))
        return CornerType.BigPlate;
      return availableCornerTypes[0];
    }
    return CornerType.None;
  }

  static getPossibleRope2Articles(sunSail: ISunSailData | ISail2dForm | ISCSunSailData | undefined): string[] {
    if (!sunSail || !SunSailLogic.hasRope2(PD.productToSunSailType(sunSail)))
      return [];
    return RopeHelper.getRopeArticles(["6.5E", "8A"]).map((a) => a.id);
  }

  static getRope2(sunSail: ISunSailData | ISail2dForm | ISCSunSailData | undefined): string | undefined {
    return getChecked({
      selection: sunSail?.articleSlots?.rope2 || "00E13001406",
      validItems: SunSailLogic.getPossibleRope2Articles(sunSail),
      emptySelection: undefined
    })
  }

  static hasRope2(sunSailType: SunSailType): boolean {
    return SST.is(sunSailType, SST.XXs); //  && SunSailLogic.getCoilUpPoleIndex(sunSail) !== -1;
  }

  static calcAxleLengthSale(sunSail: ISunSailData): number {
    const axleLength = SunSailLogic.calcAxleLength(sunSail, "logical");
    const minMaxAxle = SunSailLogic.getLogicalMinMaxAxle(sunSail);
    let roundedLen = Math.ceil(MathUtil.round(axleLength, 2));
    if (sunSail.sunSailType === SunSailType.SolidayM && Pvi.eq(sunSail.version, "2024")) {
      if (roundedLen % 2 === 0)
        roundedLen++;
    }
    const axleLengthSale = Math.ceil(MathUtil.clamp(roundedLen, minMaxAxle.minShopping, minMaxAxle.max));
    return axleLengthSale;
  }

  static calcAxleLengthFromPositions(sunSailType: SunSailType, positions: IVec3[] | IAxlePos<IVec3>): number {
    const indices = PoleLogic.getAxleIndices(sunSailType);
    if (indices) {
      const len = Array.isArray(positions) ? Vec3.len(positions[indices.first], positions[indices.second]) : Vec3.len(positions.first, positions.second);
      return len;
    }
    throw new Error("SunSailType has no axle")
  }

  static calcAxleMountDistFromLogicalLength(sunSail: ISunSailData, logicalAxleLength: number): number {
    return logicalAxleLength + SunSailLogic.axisLengthAdd(sunSail, AxleLogic.getLogicalAxleLengthType(sunSail));
  }

  static getAxisLengthAddFunction(lengthType: AxleMeasureType): (sunSail: ISunSailData, poleId: PoleId) => number {
    switch (lengthType) {
      case "brutto": return SunSailLogic.getMountToBruttoAxleDist;
      case "netto": return SunSailLogic.getMountToNettoAxleDist;
    }
  }

  static axisLengthAdd(sunSail: ISunSailData, lengthType: AxleMeasureType): number {
    return AxleLogic.calcAxleDistSum(sunSail, SunSailLogic.getAxisLengthAddFunction(lengthType))
  }

  static calcAxleLength(sunSail: ISunSailData, lengthType: AxleMeasureType | "logical", axleIndex?: number): number {
    switch (lengthType) {
      case "brutto": return SunSailLogic.calcAxleLengthFromPositions(sunSail.sunSailType, SunSailLogic.calcMountPositionsForAxlePoles(sunSail, axleIndex || 0)!) - SunSailLogic.axisLengthAdd(sunSail, lengthType);
      case "netto": return SunSailLogic.calcAxleLengthFromPositions(sunSail.sunSailType, SunSailLogic.calcMountPositionsForAxlePoles(sunSail, axleIndex || 0)!) - SunSailLogic.axisLengthAdd(sunSail, lengthType);
      case "logical": return SunSailLogic.calcAxleLength(sunSail, AxleLogic.getLogicalAxleLengthType(sunSail), axleIndex);
    }
    throw new Error("Shouldn't happen!")
  }

  static getAxleNetLength(sunSail: ISunSailData, clothIndex: number, axlePos: IAxlePos<IVec3>): number {
    const { sunSailType } = sunSail;
    const axleIndices = PoleLogic.getAxleIndices(sunSailType)!;
    const firstDistIn = SunSailLogic.calcDistanceInForClothIndex(sunSail, axleIndices.first, { clothIndex });
    const secondDistIn = SunSailLogic.calcDistanceInForClothIndex(sunSail, axleIndices.second, { clothIndex });
    const dist = firstDistIn + secondDistIn + AxleLogic.calcAxleDistSum(sunSail, SunSailLogic.getMountToNettoAxleDist);
    const axleMountLength = Vec3.len(axlePos.first, axlePos.second);

    return axleMountLength - dist;
  }

  static getAxlePos(sunSailType: SunSailType, mountPositions: IVec3[]): IAxlePos<IVec3> {
    const axleIndices = PoleLogic.getAxleIndices(sunSailType)!;
    return {
      first: mountPositions[axleIndices.first],
      second: mountPositions[axleIndices.second],
    }
  }

  static calcMountAngleDegree(mountPositions: IVec3[], a: number, b: number): number {
    const vec = Vec3.to(mountPositions[a], mountPositions[b]);
    const len = Vec3.len(vec);
    return MathUtil.rad2deg(Math.asin(vec.y / len));
  }

  static calcAxleAngleDegrees(sunSailType: SunSailType, mountPositions: IVec3[]): number {
    const indices = PoleLogic.getAxleIndices(sunSailType);
    if (indices) {
      return SunSailLogic.calcMountAngleDegree(mountPositions, indices.first, indices.second);
    }
    throw new Error("SunSailType has no axle")
  }

  static calcAngleBetweenSailGroups(sunSailType: SunSailType, mountPositions: IVec3[]): { angle: Radian, type: "A" | "V" } {
    const topology = SunSailTopology.calcTopology(sunSailType, mountPositions.length);
    Debug.assert(topology.groups.length === 2, "Invalid number of groups");
    const g1 = topology.groups[0].poleIndices;
    const n1 = Vec3.crossNormalized(Vec3.to(mountPositions[g1[0]], mountPositions[g1[1]]), Vec3.to(mountPositions[g1[0]], mountPositions[g1[2]]))
    const g2 = topology.groups[1].poleIndices;
    const n2 = Vec3.crossNormalized(Vec3.to(mountPositions[g2[0]], mountPositions[g2[1]]), Vec3.to(mountPositions[g2[0]], mountPositions[g2[2]]))

    const angle = Math.acos(Vec3.dot(n1, n2));

    if (!topology.axleIndices)
      throw new Error("Sail has no axle!");
    const a = mountPositions[topology.axleIndices[0]];
    const c = mountPositions[topology.axleIndices[1]];
    const axleDir = Vec3.dir(a, c)

    const n = Vec3.crossNormalized(n1, n2);
    const dot = Vec3.dot(n, axleDir);

    const type = dot > 0 ? "V" : "A";
    if (dot !== SunSailLogic.lastDot) {
      console.log("Dot: ", dot, type);
      SunSailLogic.lastDot = dot;
    }

    return { angle, type }
  }
  static lastDot = 0;

  static calcNetAngle(positions: IVec3[], pre: number, p: number, post: number): Radian {
    return Vec3.calcAngle(positions[pre], positions[p], positions[post]);
  }

  // sync with Logic.cpp
  static hasGasPressureAxle(sunSailType: SunSailType): boolean {
    return sunSailType === SunSailType.SolidayX || sunSailType === SunSailType.SolidayXS;
  }

  // sync with Logic.cpp
  static hasRevolvingRopePD(pd: IProductDescriptor): boolean {
    if (pd.pdt !== "sunSail")
      return false;
    const sunSailType = pd.sunSailType;
    return SunSailLogic.hasRevolvingRopeSST(sunSailType);
  }

  static hasRevolvingRopeSST(sunSailType: SunSailType): boolean {
    return AxleLogic.hasAxle(sunSailType) && !MotorLogic.hasMotor(sunSailType) && !this.hasGasPressureAxle(sunSailType);
  }

  static isSpecialRevolvingRopeLength(length: number): boolean {
    return length <= 2 || length >= 6;
  }

  // Einlegescheibe
  static hasInsertDisc(isPole: boolean, poleMaterial: MaterialType | undefined, poleMountType: PoleMountType): boolean {
    if (!isPole) return false;
    if (PoleLogic.isSteel(poleMaterial) || PoleLogic.isDroppole(poleMaterial)) {
      if (poleMountType === PoleMountType.EarthScrew) return true;
    }
    return false;
  }

  static getAxleCount(sunSailType: SunSailType): number {
    return AxleLogic.hasAxle(sunSailType) ? (sunSailType === SunSailType.SolidayTwin ? 2 : 1) : 0
  }

  static getAxleIndex(sunSailType: SunSailType, groupIndex: number): number {
    if (SunSailLogic.getAxleCount(sunSailType) > 1)
      return groupIndex;
    return 0;
  }

  static getAxleIndexForPole(sunSailType: SunSailType, poleCount: number, poleId: PoleId): number {
    if (SunSailLogic.getAxleCount(sunSailType) > 1) {
      const groupIndex = SunSailLogic.calcClothIndexFromPole(sunSailType, poleCount, poleId);
      const axleIndex = SunSailLogic.getAxleIndex(sunSailType, groupIndex)
      return axleIndex;
    }
    return 0;
  }

  static translatePoleMountType(poleMountType: PoleMountType | undefined, material: MaterialType | undefined): string {
    switch (poleMountType) {
      case PoleMountType.GroundSleeve:
        return "Hülse";
      case PoleMountType.EarthScrew:
        return "Schraubfundament";
      case PoleMountType.GroundPlate:
        return material === "Steel_V4A" ? "Platte <mark><b>V4A</b></mark>" : "Platte";
      case PoleMountType.Clamp:
        return "Schellen";
      case PoleMountType.BaseCube:
        return "Base Cube";
      case PoleMountType.ConcreteCube:
        return "Beton Cube";
      case PoleMountType.Special:
        return "Sonderkonsole";
      default:
        return "" + poleMountType;
    }
  }

  static getMaterialTypeNameForSunSail(sunSail: ISunSailData, poleId: PoleId): string {
    const materialType = SunSailLogic.getPoleMaterial(sunSail, poleId);
    const poleType = SunSailLogic.getPoleType(sunSail, poleId);
    return SunSailLogic.getMaterialTypeName(materialType, { poleType })
  }

  static getMaterialTypeName(materialType: MaterialType | undefined, options?: { poleType?: PoleType, withDiameter?: boolean }): string {
    if (!materialType)
      return "";
    if (options?.poleType === PoleType.Wall)
      return "Wand";

    let dm = "";
    if (options?.withDiameter === true) {
      dm = "" + PoleLogic.getPoleDiameterMili(materialType);
      if (dm.length > 0) dm = " " + dm;
    }
    switch (materialType) {
      case "Steel":
        return "Edelstahl" + dm;
      case "Steel_V4A":
        return "Edelstahl <mark><b>V4A</b></mark>" + dm;
      case "Alu":
        return "Alu" + dm;
      case "AluWood":
        return "Alu <mark><b>Holzoptik</b></mark>" + dm;
      case "Alu_Black":
        return "Alu <mark><b>anthrazit</b></mark>" + dm;
      case "Droppole":
        return "Droppole";
      case "Droppole_Black":
        return "Droppole <mark><b>schwarz</b></mark>";
      case "Telescope":
        return "Teleskop";
      case "TelescopeWood":
        return "Teleskop <mark>Holz</mark>";
      case "Telescope_Black":
        return "Teleskop <mark>anthrazit</mark>";
      case "Profile":
        return "Profil";
      case "Profile_Black":
        return "Profil <mark>anthrazit</mark>";
      case "Iron48":
        return "Verzinkt ø48";
      case "Iron114":
        return "Verzinkt ø114";
      case "Iron89":
        return "Verzinkt ø89";
      case "Iron89_Black":
        return "Verzinkt <mark><b>anthrazit</b></mark> ø89";
    }
    return "Unbekannt";
  }

  static getMaterialColorName(materialType: MaterialType): string {
    switch (materialType) {
      case "Droppole":
        return "natur";
      case "Droppole_Black":
        return "<mark><b>schwarz</b></mark>";
      case "AluWood":
        return "<mark><b>Holzoptik</b></mark>";
      case "Alu_Black":
        return "<mark><b>anthrazit</b></mark>";
      case "TelescopeWood":
        return "<mark><b>Holz</b></mark>";
      case "Telescope_Black":
        return "<mark><b>anthrazit</b></mark>";
      default:
        return "";
    }
  }

  static calcNetPos(sunSail: ISunSailData, poleIndex: PoleIndex, distInVal: number, mount: IVec3[]): IVec3 {
    const mountPos = mount[poleIndex];
    const indices = PoleLogic.getAxleIndices(sunSail.sunSailType);
    if (indices) {
      let dir: IVec3;
      if (indices.first === poleIndex) {
        dir = Vec3.dir(mountPos, mount[indices.second])
      } else if (indices.second === poleIndex) {
        dir = Vec3.dir(mountPos, mount[indices.first])
      } else {
        if (SST.is(sunSail.sunSailType, SST.CsXsTwin) && LogicS.isQuadFrontPole(sunSail.sunSailType, Poles.getCount(sunSail), poleIndex)) {
          const axleMountPos = SunSailLogic.getAxlePos(sunSail.sunSailType, mount);
          const a = LogicS.calcFrontNetPosition(sunSail, poleIndex, 0, axleMountPos);
          const b = LogicS.calcFrontNetPosition(sunSail, Poles.getOtherPole(sunSail.sunSailType, poleIndex), 0, axleMountPos);
          const targetPoint = Line3.getNearestPoint(a, b, mount[poleIndex], true);
          // const frontMountDist = LogicS.getMountDistance(sunSail, poleIndex, mount)
          // const targetPoint = LogicS.calcFrontNetPosition(sunSail, poleIndex, SunSailLogic.getKnotOuterDist() + frontMountDist * 0.5, mount);
          dir = Vec3.dir(mountPos, targetPoint);
        } else {
          // https://plaspack.atlassian.net/browse/APPSK-189
          const clothIndex = SunSailLogic.calcClothIndexFromPole(sunSail.sunSailType, Poles.getCount(sunSail), poleIndex);
          const axleFromTo = SunSailLogic.calcAxleNetFromMountPositions(sunSail, clothIndex, mount[indices.first], mount[indices.second]);
          const halfOnAxle = SunSailLogic.calcHalfVectorAxlePoint(mountPos, axleFromTo.first, axleFromTo.second);
          dir = Vec3.dir(mountPos, halfOnAxle);
        }
      }
      const distIn = distInVal + SunSailLogic.getMountToNettoAxleDist(sunSail, poleIndex);
      return Vec3.addScaled(mountPos, dir, distIn);
    } else {
      const pc = mount.length;
      const left = (poleIndex + 1) % pc;
      const right = (poleIndex + mount.length - 1) % pc;
      const halfDir = sunSail.poles[poleIndex].halfVec ?? TriangleHelper.calcHalfVector(mountPos, mount[left], mount[right], { correctDir: SST.is(sunSail.sunSailType, SST.Sandy) ? "right" : undefined });
      const distIn = distInVal + SunSailLogic.getMountToNettoAxleDist(sunSail, poleIndex);
      return Vec3.addScaled(mountPos, halfDir, distIn);
    }
  }

  static calcClosestPointOnAxle(point: IVec3, axlePointA: IVec3, axlePointC: IVec3): IVec3 { // nearest
    const ray = Ray3.fromPoints(axlePointA, axlePointC);
    const closestAxlePoint = Ray3.calcClosestPointOnRay(ray, point);
    return closestAxlePoint.point;
  }

  static calcHalfVectorAxlePoint(point: IVec3, axlePointA: IVec3, axlePointC: IVec3): IVec3 {
    const abVec = Vec3.dir(point, axlePointA);
    const cbVec = Vec3.dir(point, axlePointC);
    const normalAdded = Vec3.addScaled(abVec, cbVec, -1);
    const n = Vec3.normalized(normalAdded);

    const plane = Plane.fromVector(n, Vec3.dot(n, point))
    const p = IntersectionUtil.intersect(Ray3.fromPoints(axlePointA, axlePointC), plane, false)
    const ret = p ? p : Vec3.mid(axlePointA, axlePointC);
    if (Vec3.isNaN(ret)) {
      throw new Error("calcHalfVectorAxlePoint NAN!");
    }
    return ret;
  }

  static calcStraightPlaneAxlePoint(point: IVec3, axlePointA: IVec3, axlePointC: IVec3): IVec3 {
    const axleConstantHeight = Vec3.to(axlePointA, axlePointC);
    axleConstantHeight.y = 0;
    const vecRoll = Vec3.normalized(axleConstantHeight);

    const mid = Vec3.mid(axlePointA, axlePointC);

    const plane = Plane.fromVector(vecRoll, Vec3.dot(vecRoll, point))
    const p = IntersectionUtil.intersect(Ray3.fromPoints(mid, vecRoll), plane, true)

    if (!p)
      return mid; // if there is no result..

    return p;
  }

  static calcMinAllowedDistIn(sunSail: ISunSailData, poleIndex: number, options?: { forPoleOnly?: boolean }): number {
    const total = SunSailLogic.calcTotalMaxDistInOut(sunSail, poleIndex, options);
    const madin = SunSailLogic.getMinAllowedDistIn(sunSail, poleIndex);
    const maxAllowedDistOut = SunSailLogic.getMaxAllowedDistOut(sunSail.sunSailType, SunSailLogic.getInfluencingIndirectPylonType(sunSail, poleIndex))
    const minAllowedDistIn = Math.max(madin, SunSailLogic.calcDistanceInFromOut(maxAllowedDistOut, total));
    if (Number.isNaN(minAllowedDistIn)) {
      throw new Error("NAN calctoalMaxDistInOut.minAllowedDistanceInd")
    }
    return minAllowedDistIn;
  }

  static calcTotalMaxDistInOut(sunSail: ISunSailData, poleIndex: number, _options?: { forPoleOnly?: boolean }): ITotalMaxDistInOut {
    const { sunSailType } = sunSail;

    if (SST.is(sunSail.sunSailType, SST.Sandy)) {
      return { totalMaxDistIn: 0.3, totalMaxDistOut: 0.3 }
    }

    const clothIndex = SunSailLogic.calcClothIndexFromPole(sunSailType, Poles.getCount(sunSail), poleIndex)
    const axleIndex = SunSailLogic.getAxleIndex(sunSail.sunSailType, clothIndex);
    if (AxleLogic.hasAxle(sunSailType)) {
      if (PoleLogic.isAxle(sunSailType, poleIndex)) {
        const mountPos = SunSailLogic.calcMountPositionsForAxlePoles(sunSail, axleIndex)!; // don't use calcMountPositions here => infinte recursion
        const maxDistIn = SunSailLogic.getMaxAllowedDistInAxle(sunSailType, Vec3.len(mountPos?.first, mountPos?.second) - AxleLogic.calcAxleDistSum(sunSail, SunSailLogic.getMountToNettoAxleDist)).hard
        return { totalMaxDistIn: maxDistIn, totalMaxDistOut: maxDistIn }
      } else {
        const mountPositions = SunSailLogic.calcMountPositions(sunSail, axleIndex, undefined);
        const axleNet = SunSailLogic.calcAxleNetPositions(sunSail, clothIndex);
        if (!axleNet)
          throw new Error("AxleNet is null!");
        if (SST.is(sunSailType, SST.CsXsTwin) && LogicS.isQuadFrontPole(sunSailType, Poles.getCount(sunSail), poleIndex)) {
          const dists: number[] = [];
          {
            const mountPos = mountPositions[poleIndex];
            const p = SunSailMath.calcNearestAxlePoint(axleNet.first, axleNet.second, mountPos);
            dists.push(Vec3.len(mountPos, p));
          }
          // if (!options?.forPoleOnly) {
          //   const mountPos = mountPositions[SunSailLogic.getOtherPole(sunSail.sunSailType, poleIndex)];
          //   const p = SunSailLogic.calcNearestAxlePoint(axleNet.from, axleNet.to, mountPos);
          //   dists.push(Vec3.len(mountPos, p));
          // }
          // const dist = Math.min(...dists);
          return {
            totalMaxDistIn: dists[0], //  dist, // https://plaspack.atlassian.net/browse/APP22-948
            totalMaxDistOut: dists[0] // dist
          }
        } else {
          const mountPos = mountPositions[poleIndex];
          const halfOnAxle = SunSailLogic.calcHalfVectorAxlePoint(mountPos, axleNet.first, axleNet.second);
          const maxDistIn = Vec3.len(mountPos, halfOnAxle) - SunSailLogic.getMountToNettoAxleDist(sunSail, poleIndex);

          const outerPoint = Vec3.addScaled(mountPos, Vec3.dir(mountPos, halfOnAxle), SunSailLogic.getMountToNettoAxleDist(sunSail, poleIndex));
          const nearestAxlePoint = SunSailMath.calcNearestAxlePoint(axleNet.first, axleNet.second, outerPoint);
          const maxDistOut = Vec3.len(outerPoint, nearestAxlePoint);

          return { totalMaxDistIn: maxDistIn, totalMaxDistOut: maxDistOut };
        }
      }
    } else {
      const mountPositions = SunSailLogic.calcMountPositions(sunSail, axleIndex, undefined);
      const poleCount = Poles.getCount(sunSail);
      const a1 = (poleIndex + poleCount - 1) % poleCount;
      const a2 = (poleIndex + 1) % poleCount;
      const hv = SunSailLogic.calcHalfVectorAxlePoint(mountPositions[poleIndex], mountPositions[a1], mountPositions[a2])
      const dist = Vec3.len(hv, mountPositions[poleIndex]);
      const maxDistIn = dist - SunSailLogic.getMountToNettoAxleDist(sunSail, poleIndex);

      return {
        totalMaxDistIn: maxDistIn,
        totalMaxDistOut: maxDistIn
      }
    }
  }

  static calcPlateSize(): number {
    return 0.15;
  }

  static calcMinDistOut(sunSail: ISunSailData, poleIndex: number): number {
    if (SST.is(sunSail.sunSailType, SST.Sandy)) {
      return 0;
    }
    return PoleLogic.isAxle(sunSail.sunSailType, poleIndex) ? 0 : SunSailLogic.calcPlateSize(); // Plate size for C only?
  }

  static calcMaxDistInOut(sunSail: ISunSailData, poleIndex: number): { minDistIn: number; maxDistIn: number; minDistOut: number, totalMaxDistIn: number, totalMaxDistOut: number, maxAllowedDistOut: number } {
    const total = SunSailLogic.calcTotalMaxDistInOut(sunSail, poleIndex);
    const minDistIn = SunSailLogic.getMinAllowedDistIn(sunSail, poleIndex)

    const minDistOut = SunSailLogic.calcMinDistOut(sunSail, poleIndex);
    // const maxDistIn = total.maxDistIn - calcPlateSize();
    const maxDistIn = SunSailLogic.calcDistanceInFromOut(minDistOut, total)

    return {
      minDistIn,
      maxDistIn,
      minDistOut, //:SunSailLogic.calcDistanceOutFromIn(maxDistIn, total),
      totalMaxDistIn: total.totalMaxDistIn,
      totalMaxDistOut: total.totalMaxDistOut,
      maxAllowedDistOut: SunSailLogic.calcDistanceOutFromIn(minDistIn, total)
    }
  }


  static getMinAllowedAxleDistIn(sunSailType: SunSailType, poleCount: number, version: ProductVersion, poleIndex: number, ignoreMinDistIn: boolean | undefined): number {
    if (ignoreMinDistIn)
      return 0;

    if (PoleLogic.isAxle(sunSailType, poleIndex)) {
      switch (sunSailType) {
        case SunSailType.SolidayTwin:
        case SunSailType.SolidayCS:
          return 0.0;
        case SunSailType.SolidayXS:
          if (poleCount === 3) // https://plaspack.atlassian.net/browse/APPSK-49
            return 0.0;
          return 0.03; // https://plaspack.atlassian.net/browse/APPSK-49
        case SunSailType.SolidayOne:
          return 0; // poleCount === 0 ? 0.072 : 0; // SeilTrommel
        case SunSailType.SolidayFlex:
          return 0.04;
        case SunSailType.SolidayM:
          if (Pvi.eq(version, "2024"))
            return 0.0;
          return 0;
      }
      return 0.0;
    }
    throw new Error("Not an axle point!")
  }

  static getMinAllowedDistIn(sunSail: ISunSailData | ISCSunSailData, poleIndex: PoleIndex): number {
    const sunSailType = PD.productToSunSailType(sunSail)
    if (PoleLogic.isAxle(sunSailType, poleIndex)) {
      return SunSailLogic.getMinAllowedAxleDistIn(sunSailType, Poles.getCount(sunSail), sunSail.version, poleIndex, sunSail.securityCheckOptions?.ignoreMinDistIn);
    }

    if (SST.is(sunSailType, SST.CsXsTwin)) { // no minDistIn security check on axle
      return SunSailLogic.getMinAllowedDistInCSXSTwin(sunSailType, Poles.getCount(sunSail), poleIndex, SunSailLogic.isPolyPositioningForPole(sunSail, poleIndex), sunSail.securityCheckOptions?.ignoreMinDistIn)
    }

    if (sunSail.securityCheckOptions?.ignoreMinDistIn)
      return 0;

    switch (sunSailType) {
      case SunSailType.SolidayC: {
        const pylonCount = SunSailLogic.calcPylonCount(sunSail);
        const pylonType = SunSailLogic.getPolePylonType(sunSail, poleIndex);
        if (pylonCount === 0 || (!SunSailLogic.getPolePylonType(sunSail, PoleIndex.B) && !SunSailLogic.getPolePylonType(sunSail, PoleIndex.D)))
          return 0.1;
        else if (pylonCount === 1) {
          if (pylonType)
            return 0.25;
          return 0.2;
        } else if (pylonCount === 2) {
          let distIn = 0.05;
          const indirectPylonInfos = IndirectPylonLogic.getIndirectPylons(sunSail);
          for (const indirectPylon of indirectPylonInfos) {
            if (indirectPylon.oppositeIndirectPylon === poleIndex) {
              distIn = Math.max(distIn, 0.05);
            } else if (indirectPylon.indirectPylon === poleIndex) {
              distIn = Math.max(distIn, 0.20);
            }
          }
          return distIn;
        }
        return 0.05;
      }
      case SunSailType.SolidayX: {
        if (SunSailLogic.hasRopeFixArticle(sunSail, poleIndex)) {
          return 0.35;
        }
        return 0.18;
      }
      case SunSailType.SolidayA: {
        // different for version 2024?
        if (SunSailLogic.hasRopeFixArticle(sunSail, poleIndex)) {
          return 0.25;
        }
        return 0.17; // https://plaspack.atlassian.net/browse/APPSK-33
      }
      case SunSailType.SolidayOne: {
        const deflectionPosition = SunSailLogic.getOperatingDeflectionPosition(sunSail);
        if (poleIndex === deflectionPosition) {
          const heightChangeOnOtherPoint = deflectionPosition ? SunSailLogic.getHeightChangeArticle(sunSail, Poles.getOtherPole(sunSailType, deflectionPosition)) : undefined;
          if (heightChangeOnOtherPoint) {
            return 0.4; // https://plaspack.atlassian.net/browse/API-33
          }
          return 0.3;
        }
        return 0.2;// https://plaspack.atlassian.net/browse/API-2
      }
      case SunSailType.SolidayFlex: {
        return 0.25;
      }
      case SunSailType.SolidayM: {
        if (SunSailLogic.hasRopeFixArticle(sunSail, poleIndex)) {
          return 0.25;
        }
        return 0.17; // https://plaspack.atlassian.net/browse/APPSK-33
      }
      case SunSailType.Fixed:
      case SunSailType.Standard:
      case SunSailType.SolidayTwin:
        return SunSailLogic.#calcFixedMountDistIn(sunSail, poleIndex).min;
    }
    return 0.0;
  }

  static getMinAllowedDistInCSXSTwin(sunSailType: SunSailType, poleCount: number, poleIndex: number, isPolyPositioning: boolean, ignoreMinDistIn: boolean | undefined): number {
    switch (sunSailType) {
      case SunSailType.SolidayCS:
      case SunSailType.SolidayTwin: {
        if ((poleCount === 3 && poleIndex === 2) || (poleCount === 5 && poleIndex === 4))
          return ignoreMinDistIn ? 0 : 0.05;
        return isPolyPositioning ? 1.0 : 0.4;
      }
      case SunSailType.SolidayXS: {
        if (poleCount === 3)
          return ignoreMinDistIn ? 0 : 0.35;
        return 1.0;
      }
    }
    return 0;
  }

  // float Logic::CalcDistanceOutFromIn(float distanceIn, float minDistanceIn, float maxDistanceIn, float maxDistanceOut)
  // {
  //   if (Float::IsZero(maxDistanceIn))
  //     return 0.0f;
  //   float result = maxDistanceOut * ((maxDistanceIn - distanceIn) / (maxDistanceIn - minDistanceIn));
  //   return Math::Clamp(result, 0.0f, maxDistanceOut);
  // }

  static calcDistanceInFromOut(distanceOut: number, total: ITotalMaxDistInOut): number {
    const minDistanceIn = 0; // total.minDistIn;
    const maxDistanceIn = total.totalMaxDistIn;
    const maxDistanceOut = total.totalMaxDistOut;
    if (maxDistanceOut === 0)
      return 0;
    const distanceIn = Math.max(0, minDistanceIn + (maxDistanceIn - minDistanceIn) * ((maxDistanceOut - distanceOut) / maxDistanceOut));
    //   return Math::Clamp(result, 0.0f, maxDistanceOut);
    return distanceIn;
  }


  static calcDistanceOutFromIn(distanceIn: number, total: ITotalMaxDistInOut): number {
    // const minDistanceIn = 0; //total.maxDistIn;
    const maxDistanceIn = total.totalMaxDistIn;
    const maxDistanceOut = total.totalMaxDistOut;
    const distanceOut = maxDistanceOut - (distanceIn * maxDistanceOut) / maxDistanceIn;
    // const distanceOut = Math.min(total.maxAllowedDistOut, Math.max(0, maxDistanceOut - (distanceIn * maxDistanceOut) / maxDistanceIn));
    //   return Math::Clamp(result, 0.0f, maxDistanceOut);
    return Math.max(distanceOut, 0); // https://plaspack.atlassian.net/browse/APP-1354
  }

  static #calcFixedMountDistIn(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): { min: number, auto: number } {
    if (SunSailLogic.getPoleType(sunSail, poleIndex) === PoleType.Keder) {
      return { min: 0, auto: 0 }
    }

    if (Product.isProduct3d(sunSail) && sunSail.sourceConfigurator === "2d") {
      return { min: 0, auto: 0.2 }
    }

    if (SunSailLogic.getHeightChangeArticle(sunSail, poleIndex))
      return { min: 0.12, auto: 0.2 };

    const sailMount = SunSailLogic.getSailMount(sunSail, poleIndex);
    switch (sailMount) {
      case "snap": return { min: 0.2, auto: 0.2 }
      case "rope": {
        const ropeFixArticle = this.getRopeFixArticle(sunSail, poleIndex);
        switch (ropeFixArticle) { // https://plaspack.atlassian.net/browse/APP-1308
          case "00E13000024": return { min: 0.1, auto: 0.15 }; // Profi Seilspanner
        }
        break;
      }
      case "direct": {
        const article = this.getSailMountArticle(sunSail, poleIndex);
        switch (article) { // https://plaspack.atlassian.net/browse/APP-1308
          case "60002A006": return { min: 0.06, auto: 0.06 }; // Karabinerhacken
          case "00E13000126": return { min: 0.165, auto: 0.22 }; // Gewindespanner M8
          case "00E13000130": return { min: 0.195, auto: 0.25 }; // Gewindespanner M10
          case "00E13000801": return { min: 0.25, auto: 0.3 } // Spanngurt mit Schnappschäckel
          case "00E13000129": return { min: 0.12, auto: 0.2 } // Gurtbandratsche aus Edelstahl
        }
        break;
      }
    }
    // Default:
    return { min: 0, auto: 0.2 }
  }

  static #calcAutoDistanceInVal(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): number {
    const sunSailType = PD.productToSunSailType(sunSail)

    if (SST.is(sunSailType, SST.Sandy)) {
      return 0.3;
    }

    switch (sunSailType) {
      case SunSailType.Fixed:
      case SunSailType.Concept: {
        return SunSailLogic.#calcFixedMountDistIn(sunSail, poleIndex).auto;
      }
      case SunSailType.SolidayC: {
        return !PoleLogic.isAxle(sunSailType, poleIndex) ? 0.3 : 0;
      }
    }
    return 0;
  }

  static calcAutoDistanceInVal(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): number {
    const minDistIn = SunSailLogic.getMinAllowedDistIn(sunSail, poleIndex);
    return Math.max(minDistIn, SunSailLogic.#calcAutoDistanceInVal(sunSail, poleIndex));
  }

  static _calcDistanceOutForPole(sunSail: ISunSailData, poleIndex: number, options?: { minAllowedDistIn?: number }): number {
    if (SunSailLogic.getPoleType(sunSail, poleIndex) === PoleType.Keder) {
      const total = SunSailLogic.calcTotalMaxDistInOut(sunSail, poleIndex);
      return total.totalMaxDistOut;
    }

    const pole = Poles.getPole(sunSail, poleIndex);
    if (pole.dist.val === "auto") {
      const total = SunSailLogic.calcTotalMaxDistInOut(sunSail, poleIndex);
      const minAllowedDistanceIn = options?.minAllowedDistIn ?? SunSailLogic.calcMinAllowedDistIn(sunSail, poleIndex);
      const v = SunSailLogic.#calcAutoDistanceInVal(sunSail, poleIndex);
      return SunSailLogic.calcDistanceOutFromIn(Math.max(minAllowedDistanceIn, v), total)
    } else if (pole.dist.type === "out") {
      const total = SunSailLogic.calcTotalMaxDistInOut(sunSail, poleIndex);
      const minAllowedDistanceIn = options?.minAllowedDistIn ?? SunSailLogic.calcMinAllowedDistIn(sunSail, poleIndex);
      const totalMaxPossibleDistOut = SunSailLogic.calcDistanceOutFromIn(minAllowedDistanceIn, total)
      return MathUtil.clamp(pole.dist.val, 0, totalMaxPossibleDistOut);
    } else {
      const total = SunSailLogic.calcTotalMaxDistInOut(sunSail, poleIndex);
      const minAllowedDistanceIn = options?.minAllowedDistIn ?? SunSailLogic.calcMinAllowedDistIn(sunSail, poleIndex);
      const v = PoleHelper.getDistInValue(pole.dist, 0);
      const newVal = Math.max(minAllowedDistanceIn, v);
      return SunSailLogic.calcDistanceOutFromIn(newVal, total)
    }
  }

  static _calcDistanceInForPole(sunSail: ISunSailData, poleId: PoleId, options?: { minAllowedDistIn?: number }): { split: boolean, val: number | number[] } {
    if (!Poles.isCornerPole(poleId))
      return { split: false, val: 0 };

    if (SunSailLogic.getPoleType(sunSail, poleId) === PoleType.Keder)
      return { split: false, val: 0 };
    if (SunSailLogic.isTargetDist(sunSail.sunSailType, Poles.getCount(sunSail), poleId, SunSailLogic.calcAlignDistOut(sunSail))) {
      const out1 = SunSailLogic.calcDistanceOut(sunSail, poleId);
      const total = SunSailLogic.calcTotalMaxDistInOut(sunSail, poleId);
      const val = SunSailLogic.calcDistanceInFromOut(out1, total)
      return { split: false, val };
    }
    const pole = Poles.getPole(sunSail, poleId);
    if (pole.dist.val === "auto") {
      const total = SunSailLogic.calcTotalMaxDistInOut(sunSail, poleId);
      const minAllowedDistIn = options?.minAllowedDistIn ?? SunSailLogic.calcMinAllowedDistIn(sunSail, poleId);
      const _v = SunSailLogic.#calcAutoDistanceInVal(sunSail, poleId);
      const val = Math.min(Math.max(minAllowedDistIn, _v), total.totalMaxDistIn);
      return { split: false, val }
    }
    else if (pole.dist.type === "out") {
      const total = SunSailLogic.calcTotalMaxDistInOut(sunSail, poleId);
      const minAllowedDistIn = options?.minAllowedDistIn ?? SunSailLogic.calcMinAllowedDistIn(sunSail, poleId);
      const val = Math.max(minAllowedDistIn, SunSailLogic.calcDistanceInFromOut(pole.dist.val, total));
      return { split: false, val }
    } else {
      const total = SunSailLogic.calcTotalMaxDistInOut(sunSail, poleId);
      const minAllowedDistIn = options?.minAllowedDistIn ?? SunSailLogic.calcMinAllowedDistIn(sunSail, poleId);
      const array = SunSailLogic.toDistInArray(pole.dist.val).map((v) => Math.min(Math.max(minAllowedDistIn, v), total.totalMaxDistIn));
      const split = SunSailLogic.isAxleSplit(array);
      return { split, val: split ? array : array[0] }
    }
  }

  static calcDistanceOut(sunSail: ISunSailData, poleId: PoleId, options?: { minAllowedDistIn?: number }): number {
    if (!Poles.isCornerPole(poleId))
      return 0;

    if (SST.is(sunSail.sunSailType, SST.CsXsTwin) && LogicS.isQuadFrontPole(sunSail.sunSailType, Poles.getCount(sunSail), poleId)) {
      // https://plaspack.atlassian.net/browse/APPSK-103
      const otherPole = Poles.getOtherPole(sunSail.sunSailType, poleId);
      return Math.min(SunSailLogic._calcDistanceOutForPole(sunSail, poleId, options), SunSailLogic._calcDistanceOutForPole(sunSail, otherPole, options));
    }

    if (SunSailLogic.isTargetDist(sunSail.sunSailType, Poles.getCount(sunSail), poleId, SunSailLogic.calcAlignDistOut(sunSail))) {
      const out1 = SunSailLogic._calcDistanceOutForPole(sunSail, poleId, options);
      const otherPoleIndex = Poles.getOtherPole(sunSail.sunSailType, poleId)
      const out2 = SunSailLogic._calcDistanceOutForPole(sunSail, otherPoleIndex, options);
      const finalOut = Math.min(out1, out2);
      return finalOut;
    } else {
      return SunSailLogic._calcDistanceOutForPole(sunSail, poleId, options);
    }
  }

  static calcDistancesIn(sunSail: ISunSailData, poleId: PoleId, options?: { minAllowedDistIn?: number }): { split: boolean, array: number[] } {
    if (SunSailLogic.getPoleType(sunSail, poleId) === PoleType.Keder)
      return { split: false, array: [0, 0] };

    if (SunSailLogic.isTargetDist(sunSail.sunSailType, Poles.getCount(sunSail), poleId, SunSailLogic.calcAlignDistOut(sunSail))) {
      const out1 = SunSailLogic._calcDistanceOutForPole(sunSail, poleId, options);
      const otherPoleIndex = Poles.getOtherPole(sunSail.sunSailType, poleId)
      const out2 = SunSailLogic._calcDistanceOutForPole(sunSail, otherPoleIndex, options);
      const finalOut = Math.min(out1, out2);
      const total = SunSailLogic.calcTotalMaxDistInOut(sunSail, poleId);
      const distIn = SunSailLogic.calcDistanceInFromOut(finalOut, total)
      return { split: false, array: [distIn, distIn] };
    } else {
      const v = SunSailLogic._calcDistanceInForPole(sunSail, poleId, options);
      return { split: v.split, array: Array.isArray(v.val) ? v.val : [v.val, v.val] }
    }
  }

  static isAxleSplit(distancesIn: number[] | number | undefined): boolean {
    return Array.isArray(distancesIn) && distancesIn[0] !== distancesIn[1];
  }

  static calcDistanceInIndexForClothIndex(_sunSailType: SunSailType, clothIndex: number): number {
    return clothIndex;
  }

  static calcDistanceInForClothIndex(sunSail: ISunSailData, poleIndex: number, options?: { clothIndex?: number, minAllowedDistIn?: number }): number {
    const index = SunSailLogic.calcDistanceInIndexForClothIndex(sunSail.sunSailType, options?.clothIndex || 0)
    return SunSailLogic.calcDistanceIn(sunSail, poleIndex, { ...options, index })
  }

  static calcDistanceIn(sunSail: ISunSailData, poleIndex: number, options?: { index?: number, minAllowedDistIn?: number }): number {
    const distancesIn = SunSailLogic.calcDistancesIn(sunSail, poleIndex, options)
    if (Array.isArray(distancesIn.array))
      return distancesIn.array[options?.index || 0];
    if (options?.index)
      throw new Error("DistanceIn index !== 0 but just one distanceIn")
    return distancesIn.array[0];
  }

  static toDistInArray(distIn: number | number[]): number[] {
    if (Array.isArray(distIn)) {
      if (distIn.length === 2) {
        return distIn;
      }
      return [distIn[0], distIn[0]]
    } else {
      return [distIn, distIn];
    }
  }

  static isValidPoleMaterialType(materialType: MaterialType, sst: SunSailType, poleType: PoleType, access: Access, featureLevel: FeatureLevel): boolean {
    return !!SunSailLogic.getValidPoleMaterialTypes(sst, poleType, access, featureLevel)[materialType];
  }

  static getValidPoleMaterialTypes = CacheUtil.memo(SunSailLogic.#getValidPoleMaterialTypes, "PoleLogic.getValidPoleMaterialTypes", {
    idsFromArgs: 3,
    inputsEqualFn: "ref",
    resultEqualFn: "shallow",
  })

  static #getValidPoleMaterialTypes(sst: SunSailType, poleType: PoleType, access: Access, featureLevel: FeatureLevel): PartialRecord<MaterialType, boolean> {
    if (poleType !== PoleType.Pole)
      return {};
    const types = SunSailLogic.#getValidPoleMaterialTypesInternal(sst, access, featureLevel);
    for (const k of getKeys(types)) {
      if (!types[k])
        delete types[k]
    }
    return types;
  }

  static #getValidPoleMaterialTypesInternal(sst: SunSailType, access: Access, featureLevel: FeatureLevel): PartialRecord<MaterialType, boolean> {
    const e2sPoles = FL.le(featureLevel, "user") || access >= Access.SolidayPro;
    const solidayPoles = FL.ge(featureLevel, "dealer");
    switch (sst) {
      case SunSailType.SolidayA:
      case SunSailType.SolidayOne:
      case SunSailType.SolidayFlex:
      case SunSailType.SolidayX:
      case SunSailType.SolidayC:
      case SunSailType.SolidayCS:
      case SunSailType.SolidayTwin:
      case SunSailType.SolidayXS: return {
        "Alu": true,
        "Alu_Black": true,
        "Droppole": true,
        "Droppole_Black": true,
        "Steel": true,
        "Steel_V4A": true,
      }
      case SunSailType.SolidayM: return {
        "Alu": true,
        "Alu_Black": true,
        "Steel": true,
        "Steel_V4A": true,
      }
      case SunSailType.Standard:
      case SunSailType.Fixed: return {
        "Alu": solidayPoles,
        "Alu_Black": solidayPoles,
        "Steel": solidayPoles,
        "Steel_V4A": solidayPoles,
        "Droppole": solidayPoles,
        "Droppole_Black": solidayPoles,
        "Telescope": e2sPoles,
        "Telescope_Black": e2sPoles,
        "Profile": e2sPoles,
        "Profile_Black": e2sPoles,
        "Iron114": solidayPoles,
        "Iron89": solidayPoles,
        "Iron89_Black": solidayPoles,
      }
      case SunSailType.Concept: return {
        "Alu": true,
        "Alu_Black": true,
        "Steel": true,
        "Steel_V4A": true,
        "Droppole": true,
        "Droppole_Black": true,
        "Iron114": true,
        "Iron89": true,
        "Iron89_Black": true,
      }
      case SunSailType.SandyRect:
      case SunSailType.SandyPoly: return {
        "Iron48": true,
        "Alu": true,
        "Alu_Black": false // https://plaspack.atlassian.net/browse/APP-1529
      }
    }
    return {};
  }



  static #getValidMountTypesForPoleMaterial(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): PartialRecord<PoleMountType, boolean> {
    const materialGroup = SunSailLogic.materialGroupFromMaterial(SunSailLogic.getPoleMaterial(sunSail, poleId), SunSailLogic.getPoleType(sunSail, poleId))
    if (!materialGroup)
      return {};

    const sunSailType = PD.productToSunSailType(sunSail);
    if (SST.is(sunSailType, SST.Sandy)) {
      const ret: Record<PoleMountType, boolean> = {
        [PoleMountType.None]: true,
        [PoleMountType.GroundSleeve]: SunSailLogic.calcPossibleGroundSleeveArticleIds(sunSail, poleId, false).length > 0,
        [PoleMountType.EarthScrew]: SunSailLogic.calcPossibleEarthScrews(sunSail, poleId, false).articles.length > 0,
        [PoleMountType.GroundPlate]: SunSailLogic.calcPossibleGroundPlateArticleIds(sunSail, poleId, false).length > 0,
        [PoleMountType.Clamp]: false,
        [PoleMountType.BaseCube]: false,
        [PoleMountType.ConcreteCube]: false,
        [PoleMountType.Special]: false,
      };
      return ret;
    } else {
      const ret: Record<PoleMountType, boolean> = {
        [PoleMountType.None]: ["telescope", "profile"].includes(materialGroup || "") ? false : true,
        [PoleMountType.GroundSleeve]: SunSailLogic.calcPossibleGroundSleeveArticleIds(sunSail, poleId, false).length > 0,
        [PoleMountType.EarthScrew]: SunSailLogic.calcPossibleEarthScrews(sunSail, poleId, false).articles.length > 0,
        [PoleMountType.GroundPlate]: SunSailLogic.calcPossibleGroundPlateArticleIds(sunSail, poleId, false).length > 0,
        [PoleMountType.Clamp]: ["steel", "alu", "droppole", "telescope", "profile"].includes(materialGroup || ""),
        [PoleMountType.BaseCube]: ["steel", "alu", "droppole"].includes(materialGroup || ""),
        [PoleMountType.ConcreteCube]: ["steel", "alu", "droppole"].includes(materialGroup || ""),
        [PoleMountType.Special]: ["telescope", "profile"].includes(materialGroup || "") ? false : true,
      };
      return ret;
    }
  }

  static _getValidPoleMountTypes(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): PoleMountType[] {
    const m = SunSailLogic.#getValidMountTypesForPoleMaterial(sunSail, poleId)
    return mapRecordToArray(m, (from, id) => from ? id : "" as any).filter((f) => !!f);
  }


  static getValidPoleMountTypesCached = CacheUtil.memo(SunSailLogic._getValidPoleMountTypes, "_getValidPoleMountTypes", {
    idsFromArgs: (sunSail, poleId) => [sunSail.id, poleId],
    inputsEqualFn: "ref",
    resultEqualFn: "shallow",
  });

  static getValidPoleMountTypes(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): PoleMountType[] {
    return SunSailLogic.getValidPoleMountTypesCached(sunSail, poleId);
  }


  static getMaxSunSailAreaItems(sunSailType: SunSailType, poleCount: number, version: ProductVersion, featureLevel: FeatureLevel, clothType?: ClothType, pylonCount?: number): { type: ProjectCheckErrorType, value: number; textId?: LanguageId }[] {

  // const warningId: LanguageId = "Rule_MaxSailArea_Warning"
  // const seriousErrorId: LanguageId = "Rule_MaxSailArea"

    switch (sunSailType) {
      case SunSailType.SolidayA: {
        if (poleCount === 3) return [{ type: ProjectCheckErrorType.Error, value: 27.5 }];
        if (Pvi.eq(version, "2024")) {
          return [{ type: ProjectCheckErrorType.Error, value: 55.0 }]
        }
        return [{ type: ProjectCheckErrorType.Error, value: 56.0 }]
      }
      case SunSailType.SolidayOne: {
        if (poleCount === 3) return [{ type: ProjectCheckErrorType.Error, value: 18.0 }]
        return [{ type: ProjectCheckErrorType.Warning, value: 50.0 }, { type: ProjectCheckErrorType.Error, value: 55.0 }]
      }
      case SunSailType.SolidayFlex: { // todo: Soliday-Flex
        if (poleCount === 3) return [{ type: ProjectCheckErrorType.Error, value: 18.0 }]
        if (featureLevel === "user")
          return [{ type: ProjectCheckErrorType.Error, value: 50.0 }]
        return [{ type: ProjectCheckErrorType.Error, value: 65.0 }]
      }
      case SunSailType.SolidayM: {
        if (poleCount === 3) return [{ type: ProjectCheckErrorType.Error, value: 32.5 }]
        return [{ type: ProjectCheckErrorType.Warning, value: 65.0 }, { type: ProjectCheckErrorType.Error, value: 70.0 }]; // plaspack.atlassian.net/browse/APP22-1014?focusedCommentId=12017
      }
      case SunSailType.SolidayC: {
        if (clothType === ClothType.Soltis92 || clothType === ClothType.Soltis86) return [{ type: ProjectCheckErrorType.Error, value: 55.0 }];
        if (pylonCount === 1) return [{ type: ProjectCheckErrorType.Error, value: 50.0 }]; // https://plaspack.atlassian.net/browse/APP22-1034
        return [{ type: ProjectCheckErrorType.Error, value: 85.5 }]; // https://plaspack.atlassian.net/browse/APPSK-144
      }
      case SunSailType.SolidayX:
        return [{ type: ProjectCheckErrorType.Error, value: 45.0 }]
      case SunSailType.SolidayCS:
        return poleCount === 3 ? [{ type: ProjectCheckErrorType.Error, value: 28.5 }] : [{ type: ProjectCheckErrorType.Error, value: 56.0 }]; // https://plaspack.atlassian.net/browse/APPSK-39
      case SunSailType.SolidayXS:
        if (poleCount === 3) return [{ type: ProjectCheckErrorType.Error, value: 22.5 }]
        return [{ type: ProjectCheckErrorType.Error, value: 35.0 }]
      case SunSailType.SolidayTwin:
        return [{ type: ProjectCheckErrorType.Error, value: 110.0 }]
      case SunSailType.Standard:
        return [];
      case SunSailType.Fixed:
      case SunSailType.Concept:
        return [{ type: ProjectCheckErrorType.Warning, value: 36.0, textId: "Rule_MaxSailArea_Warning_Fixed" }]
      case SunSailType.RaffC:
        return [{ type: ProjectCheckErrorType.Error, value: 60.0 }]
      case SunSailType.RaffM:
        return [{ type: ProjectCheckErrorType.Error, value: 36.0 }]
      case SunSailType.SandyRect:
      case SunSailType.SandyPoly:
        return [{ type: ProjectCheckErrorType.Error, value: 80.0, textId: "Rule_MaxSailArea_Error_Sandy" }];
      default:
        throw new Error("Invalid sunSailType!");
    }
    // return 100;
  }

  static #getMinMaxAxleIntern(sunSailType: SunSailType, version: ProductVersion, featureLevel: FeatureLevel, poleCount: number, axleType: AxleType | undefined): { min: number, max: number, minShopping?: number } {
    switch (sunSailType) {
      case SunSailType.SolidayA: {
        if (version === "2024")
          return poleCount === 3 ? { min: 3, max: 6 } : { min: 3.0, max: 9.0 }
        else
          return poleCount === 3 ? { min: 3, max: 6 } : { min: 3.0, max: 8.0 }
      }
      case SunSailType.SolidayX:
        return poleCount === 3 ? { min: 3, max: 6 } : { min: 3.0, max: 8.0 }
      case SunSailType.SolidayOne:
        return poleCount === 3 ? { min: 3, max: 6 } : { min: 3.0, max: 9.0 }
      case SunSailType.SolidayFlex: {
        if (poleCount === 3)
          return { min: 3, max: 6 };
        return { min: 3.0, max: { "user": 8, "dealer": 10.5, "soliday": 13 }[featureLevel] }
      }
      case SunSailType.SolidayM:
        return poleCount === 3 ? { min: 3, max: 6 } : { min: 4.0, max: 13.0 } // https://plaspack.atlassian.net/browse/APP22-1005
      case SunSailType.SolidayC:
        return { min: 3.0, minShopping: 4, max: axleType === "C_90" ? 12 : 11.5 }
      case SunSailType.SolidayCS:
        return { min: 2.0, max: 8.0 }
      case SunSailType.SolidayXS:
        return { min: 3.0, max: 6.0 }
      case SunSailType.SolidayTwin:
        return { min: 2.0, max: 8.0 }
    }
    return { min: 1, max: 10 }
  }

  static getLogicalMinMaxAxle(sunSail: ISunSailData): { min: number, max: number, minShopping: number } {
    const r = SunSailLogic.#getMinMaxAxleIntern(sunSail.sunSailType, sunSail.version, FL.fromProduct(sunSail), Poles.getCount(sunSail), SunSailLogic.getAxleType(sunSail));
    return { minShopping: r.min, ...r };
  }

  static isSameSide(a: PoleIndex, b: PoleIndex, a2: PoleIndex, b2: PoleIndex): boolean {
    if (a === a2 && b === b2) return true;
    if (a === b2 && b === a2) return true;
    return false;
  }

  static splitPoly2(points: IVec2[], ia: IVec2, ib: IVec2): { left: IVec2[]; right: IVec2[] } {
    let isLeft = false;
    const normalized = Vec2.dir(ia, ib);
    const normal = { x: normalized.y, y: -normalized.x };

    const left: IVec2[] = [];
    const right: IVec2[] = [];
    for (let i = 0; i < points.length; i++) {
      const a = points[i];
      const b = points[(i + 1) % points.length];

      if (i === 0) {
        const v2 = Vec2.dir(ia, a);
        isLeft = Vec2.dot(v2, normal) > 0;
      }
      const intersection = getLineIntersection(a.x, a.y, b.x, b.y, ia.x, ia.y, ib.x, ib.y);

      if (intersection) {
        (isLeft ? left : right).push(a);
        if (intersection.x === b.x && intersection.y === b.y) {
          (isLeft ? left : right).push({ x: intersection.x, y: intersection.y });
        } else if (intersection.x === a.x && intersection.y === a.y) {
          //
        } else {
          (isLeft ? left : right).push({ x: intersection.x, y: intersection.y });
          (isLeft ? right : left).push({ x: intersection.x, y: intersection.y });
        }
        isLeft = !isLeft;
      } else {
        (isLeft ? left : right).push(a);
      }
    }
    if (left.length <= 2) left.length = 0;
    if (right.length <= 2) right.length = 0;
    return { left, right };
  }

  static getMaxNetSideLength(sunSailType: SunSailType, featureLevel: FeatureLevel, pylonCount: number): { warning: number; error: number; } {
    switch (sunSailType) {
      case SunSailType.SolidayA:
        return { warning: 9.5, error: Number.MAX_VALUE }
      case SunSailType.SolidayOne:
        return { warning: 9.5, error: Number.MAX_VALUE }
      case SunSailType.SolidayFlex: // todo: Soliday-FLEX
        return { warning: featureLevel === "user" ? 8.0 : 9.0, error: Number.MAX_VALUE }
      case SunSailType.SolidayM:
        return { warning: 9.0, error: 11.0 }; // https://plaspack.atlassian.net/browse/APP-1332
      case SunSailType.SolidayX:
        return { warning: 9.0, error: Number.MAX_VALUE }
      case SunSailType.SolidayC:
        return { warning: pylonCount === 2 ? 10 : 9, error: Number.MAX_VALUE }
      case SunSailType.Fixed:
        return { warning: 12.0, error: featureLevel === "user" ? 12.0 : Number.MAX_VALUE  }

    }
    return { warning: 12.0, error: Number.MAX_VALUE }
  }

  static getMaxMountSideLength(sunSailType: SunSailType): number {
    if (sunSailType === SunSailType.Concept)
      return 8.0;
    return Number.MAX_VALUE;
  }

  static findNetGroup(netGroups: SunSailGroup[], poleIndex: PoleIndex, groupIndex?: number): number {
    if (groupIndex) return groupIndex;
    for (let iGroup = 0; iGroup < netGroups.length; iGroup++) {
      if (netGroups[iGroup].poleIndices.includes(poleIndex)) {
        return iGroup;
      }
    }
    return -1;
  }

  static calcAreaPositioningDistFromNormal(sunSailType: SunSailType): number {
    switch (sunSailType) {
      case SunSailType.SolidayCS:
        return 0.3;
      case SunSailType.SolidayXS:
        return 0.2;
    }
    return 0;
  }

  static calcClothIndexFromEdge(sunSailType: SunSailType, poleCount: number, edge: EdgeName): number {
    const poleIndices = getEdgePoleIds(edge);
    const g1 = SunSailLogic.calcClothIndexFromPole(sunSailType, poleCount, poleIndices[0]);
    const g2 = SunSailLogic.calcClothIndexFromPole(sunSailType, poleCount, poleIndices[1]);
    return Math.max(g1, g2);
  }

  static calcClothIndexFromPole(sunSailType: SunSailType, poleCount: number, poleIndex: number): number {
    if (SST.is(sunSailType, SST.CsXsTwin)) {
      return LogicS.getSideIndexForPole(poleIndex);
    }
    const topology = SunSailTopology.calcTopology(sunSailType, poleCount);
    for (let i = 0; i < topology.groups.length; i++) {
      if (topology.groups[i].nonAxleIndices.includes(poleIndex)) {
        return i;
      }
    }
    return 0;
  }

  static getMaxAllowedDistOut(sunSailType: SunSailType, pylonType: PylonType | undefined): number {
    switch (sunSailType) {
      case SunSailType.SolidayA:
        return 7.0;
      case SunSailType.SolidayOne:
        return 6.0;
      case SunSailType.SolidayFlex:
        return 7.5;
      case SunSailType.SolidayX:
        return 6.0;
      case SunSailType.SolidayM:
        return 8.0;
      case SunSailType.SolidayC:
      case SunSailType.SolidayCS:
      case SunSailType.SolidayTwin: {
        if (pylonType) {
          return SunSailLogic.getPylonInfo(pylonType).maxDistOut;
        }
        return 7.1;
      }
      case SunSailType.SolidayXS:
        return 6.0;
    }
    return 1000.0;
  }

  static getMaxAllowedDistInAxle(sunSailType: SunSailType, nettoAxleLength: number): { hard: number; soft: number } {
    const factor = { hard: 0.5, soft: 1 / 3 }
    const di = { hard: factor.hard * nettoAxleLength, soft: factor.soft * nettoAxleLength };
    if (SST.is(sunSailType, SST.CsXsTwin)) {
      const val = Math.max((nettoAxleLength - 2) * 0.5, 0);
      return {
        hard: val,
        soft: val
      }
    } else {
      return di
    }
  }


  static getMaxAllowedDistInNonAxle(sunSailType: SunSailType): number {
    switch (sunSailType) {
      case SunSailType.SolidayA:
      case SunSailType.SolidayM:
      case SunSailType.SolidayC:
      case SunSailType.SolidayX:
        return 5.0;
      case SunSailType.SolidayCS:
      case SunSailType.SolidayXS:
      case SunSailType.SolidayTwin:
        return 4.0;
    }
    return 5.0;
  }

  static getMaxAllowedDistInNonAxleWarning(sunSailType: SunSailType, poleCount: number, poleIndex: number): number {
    if (SST.is(sunSailType, SST.CsXsTwin) && LogicS.isQuadFrontPole(sunSailType, poleCount, poleIndex))
      return 2;

    switch (sunSailType) {
      case SunSailType.SolidayC:
      case SunSailType.SolidayCS: // CS3
        return 3.0;
      case SunSailType.SolidayA:
      case SunSailType.SolidayM:
      case SunSailType.SolidayX:
        return 4.0;
      case SunSailType.SolidayOne:
        return 5.0;
      case SunSailType.SolidayFlex:
        return 5.0;

    }
    return Number.MAX_VALUE;
  }

  // possible improvement: Satz von der Winkelhalbierenden: Teilt gegenüberliegende Seite im Verhältnis der Anliegenden Seiten...
  static calcApproximation(sunSail: ISunSailData, poleIndex: number, axlePositions: IAxlePos<IVec3>): { point: IVec3; half: IVec3 } {
    const sunSailId = sunSail.id;
    const pole = Poles.getPole(sunSail, poleIndex);
    const mountPosDelta = Vec3.from(0, -0.1, -0.1);
    const lengthBVal = SunSailLogic.getPoleLengthB(sunSail, poleIndex);
    const bottomExtension = SunSailLogic.calcBottomPoleExtension(sunSail, poleIndex);
    const offsetY = SunSailLogic.calcPoleOffsetY(sunSail, poleIndex)
    const poleAngle = SunSailLogic.getPoleAngle(sunSail, poleIndex);
    const poleLength = SunSailLogic.getPoleLength(sunSail, poleIndex);
    const m = PoleHelper.calcMatrixForLengthPos(sunSailId, poleIndex, poleLength, poleAngle, lengthBVal, bottomExtension, offsetY, poleLength + mountPosDelta.y);
    const mPos = Vec3.applyMatrix4(Vec3.from(0, 0, mountPosDelta.z), m);

    // use pole pos with target height as approximation point for approximated half vector
    const approximationPoint1 = Vec3.add(pole.position, Vec3.y(mPos));

    const approxHalf = TriangleHelper.calcHalfVector(approximationPoint1, axlePositions.first, axlePositions.second);
    const rotationY = SunSailMath.calcGroundRotationFromVec(approxHalf);
    const groundMatrix = SunSailLogic.calcPoleGroundMatrixFromValues(pole.position, rotationY);

    // calc better approximation point with approximated half vector
    const approximationPoint = Vec3.applyMatrix4(mPos, groundMatrix);
    return {
      point: approximationPoint,
      half: TriangleHelper.calcHalfVector(approximationPoint, axlePositions.first, axlePositions.second),
    };
  }

  // *** axle helper ***
  static #handleDouble(dmt: DoubleMountType | undefined, mountInfo: IExtendedMountInfo): { doubleMount?: DoubleMountType, extendedMount: IExtendedMountInfo[] } {
    if (dmt === "x")
      return { doubleMount: dmt, extendedMount: [mountInfo, mountInfo] };
    if (dmt === "xs") {
      if (mountInfo.type === "heightChange")
        return { doubleMount: dmt, extendedMount: [{ type: "heightChange" }, mountInfo] };
      return { doubleMount: dmt, extendedMount: [{ type: "direct" }, mountInfo] };
    }
    return { extendedMount: [mountInfo] };
  }

  static #handleWall(_sunSailType: SunSailType, dmt: DoubleMountType | undefined, rollType: MountArticle): { doubleMount?: DoubleMountType, extendedMount: IExtendedMountInfo[] } {
    if (dmt === "xs")
      return { doubleMount: dmt, extendedMount: [{ type: "shackleOnly", articles: ["00E13000026"] }, { type: "rollOnly", articles: [rollType] }] }
    if (dmt)
      return SunSailLogic.#handleDouble(dmt, { type: "rollOnly", articles: [rollType] });
    return { doubleMount: dmt, extendedMount: [{ type: "rollOnly", articles: [rollType] }] }
  }

  static #calcNonAxleMountTypes(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): IPointMountInfo[] {
    const poleType = SunSailLogic.getPoleType(sunSail, poleIndex)
    const poleMaterial = SunSailLogic.getPoleMaterial(sunSail, poleIndex)
    const pylonType = SunSailLogic.getPolePylonType(sunSail, poleIndex);


    const sunSailType = PD.productToSunSailType(sunSail)
    if (PoleLogic.isAxle(sunSailType, poleIndex))
      return [{ mountType: "direct", extendedMount: [{ type: "direct" }] }];

    if (poleType === PoleType.Keder)
      return [{ mountType: "keder", extendedMount: [] }]

    if (pylonType) {
      return [{ mountType: "pylon", extendedMount: [{ type: "direct" }] }];
    }

    const rollShackle = "00E13000026";
    const roll = SunSailLogic.calcRollArticle(sunSail, poleIndex);

    if (SST.is(sunSailType, SST.One)) {
      const doubleMount = SunSailLogic.isDoubleMount(sunSail, poleIndex);
      if (SunSailLogic.getHeightChangeArticle(sunSail, poleIndex)) {
        return [{ mountType: "heightChange", extendedMount: [{ type: "heightChange" }] }];
      }

      switch (poleType) {
        case PoleType.Wall: {
          return [{ mountType: "wall", ...SunSailLogic.#handleWall(sunSailType, doubleMount, roll) }];
        }
        case PoleType.Special: {
          return [{ mountType: "special", ...SunSailLogic.#handleWall(sunSailType, doubleMount, roll) }];
        }
        case PoleType.SailOnly: {
          return [{ mountType: "direct", extendedMount: [{ type: "direct" }] }];
        }
      }

      return [
        { mountType: "clamp", ...SunSailLogic.#handleDouble(doubleMount, { type: "rollOnly", articles: [roll] }) },
        { mountType: "bolt", ...SunSailLogic.#handleDouble(doubleMount, { type: "rollOnly", articles: [roll] }) }
      ];
    }
    else if (SST.is(sunSailType, SST.AMX)) {
      const doubleMount = SunSailLogic.isDoubleMount(sunSail, poleIndex);
      if (SunSailLogic.getHeightChangeArticle(sunSail, poleIndex)) {
        return [{ mountType: "heightChange", doubleMount, ...SunSailLogic.#handleDouble(doubleMount, { type: "heightChange" }) }];
      }

      switch (poleType) {
        case PoleType.Wall: {
          return [{ mountType: "wall", ...SunSailLogic.#handleWall(sunSailType, doubleMount, roll) }];
        }
        case PoleType.Special: {
          return [{ mountType: "special", ...SunSailLogic.#handleWall(sunSailType, doubleMount, roll) }];
        }
        case PoleType.SailOnly: {
          return [{ mountType: "direct", extendedMount: [{ type: "direct" }] }];
        }
      }

      return [
        { mountType: "bolt", ...SunSailLogic.#handleDouble(doubleMount, { type: "rollOnly", articles: [roll] }) },
        { mountType: "clamp", ...SunSailLogic.#handleDouble(doubleMount, { type: "rollOnly", articles: [roll] }) }
      ];
    } else if (SST.is(sunSailType, SST.C)) {
      if (!pylonType) {
        const indirectPylonInfos = IndirectPylonLogic.getIndirectPylons(sunSail);
        if (IndirectPylonLogic.isOppositeIndirectPylon(indirectPylonInfos, poleIndex)) {
          switch (poleType) {
            case PoleType.Wall:
              return [{ mountType: "wall", extendedMount: [{ type: "shackleOnly", articles: [rollShackle] }] }]
            case PoleType.Special:
              return [{ mountType: "special", extendedMount: [{ type: "shackleOnly", articles: [rollShackle] }] }]
            case PoleType.SailOnly: {
              return [{ mountType: "direct", extendedMount: [{ type: "direct" }] }];
            }
          }
          return [
            { mountType: "clamp", extendedMount: [{ type: "shackleOnly", articles: [rollShackle] }] },
            { mountType: "bolt", extendedMount: [{ type: "shackleOnly", articles: [rollShackle] }] }
          ];
        } else if (IndirectPylonLogic.isOppositeAxlePylon(indirectPylonInfos, poleIndex)) {
          // if (poleType === PoleType.Wall)
          //   return [{ mountType: "wall", extendedMountType: "shackleOnly" }]
          // return [{ mountType: "clamp", extendedMountType: "shackleOnly" }, { mountType: "bolt", extendedMountType: "shackleOnly" }];
        }
      }

      switch (poleType) {
        case PoleType.Wall:
          return [{ mountType: "wall", extendedMount: [{ type: "rollOnly", articles: ["00E13001151"] }] }];
        case PoleType.Special:
          return [{ mountType: "wall", extendedMount: [{ type: "rollOnly", articles: ["00E13001151"] }] }];
        case PoleType.SailOnly:
          return [{ mountType: "direct", extendedMount: [{ type: "direct" }] }];
      }

      if (PoleLogic.isAluOrSteel(poleMaterial))
        return [
          { mountType: "clamp", extendedMount: [{ type: "rollOnly", articles: ["00E13001151"] }] },
          { mountType: "bolt", extendedMount: [{ type: "rollOnly", articles: ["00E13001151"] }] }
        ];

      return [{ mountType: "clamp", extendedMount: [{ type: "rollOnly", articles: ["00E13001151"] }] }];
    } else if (SST.is(sunSailType, SST.Xs)) {
      const doubleMount = SunSailLogic.isDoubleMount(sunSail, poleIndex);
      const isCoilUpPole = SunSailLogic.getSailCoilUpPoleIndex(sunSail) === poleIndex;
      if (SunSailLogic.getHeightChangeArticle(sunSail, poleIndex)) {
        if (isCoilUpPole)
          return [{ mountType: "heightChange", ...SunSailLogic.#handleDouble(doubleMount, { type: "heightChange" }) }];
        else
          return [{ mountType: "heightChange", ...SunSailLogic.#handleDouble(doubleMount, { type: "shackleOnly", articles: [rollShackle] }) }];
      }

      switch (poleType) {
        case PoleType.Wall:
          return [{ mountType: "wall", ...SunSailLogic.#handleWall(sunSailType, doubleMount, roll) }];
        case PoleType.Special:
          return [{ mountType: "special", ...SunSailLogic.#handleWall(sunSailType, doubleMount, roll) }];
        case PoleType.SailOnly:
          return [{ mountType: "direct", extendedMount: [{ type: "direct" }] }];
      }

      if (isCoilUpPole)
        return [
          { mountType: "bolt", ...SunSailLogic.#handleDouble(doubleMount, { type: "rollOnly", articles: [roll] }) },
          { mountType: "clamp", ...SunSailLogic.#handleDouble(doubleMount, { type: "rollOnly", articles: [roll] }) }
        ];
      else
        return [
          { mountType: "bolt", ...SunSailLogic.#handleDouble(doubleMount, { type: "shackleOnly", articles: [rollShackle] }) },
          { mountType: "clamp", ...SunSailLogic.#handleDouble(doubleMount, { type: "shackleOnly", articles: [rollShackle] }) }
        ];
    } else if (SST.is(sunSailType, SST.CoSF, SST.Flex)) {
      if (SunSailLogic.getHeightChangeArticle(sunSail, poleIndex)) {
        const doubleMount = SunSailLogic.isDoubleMount(sunSail, poleIndex);
        return [{ mountType: "heightChange", ...SunSailLogic.#handleDouble(doubleMount, { type: "heightChange" }) }];
      }

      const sailMountArticle = SunSailLogic.getSailMountArticle(sunSail, poleIndex);
      // const sm2 = SunSailLogic.getSailMount(sunSail, poleIndex);
      // console.log("SM", poleIndex, sailMountArticle, sm2)
      const additionalSailMountArticleMap: Record<string, IExtendedMountInfo> = {
        "00E13000801": { type: "shackleOnly", articles: [rollShackle] }, // Spanngurt mit Schnappschäkel
        "00E13000024": { type: "shackleOnly", articles: [rollShackle] }, // Profi-Seilspanner schwarz
        "00E13000129": { type: "shackleOnly", articles: [rollShackle] }, // Gurtbandratsche aus Edelstahl für Verspannung
      }
      const em: IExtendedMountInfo = additionalSailMountArticleMap[sailMountArticle || ""] || { type: "direct" };

      const pulley = SunSailLogic.getSailPulleyArticle(sunSail, poleIndex);
      switch (poleType) {
        case PoleType.Wall:
          return [{ mountType: "wall", extendedMount: [pulley ? { type: "rollOnly", articles: [pulley as MountArticle] } : em] }];
        case PoleType.Special:
          return [{ mountType: "special", extendedMount: [pulley ? { type: "rollOnly", articles: [pulley as MountArticle] } : em] }];
        case PoleType.SailOnly:
          return [{ mountType: "direct", extendedMount: [{ type: "direct" }] }];
      }

      if (PoleLogic.isAluOrSteel(poleMaterial) || PoleLogic.isIron(poleMaterial)) {
        return [
          { mountType: "clamp", extendedMount: [pulley ? { type: "rollOnly", articles: [pulley as MountArticle] } : em] },
          { mountType: "bolt", extendedMount: [pulley ? { type: "rollOnly", articles: [pulley as MountArticle] } : em] }
        ];
      } else if (PoleLogic.isTeleProfil(poleMaterial)) {
        return [{ mountType: PoleLogic.isTelescope(poleMaterial) ? "telescope" : "profile", extendedMount: [pulley ? { type: "rollOnly", articles: [pulley as MountArticle] } : em] }];
      }

      return [{ mountType: "clamp", extendedMount: [pulley ? { type: "rollOnly", articles: [pulley as MountArticle] } : em] }];
    } else if (SST.is(sunSailType, SST.Sandy)) {
      const article = SunSailLogic.getHeightChangeArticle(sunSail, poleIndex)
      if (article)
        return [{ mountType: "heightChange", extendedMount: [{ type: "direct" }] }];
    }
    return [{ mountType: "direct", extendedMount: [{ type: "direct" }] }];
  }

  static solvePrimaryClampOrBolt(mountInfos: IPointMountInfo[], sunSail: ISunSailData | ISCSunSailData, poleIndex: number): IPointMountInfo {
    if (mountInfos.length === 1)
      return mountInfos[0];
    const primaryMountArticle = SunSailLogic.getPrimaryMountArticle(sunSail, poleIndex);
    if (!primaryMountArticle) {
      throw new Error("No mount article!");
    }
    const item = mountInfos.find((mi) => mi.mountType === primaryMountArticle.mountType)
    if (item)
      return item;
    return mountInfos[0];
  }

  static calcMountTypes(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): IPointMountInfo[] {
    const sunSailType = PD.productToSunSailType(sunSail);
    const isAxle = PoleLogic.isAxle(sunSailType, poleIndex);
    return isAxle ? SunSailLogic.#calcAxleMountTypes(sunSail, poleIndex) : SunSailLogic.#calcNonAxleMountTypes(sunSail, poleIndex);
  }

  static #calcPrimaryMountType(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): IPointMountInfo {
    return SunSailLogic.solvePrimaryClampOrBolt(SunSailLogic.calcMountTypes(sunSail, poleIndex), sunSail, poleIndex);
  }

  static calcPrimaryMountType = CacheUtil.memo(SunSailLogic.#calcPrimaryMountType, "SunSailLogic.calcPrimaryMountType", {
    idsFromArgs: (sunSail, poleIndex) => [sunSail.id, poleIndex],
    inputsEqualFn: "ref",
    resultEqualFn: "level3",
  });

  static calcAxleMountType(ssd: ISunSailData): AxlePoleWallMountType | undefined {
    const indices = PoleLogic.getAxleIndices(ssd.sunSailType);
    if (indices) {
      const first = SunSailLogic.getPoleType(ssd, indices.first) === PoleType.Pole ? "m" : "w";
      const second = SunSailLogic.getPoleType(ssd, indices.second) === PoleType.Pole ? "m" : "w";
      return (first + second) as AxlePoleWallMountType;
    }
    return undefined;
  }

  static calcVariant68(sunSail: ISunSailData): "WAND" | "MAST" | undefined {
    const axleMountType = SunSailLogic.calcAxleMountType(sunSail);
    switch (axleMountType) {
      case undefined:
        return undefined;
      case "ww":
        return "WAND";
      default:
        return "MAST";
    }
  }

  static calcVariant06(poleCount: number): "3-ECK" | "4-ECK" | "5-ECK" | "6-ECK" | undefined {
    switch (poleCount) {
      case 3:
        return "3-ECK";
      case 4:
        return "4-ECK";
      case 5:
        return "5-ECK";
      case 6:
        return "6-ECK";
    }
    return undefined;
  }

  static #calcAxleMountTypes(sunSail: ISunSailData | ISCSunSailData, poleIndex: PoleIndex): IPointMountInfo[] {
    const sunSailType = PD.productToSunSailType(sunSail)
    const poleMaterial = SunSailLogic.getPoleMaterial(sunSail, poleIndex);
    const poleType = SunSailLogic.getPoleType(sunSail, poleIndex)

    if (FlexLogic.isFlexC(sunSailType, poleIndex)) {
      if (SunSailLogic.getHeightChangeArticle(sunSail, poleIndex)) {
        const doubleMount = SunSailLogic.isDoubleMount(sunSail, poleIndex);
        return [{ mountType: "heightChange", ...SunSailLogic.#handleDouble(doubleMount, { type: "heightChange" }) }];
      }

      // const rollShackle = "00E13000026";
      const pulley = SunSailLogic.getSailPulleyArticle(sunSail, poleIndex);
      switch (poleType) {
        case PoleType.Wall:
          return [{ mountType: "wall", extendedMount: [pulley ? { type: "rollOnly", articles: [pulley as MountArticle] } : { type: "direct" }] }];
        case PoleType.Special:
          return [{ mountType: "special", extendedMount: [pulley ? { type: "rollOnly", articles: [pulley as MountArticle] } : { type: "direct" }] }];
        case PoleType.SailOnly:
          return [{ mountType: "direct", extendedMount: [{ type: "direct" }] }];
      }

      if (PoleLogic.isAluOrSteel(poleMaterial) || PoleLogic.isIron(poleMaterial)) {
        return [
          { mountType: "clamp", extendedMount: [pulley ? { type: "rollOnly", articles: [pulley as MountArticle] } : { type: "direct" }] },
          { mountType: "bolt", extendedMount: [pulley ? { type: "rollOnly", articles: [pulley as MountArticle] } : { type: "direct" }] }
        ];
      } else if (PoleLogic.isTeleProfil(poleMaterial)) {
        return [{ mountType: PoleLogic.isTelescope(poleMaterial) ? "telescope" : "profile", extendedMount: [pulley ? { type: "rollOnly", articles: [pulley as MountArticle] } : { type: "direct" }] }];
      }

      return [{ mountType: "clamp", extendedMount: [pulley ? { type: "rollOnly", articles: [pulley as MountArticle] } : { type: "direct" }] }];
    }


    const extendedMount: IExtendedMountInfo[] = [{ type: "direct" }];
    switch (poleType) {
      case PoleType.Special:
        return [{ mountType: "special", extendedMount }];
      case PoleType.Wall: {
        const mountArticle = SunSailLogic.getWallPlateArticle(sunSail, poleIndex);
        if (["00E13001095", "00E13001910", "00E13001889"].includes(mountArticle)) {
          if (poleIndex === PoleIndex.A)
            return [{ mountType: "direct", extendedMount }];
          return [{ mountType: "wallShackle", extendedMount }];
        }
        if (mountArticle === "00E13000046" || mountArticle === "00E13001862" || mountArticle === "00E13000118" || mountArticle === "00E13001910" || mountArticle === "00E13001889" || mountArticle === "00E13001025")
          return [{ mountType: "direct", extendedMount }];

        const indirectPylons = IndirectPylonLogic.getIndirectPylons(sunSail);
        for (const ip of indirectPylons) {
          if (ip.oppositeAxlePylon === poleIndex)
            return [{ mountType: "adapter_buegel", extendedMount }];
        }

        if (SunSailLogic.hasTwistingSetForSunSail(sunSail)) {
          if (ONE_AXLE_WALL_PLATES.includes(mountArticle))
            return [{ mountType: "adapter", extendedMount }];
          return [{ mountType: "adapter_buegel", extendedMount }];
        }

        return [{ mountType: "adapter", extendedMount }];
      }
      case PoleType.Pole: {
        switch (sunSailType) {
          case SunSailType.SolidayC:
            return [{ mountType: "clamp", extendedMount }];
          case SunSailType.SolidayA:
            if (Pvi.is(sunSail.version, "2024"))
              return [{ mountType: "clamp", extendedMount }];
            return [{ mountType: poleIndex === PoleIndex.C && !PoleLogic.isDroppole(poleMaterial) ? "bolt" : "clamp", extendedMount }];
          case SunSailType.SolidayOne:
            return [{ mountType: "clamp", extendedMount }];
          case SunSailType.SolidayFlex:
            return [{ mountType: "clamp", extendedMount }];
          case SunSailType.SolidayM: {
            if (poleIndex === SunSailLogic.getThreadedPoleIndex(sunSail))
              return [{ mountType: "threaded", extendedMount }];
            if (sunSail.version === "2024")
              return [{ mountType: "clamp", extendedMount }]
            return [{ mountType: poleIndex === PoleIndex.C && !PoleLogic.isDroppole(poleMaterial) ? "bolt" : "clamp", extendedMount }];
          }
          case SunSailType.SolidayX: {
            return [{ mountType: "clamp", extendedMount }];
          }
          case SunSailType.SolidayCS:
          case SunSailType.SolidayXS: {
            if (PoleLogic.isDroppole(poleMaterial)) {
              return [
                { mountType: "ringAdapter", extendedMount },
                { mountType: "clamp", extendedMount },
              ]
            }
            return [{ mountType: "clamp", extendedMount }];
          }
        }
        return [{ mountType: "clamp", extendedMount }];
      }
    }
    return [{ mountType: "direct", extendedMount }];
  }

  static hasAxleThreadTensioner(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): boolean {
    const sunSailType = PD.productToSunSailType(sunSail);
    if (sunSailType === SunSailType.SolidayM && Pvi.is(sunSail.version, "2024") && poleIndex === PoleIndex.C) {
      if (SunSailLogic.getPoleType(sunSail, PoleIndex.A) === PoleType.Wall && SunSailLogic.getPoleType(sunSail, PoleIndex.C) === PoleType.Wall)
        return true;
    }
    return false;
  }

  // Outer Offset => Point => MountingPoint => AxleStart => MinCloth
  static calcOuterOffset(pointType: PointType, material: MaterialType): number {
    switch (pointType) {
      case "none":
        return 0.0;
      case "pole":
        {
          switch (material) {
            case "Steel":
            case "Steel_V4A":
              return 0.035;
            case "Alu":
            case "AluWood":
            case "Alu_Black":
              return 0.039;
            case "Droppole":
            case "Droppole_Black":
              return 0.07;
            case "Telescope":
            case "Telescope_Black":
            case "TelescopeWood":
              return 0.04;
            case "Profile":
            case "Profile_Black":
              return 0.05;
            case "Iron48":
              return 0.024;
            case "Iron114":
              return 0.057;
            case "Iron89":
            case "Iron89_Black":
              return 0.089;
          }
        }
        return 0.04;
      case "wall":
        return 0.033;
    }
    Debug.notSupported();
    return 0.0;
  }

  // Outer Offset => Point => MountingPoint => AxleStart => MinCloth
  static getPointToMountDist(sunSailType: SunSailType, pointType: PointType, iPole: number, material: MaterialType): number {
    if (PoleLogic.isAxle(sunSailType, iPole)) {
      if (pointType === "pole") {
        if (PoleLogic.isAlu(material) || PoleLogic.isDroppole(material)) return 0.094;
        if (PoleLogic.isSteel(material)) return 0.09;
        return 0.09;
      } else {
        return 0.0;
      }
    } else {
      if (pointType === "pole") {
        if (PoleLogic.isAlu(material) || PoleLogic.isDroppole(material)) return 0.06;
        if (PoleLogic.isSteel(material)) return 0.056;
        return 0.06;
      } else if (pointType === "wall") {
        return 0.0;
      } else return 0.0; // e.g. Raff...
    }
  }

  static calcAxlePoleWallMountType(sunSail: ISunSailData): AxlePoleWallMountType | undefined {
    const axleIndices = PoleLogic.getAxleIndices(sunSail.sunSailType);
    if (!axleIndices)
      return undefined;
    const first = SunSailLogic.getPoleType(sunSail, axleIndices.first) === PoleType.Pole ? "m" : "w";
    const second = SunSailLogic.getPoleType(sunSail, axleIndices.second) === PoleType.Pole ? "m" : "w";
    return (first + second) as any;
  }

  static calcAxleBearingFromSunSail(sunSail: ISunSailData, poleIndex: number): "long" | "short" {
    const calcLowerAxleIndex = (): number => {
      const axleMountPositions = SunSailLogic.calcMountPositionsForAxlePoles(sunSail, 0)!
      return SunSailLogic.calcLowerAxleIndex(sunSail.sunSailType, axleMountPositions) || 0;
    }
    return SunSailLogic.#calcAxleBearginsFromAxleMountType(sunSail.sunSailType, poleIndex, SunSailLogic.calcAxlePoleWallMountType(sunSail), calcLowerAxleIndex)
  }

  static calcLowerAxleIndex(sunSailType: SunSailType, mountPositions: IVec3[] | IAxlePos<IVec3>): number | undefined {
    const axleIndices = PoleLogic.getAxleIndices(sunSailType)
    if (!axleIndices)
      return undefined;

    const firstY = Array.isArray(mountPositions) ? mountPositions[axleIndices.first].y : mountPositions.first.y;
    const secondY = Array.isArray(mountPositions) ? mountPositions[axleIndices.second].y : mountPositions.second.y;
    return firstY <= secondY ? axleIndices.first : axleIndices.second;
  }

  static #calcAxleBearginsFromAxleMountType(sunSailType: SunSailType, poleId: PoleId, axleType: AxlePoleWallMountType | undefined, calcLowerAxlePoint: () => number): "long" | "short" {
    if (axleType !== "ww")
      return "short";
    if (sunSailType === SunSailType.SolidayX) {
      const lowerAxlePoint = calcLowerAxlePoint();
      return poleId === lowerAxlePoint ? "short" : "long";
    }
    return poleId === 0 ? "short" : "long";
  }

  static calcMinMaxMountToAxle(sunSail: ISunSailData, poleId: PoleId): { min: number, max: number } {
    if (!PoleLogic.isAxle(sunSail.sunSailType, poleId))
      throw new Error("calcMinMax called for non axle point")
    if (!FlexLogic.isFlexC(sunSail.sunSailType, poleId))
      throw new Error("calcMinMaxMountToAxle just implemented for C Point on Flex!")

    const mp = SunSailLogic.calcMountPositionsForAxlePoles(sunSail, 0)!;
    const axleMountLen = Vec3.len(mp.first, mp.second);

    const min = SunSailLogic.getSailMount(sunSail, poleId) === "float" ? 0.5 : 0.32;
    const minMaxAxle = SunSailLogic.getLogicalMinMaxAxle(sunSail)
    return {
      min,
      max: Math.max(Math.min(axleMountLen - SunSailLogic.getMountToBruttoAxleDist(sunSail, PoleIndex.A) - minMaxAxle.min, 4.5), min)
    }
  }

  static getMountToLogicalAxleDist(sunSail: ISunSailData, poleId: PoleId): number {
    const axleLengthType = AxleLogic.getLogicalAxleLengthType(sunSail);
    if (axleLengthType === "brutto")
      return SunSailLogic.getMountToBruttoAxleDist(sunSail, poleId);
    else if (axleLengthType === "netto")
      return SunSailLogic.getMountToNettoAxleDist(sunSail, poleId);
    throw new Error("Invalid axleLengthType! " + axleLengthType);
  }

  static getMountToBruttoAxleDist(sunSail: ISunSailData, poleId: PoleId): number {
    Debug.assert(PoleLogic.isAxle(sunSail.sunSailType, poleId), "Pole is not an axle pole!");
    switch (sunSail.sunSailType) {
      case SunSailType.SolidayC:
        return poleId === PoleIndex.A ? 0.034 : 0.084; // https://plaspack.atlassian.net/browse/API-96
      case SunSailType.SolidayXS:
      case SunSailType.SolidayX: {  // estimated
        return SunSailLogic.calcAxleBearingFromSunSail(sunSail, poleId) === "short" ? 0.04 : 0.078
      }
      case SunSailType.SolidayTwin:
      case SunSailType.SolidayCS:
        return poleId === PoleIndex.A ? 0.034 : 0.084; // https://plaspack.atlassian.net/browse/API-96
      case SunSailType.SolidayA:
        if (Pvi.eq(sunSail.version, "2024"))
          return poleId === PoleIndex.A ? 0.068 : 0.089; // https://plaspack.atlassian.net/browse/API-96
        return 0.07;
      case SunSailType.SolidayOne: {
        return poleId === PoleIndex.A ? 0.068 : 0.089; // https://plaspack.atlassian.net/browse/API-96
      }
      case SunSailType.SolidayM:
        if (Pvi.eq(sunSail.version, "2024")) {
          if (poleId === PoleIndex.A)
            return 0.08;
          if (poleId === PoleIndex.C) {
            return 0.135 + (SunSailLogic.hasAxleThreadTensioner(sunSail, poleId) ? THREAD_TENSIONER_DIST : 0);
          }
          return 0;
        } else
          return 0.07;
      case SunSailType.SolidayFlex: { // todo: Soliday-Flex
        if (poleId === PoleIndex.A)
          return 0.105;
        const minMax = SunSailLogic.calcMinMaxMountToAxle(sunSail, poleId);
        const mountToAxle = Poles.getPole(sunSail, poleId).mountToAxle;
        if (mountToAxle === undefined)
          return minMax.min;
        return MathUtil.clamp(mountToAxle, minMax.min, minMax.max);
      }
    }
    return 0.07;
  }

  static getMountToNettoAxleDist(sunSail: ISunSailData, poleIndex: number): number {
    if (PoleLogic.isAxle(sunSail.sunSailType, poleIndex))
      return SunSailLogic.getMountToBruttoAxleDist(sunSail, poleIndex) + AxleLogic.getAxleBruttoToNettoDist(sunSail, poleIndex);
    else {
      return 0.0;
    }
  }

  static calcAxleDists(sunSail: ISunSailData, fn: (sunSail: ISunSailData, poleIndex: PoleIndex) => number): IAxlePos<number> {
    const axleIndices = PoleLogic.getAxleIndices(sunSail.sunSailType);
    if (!axleIndices)
      return {
        first: 0,
        second: 0
      }
    return {
      first: fn(sunSail, axleIndices.first),
      second: fn(sunSail, axleIndices.second),
    }
  }

  static calcClampAngle(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): number {
    if (SunSailLogic.isPerpendicular(sunSail, poleIndex)) {
      return (poleIndex === 1 ? -1 : 1) * Math.PI * 0.5
    }
    return 0;
  }

  static calcPoleSegments(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId): IPoleSegmentsExtended {
    const lengthBVal = SunSailLogic.getPoleLengthB(sunSail, poleId);
    const bottomExtension = SunSailLogic.calcBottomPoleExtension(sunSail, poleId);
    const poleAngle = SunSailLogic.getPoleAngle(sunSail, poleId);
    const poleLength = SunSailLogic.getPoleLength(sunSail, poleId);
    const poleSegments = PoleHelper.calcPoleSegmentsExtended(sunSail.id, poleId, poleLength, poleAngle, lengthBVal, bottomExtension, SunSailLogic.calcPoleOffsetY(sunSail, poleId));
    return poleSegments;
  }

  private static _calcAxleMountPosition(sunSail: ISunSailData, poleIndex: number, axleIndex: number): IVec3 {
    const positionMode = sunSail.positionMode;
    switch (positionMode || "bottom") {
      case "mount": return SunSailLogic.getPoleMountPositionModePos(sunSail, poleIndex, axleIndex)
      case "bottom": {
        const relFirst = SunSailLogic.calcMountSlope(sunSail, poleIndex, axleIndex === 0 ? 0 : SunSailLogic.secondAxleOffset);
        const poleTop = Vec3.applyMatrix4(relFirst, SunSailLogic.calcPoleGroundMatrix(sunSail, poleIndex));
        return poleTop;
      }
      default: throw new Error("LogicS.calcMountPosition: PositionMode not supported so far: " + positionMode)
    }
  }

  static secondAxleOffset = 0.18;

  static calcMountPositionForAxlePole = CacheUtil.memo(SunSailLogic._calcAxleMountPosition, "calcAxleMountPosition", {
    idsFromArgs: (sunSail, poleIndex, axleIndex) => [sunSail.id, poleIndex, axleIndex],
    resultEqualFn: "shallow"
  })

  static calcAxlePositions(sunSail: ISunSailData, axleIndex: number, fn: (sunSail: ISunSailData, poleIndex: PoleIndex) => number): IAxlePos<IVec3> | undefined {
    const mountPositions = SunSailLogic.calcMountPositionsForAxlePoles(sunSail, axleIndex);
    if (!mountPositions)
      return undefined;

    const ac = Vec3.dir(mountPositions.first, mountPositions.second);

    const dists = SunSailLogic.calcAxleDists(sunSail, fn);
    return {
      first: Vec3.addScaled(mountPositions.first, ac, dists.first),
      second: Vec3.addScaled(mountPositions.second, ac, -dists.second)
    }
  }

  // *** pole helper ****
  static calcGroundRotation(sunSail: ISunSailData, poleId: PoleId): number {
    const { sunSailType, id: sunSailId } = sunSail;
    if (SST.is(sunSailType, SST.AMCX, SST.One, SST.Flex))
      return LogicAMCX4.calcGroundRotation(sunSailId, poleId, sunSail);
    else if (SST.is(sunSailType, SST.CsXsTwin))
      return LogicS.calcGroundRotation(sunSailId, poleId, sunSail)
    else if (sunSail.sunSailType === SunSailType.Standard) {
      const ret = StandardLogic.calculate(sunSail);
      return ret.groundRotations[poleId];
    } else if (SST.is(sunSailType, SST.Sandy)) {
      return LogicFixed.calcGroundRotation(sunSailId, poleId, sunSail);
    }
    return LogicFixed.calcGroundRotation(sunSailId, poleId, sunSail);
  }

  static _calcPoleGroundMatrix(sunSail: ISunSailData, poleId: PoleId): IMat4 {
    return SunSailLogic.calcPoleGroundMatrixFromValues(SunSailLogic.getPolePos(sunSail, poleId), SunSailLogic.calcGroundRotation(sunSail, poleId));
  }

  static calcPoleGroundMatrix = CacheUtil.memo(SunSailLogic._calcPoleGroundMatrix, "calcPoleGroundMatrix", {
    idsFromArgs: (sunSail, poleIndex) => [sunSail.id, poleIndex],
    resultEqualFn: "shallow"
  })

  static calcPoleGroundMatrixFromValues(position: IVec3, rotationY: number): IMat4 {
    return Mat4.mul(Mat4.createTranslation(position.x, position.y, position.z), Mat4.createRotationY(rotationY));
  }

  static calcAxlePosFromMountXY(mountFrom: IVec3, mountTo: IVec3, dist: IVec2): IVec3 {
    const mountDir = Vec3.dir(mountFrom, mountTo);
    const p = Vec3.addScaled(mountFrom, mountDir, dist.x);
    if (dist.y) {
      const mountDirUp = Vec3.cross(mountDir, Vec3.from(mountDir.z, 0, -mountDir.x));
      return Vec3.addScaled(p, mountDirUp, dist.y);
    }
    return p;
  }

  static calcDeltaForLengthPos(sunSail: ISunSailData, poleIndex: number, lengthPos: number, bottomExtension: number, offsetY: number, options?: IPoleLengthPosOptions): IVec3 {
    const poleLengthB = SunSailLogic.getPoleLengthB(sunSail, poleIndex);
    const poleAngle = SunSailLogic.getPoleAngle(sunSail, poleIndex);
    const poleLength = SunSailLogic.getPoleLength(sunSail, poleIndex);
    const m = PoleHelper.calcMatrixForLengthPos(sunSail.id, poleIndex, poleLength, poleAngle, poleLengthB, bottomExtension, offsetY, lengthPos, options);
    return Vec3.applyMatrix4(Vec3.empty, m);
  }

  static calcAxleNetFromMountPositions(sunSail: ISunSailData, clothIndex: number, mountFrom: IVec3, mountTo: IVec3): IAxlePos<IVec3> {
    const sunSailType = sunSail.sunSailType;
    const poleCount = Poles.getCount(sunSail);

    const indices = PoleLogic.getAxleIndices(sunSailType)!;

    let dy = 0;
    if (clothIndex !== -1) { // axle height
      const topology = SunSailTopology.calcTopology(sunSailType, poleCount);
      let isTop = true;
      if (topology.groups.length > 1) {
        // axle height
        const i1 = topology.groups[0].nonAxleIndices[0];
        const i2 = topology.groups[1].nonAxleIndices[0];
        const left = SunSailLogic.calcDeltaForLengthPos(sunSail, i1, SunSailLogic.getPoleLength(sunSail, i1) * 1.0, SunSailLogic.calcBottomPoleExtension(sunSail, i1), SunSailLogic.calcPoleOffsetY(sunSail, i1));
        const right = SunSailLogic.calcDeltaForLengthPos(sunSail, i2, SunSailLogic.getPoleLength(sunSail, i2) * 1.0, SunSailLogic.calcBottomPoleExtension(sunSail, i1), SunSailLogic.calcPoleOffsetY(sunSail, i1));

        const leftTop = left.y >= right.y;
        isTop = [SunSailType.SolidayTwin, SunSailType.SolidayFlex].includes(sunSailType) || (clothIndex === 0 && leftTop) || (clothIndex === 1 && !leftTop);
      }
      dy = SunSailLogic.calcAxleDeltaY(sunSail, isTop)
    }

    const fromDist = Poles.getPole(sunSail, indices.first).dist;
    Debug.assert(fromDist.type === "in", "Invalid type");
    const toDist = Poles.getPole(sunSail, indices.second).dist;
    Debug.assert(toDist.type === "in", "Invalid type");

    const fromDistIn = SunSailLogic.calcDistanceInForClothIndex(sunSail, indices.first, { clothIndex })
    const doDistIn = SunSailLogic.calcDistanceInForClothIndex(sunSail, indices.second, { clothIndex })

    // move to axleStart
    const fromDX = fromDistIn + SunSailLogic.getMountToNettoAxleDist(sunSail, indices.first);
    const from = SunSailLogic.calcAxlePosFromMountXY(mountFrom, mountTo, { x: fromDX, y: dy });
    const fromDY = doDistIn + SunSailLogic.getMountToNettoAxleDist(sunSail, indices.second)
    const to = SunSailLogic.calcAxlePosFromMountXY(mountTo, mountFrom, { x: fromDY, y: dy })

    if (Vec3.isNaN(from)) {
      throw new Error("NAN calcAxleNetFromMountPositions")
    }

    return {
      first: from,
      second: to
    }
  }

  // **** net helpers ****
  static calcAxleNetPositions(sunSail: ISunSailData, clothIndex: number): IAxlePos<IVec3> | undefined {
    const { sunSailType } = sunSail;
    const mount = SunSailLogic.calcMountPositionsForAxlePoles(sunSail, SunSailLogic.getAxleIndex(sunSailType, clothIndex));
    if (!mount)
      return undefined;

    return SunSailLogic.calcAxleNetFromMountPositions(sunSail, clothIndex, mount.first, mount.second);
  }

  static #getMountClampOffset(sunSail: ISunSailData | ISCSunSailData, poleIndex: PoleIndex): IVec3 {
    return ClampLogic.calcClampMountOffset(SunSailLogic.getPrimaryMountArticle(sunSail, poleIndex).articleNumber)
  }
  static #getMountBoltOffset(): IVec3 {
    return Vec3.from(0, 0, -0.07);
  }
  static calcMountPosDelta(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): IVec3 {
    const poleType = SunSailLogic.getPoleType(sunSail, poleIndex)
    const poleMaterial = SunSailLogic.getPoleMaterial(sunSail, poleIndex);

    const sunSailType = PD.productToSunSailType(sunSail)

    if (PoleLogic.isAxle(sunSailType, poleIndex)) {
      const axleMountType = SunSailLogic.calcPrimaryMountType(sunSail, poleIndex).mountType;
      switch (axleMountType) {
        case "direct":
          return Vec3.from(0, 0, 0);
        case "wallShackle":
          return Vec3.from(0, 0, -0.025);
        case "threaded":
          return Vec3.from(0, 0, -0.145);
        case "clamp": return SunSailLogic.#getMountClampOffset(sunSail, poleIndex);
        case "bolt": return SunSailLogic.#getMountBoltOffset();
        case "wall": return Vec3.ZERO;
        case "ringAdapter":
          return Vec3.from(0, 0, -0.06);
        case "heightChange": return SunSailLogic.calcHeightChangeRopeOffset(sunSail, poleIndex);
      }
      if (poleType === PoleType.Wall) {
        return SunSailLogic.#getMountBoltOffset();
      } else if (poleType === PoleType.Pole) {
        return SunSailLogic.#getMountClampOffset(sunSail, poleIndex);
      }
    } else {
      const mountType = SunSailLogic.calcPrimaryMountType(sunSail, poleIndex).mountType;
      switch (mountType) {
        case "pylon": {
          if (PoleLogic.isSteel(poleMaterial))
            return Vec3.from(-0.015, 0.01, -0.035); // pylon mount
          else if (PoleLogic.isAlu(poleMaterial))
            return Vec3.from(-0.015, 0.01, -0.039); // pylon mount
          else if (PoleLogic.isDroppole(poleMaterial))
            return Vec3.from(0.0, 0, -0.06);
          return Vec3.ZERO;
        }
        case "clamp": return SunSailLogic.#getMountClampOffset(sunSail, poleIndex);
        case "bolt": return SunSailLogic.#getMountBoltOffset();
        case "heightChange": return SunSailLogic.calcHeightChangeRopeOffset(sunSail, poleIndex);
        case "wall": return Vec3.ZERO;
        case "profile":
          return Vec3.from(0, 0, -0.05);
        case "telescope": {
          const y = SunSailLogic.getPoleLength(sunSail, poleIndex) - SunSailLogic.calcTopMountY(sunSail, poleIndex);
          return Vec3.from(0, 0, y >= 1.8 ? -0.04 : -0.05);
        }
      }
    }
    return Vec3.ZERO;
  }

  static calcHeightChangeRopeOffset(sunSail: ISunSailData | ISCSunSailData, poleIndex: PoleIndex): IVec3 {
    const poleType = SunSailLogic.getPoleType(sunSail, poleIndex)
    const poleMaterial = SunSailLogic.getPoleMaterial(sunSail, poleIndex);
    const article = SunSailLogic.getHeightChangeArticle(sunSail, poleIndex);
    if (article) {
      const slideInfo = HeightChangeLogic.getSlideInfo(sunSail, poleIndex);
      if (slideInfo) {
        return Vec3.from(0.0, -slideInfo.rollOffsetY - HeightChangeSlideMeasures.mountCapHeight - HeightChangeSlideMeasures.schlittenSlideOffset, -(PoleLogic.getPoleDiameterMeter(poleMaterial, poleType) * 0.5 + slideInfo.rollOffsetX));
      }
      const sandyInfo = HeightChangeLogic.getSandyInfoForArticle(article);
      if (sandyInfo) {
        return Vec3.from(0, 0, sandyInfo.offsetX);
      }
    }
    return Vec3.from(0.0, 0.0, -0.09);
  }

  static calcMountSlope(sunSail: ISunSailData | ISCSunSailData, poleIndex: PoleIndex, offsetY: number = 0): IVec3 {
    const poleSegments = SunSailLogic.calcPoleSegments(sunSail, poleIndex);
    const mountOffset = SunSailLogic.calcMountPosDelta(sunSail, poleIndex);
    const mountMatrix = PoleHelper.calcMatrixForLengthPosFromSegments(poleSegments, -mountOffset.y + offsetY, SunSailLogic.calcPoleLengthOptions(sunSail, poleIndex, "mount"));

    const clampAngle = SunSailLogic.calcClampAngle(sunSail, poleIndex);
    const rotatedMountMatrix = Mat4.mul(mountMatrix, Mat4.createRotationY(clampAngle));
    const relFirst = Vec3.applyMatrix4(Vec3.xz(mountOffset), rotatedMountMatrix)
    return relFirst;
  }

  static _calcMountPositions(sunSail: ISunSailData, axleIndex: number, options: INetPositionOptions | undefined): IVec3[] {

    if (SST.is(sunSail.sunSailType, SST.CsXsTwin))
      return MathUtil.checkNaN(LogicS.calcMountPositions(sunSail, axleIndex, options), "_calcMountPositions");
    else if (SST.is(sunSail.sunSailType, SST.AMCX, SST.One, SST.Flex))
      return MathUtil.checkNaN(LogicAMCX4.calcMountPositions(sunSail, options), "_calcMountPositions");
    else if (sunSail.sunSailType === SunSailType.Standard) {
      const ret = StandardLogic.calculate(sunSail);
      return MathUtil.checkNaN(ret.mountPositions, "_calcMountPositions");
    }
    return MathUtil.checkNaN(LogicFixed.calcMountPositions(sunSail, axleIndex, options), "_calcMountPositions");
  }

  static calcMountPositions = CacheUtil.memo(SunSailLogic._calcMountPositions, "calcMountPositions", {
    idsFromArgs: (sunSail, axleIndex) => [sunSail.id, axleIndex],
    resultEqualFn: "level2"
  })

  static calcSailMountPositions(sunSail: ISunSailData, axleIndex: number): IVec3[] {
    const mountPositions = SunSailLogic.calcMountPositions(sunSail, axleIndex, undefined);
    return SunSailLogic.calcSailMountPositionsFromMountPositions(sunSail, mountPositions);
  }

  static calcSailMountPositionsFromMountPositions(sunSail: ISunSailData, mountPositions: IVec3[]): IVec3[] {
    const axleIndices = PoleLogic.getAxleIndices(sunSail.sunSailType);
    const axleDir = axleIndices ? Vec3.dir(mountPositions[axleIndices.first], mountPositions[axleIndices.second]) : undefined;

    const sailMountPositions: IVec3[] = [];
    for (let i = 0; i < mountPositions.length; i++) {
      if (PoleLogic.isAxle(sunSail.sunSailType, i)) {
        const mountToAxle = SunSailLogic.getMountToNettoAxleDist(sunSail, i);
        if (mountToAxle > 0 && axleDir) {
          const p = Vec3.addScaled(mountPositions[i], axleDir, mountToAxle * (axleIndices?.first === i ? 1 : -1))
          sailMountPositions.push(p);
        } else
          sailMountPositions.push(mountPositions[i]);
      } else {
        sailMountPositions.push(mountPositions[i]);
      }
    }
    return sailMountPositions;
  }

  // static calcDistIn(sunSail: ISunSailData, poleIndex: number, clothIndex: number | undefined, dist: IDistInOut): number {
  //   if (dist.type === "in") {
  //     return PoleHelper.getDistInVal(dist.val, clothIndex);
  //   } else {
  //     const total = SunSailLogic.calcTotalMaxDistInOut(sunSail, poleIndex);
  //     const distanceIn = SunSailLogic.calcDistanceInFromOut(dist.val, total)
  //     return distanceIn;
  //   }
  // }

  static calcNetPositionsFromMountPositions(sunSail: ISunSailData, clothIndex: number, mount: IVec3[]): IVec3[] {
    const { sunSailType } = sunSail;
    const poleCount = Poles.getCount(sunSail);
    const indices = PoleLogic.getAxleIndices(sunSailType)!;

    const net: IVec3[] = [];
    if (indices) { // first calculate axle positions
      const axleFromTo = SunSailLogic.calcAxleNetFromMountPositions(sunSail, clothIndex, mount[indices.first], mount[indices.second]);
      net[indices.first] = axleFromTo.first;
      net[indices.second] = axleFromTo.second;
    }

    // then calculate all other positions
    for (let i = 0; i < poleCount; i++) {
      if (PoleLogic.isAxle(sunSailType, i)) {
        // ...already handled
      } else {
        if (SunSailLogic.calcDistanceIn(sunSail, i, { index: 0 }) !== SunSailLogic.calcDistanceIn(sunSail, i, { index: 1 }))
          throw new Error("There should be no difference in DistIn!");
        const distIn = SunSailLogic.calcDistanceIn(sunSail, i); // always the first... ?
        net[i] = SunSailLogic.calcNetPos(sunSail, i, distIn, mount);
      }
    }
    return net;
  }

  static calcNetPositionGroups(sunSail: ISunSailData, options?: INetPositionOptions): IVec3[][] {
    return SunSailLogic._calcNetPositionGroups(sunSail, options);

  }
  static _calcNetPositionGroups = CacheUtil.memo(SunSailLogic.#calcNetPositionGroups, "calcNetPositions", {
    idsFromArgs: (sunSail) => [sunSail.id],
    resultEqualFn: "level3"
  })
  static #calcNetPositionGroups(sunSail: ISunSailData, options: INetPositionOptions | undefined): IVec3[][] {
    if (sunSail.positionMode === "net") {
      throw new Error("calcNetPositionGroups should not be called for net positions!")
      // SandyLogic.checkPositionMode(sunSail);
      // const sunSailType = PD.productToSunSailType(sunSail)
      // const poleCount = Poles.getCount(sunSail);
      // const netPositionGroups: IVec3[][] = [];
      // const topology = SunSailTopology.calcTopology(sunSailType, poleCount);
      // for (let i = 0; i < topology.groups.length; i++) {
      //   netPositionGroups[i] = sunSail.poles.map((p) => p.position);
      // }
      // return netPositionGroups;
    } else {
      const sunSailType = PD.productToSunSailType(sunSail)
      const poleCount = Poles.getCount(sunSail);

      if (SST.is(sunSail.sunSailType, SST.CsXsTwin) && poleCount > 3) {
        return LogicS.calcNetPositionGroups(sunSail, options);
      } else if (sunSailType === SunSailType.Standard) {
        const ret = StandardLogic.calculate(sunSail);
        return ret.netPositionGroups;
      } else {
        const mount = SunSailLogic.calcMountPositions(sunSail, 0, options);
        const netPositionGroups: IVec3[][] = [];
        const topology = SunSailTopology.calcTopology(sunSailType, poleCount);
        for (let i = 0; i < topology.groups.length; i++) {
          netPositionGroups[i] = SunSailLogic.calcNetPositionsFromMountPositions(sunSail, i, mount);
        }
        return netPositionGroups;
      }
    }
  }

  static calcNetPositionsForGroup(sunSail: ISunSailData, groupIndex: number, options: INetPositionOptions | undefined): IVec3[] {
    const { sunSailType } = sunSail;

    if (sunSail.positionMode === "net") {
      throw new Error("calcNetPositionsForGroup should not be called for net positions!")
      // SandyLogic.checkPositionMode(sunSail);
      // return sunSail.poles.map((p) => p.position);
    } {
      const poleCount = Poles.getCount(sunSail)

      if (SST.is(sunSailType, SST.CsXsTwin) && poleCount > 3) {
        return LogicS.calcNetPositionsForGroup(sunSail, groupIndex, options);
      } else if (sunSail.sunSailType === SunSailType.Standard) {
        const ret = StandardLogic.calculate(sunSail);
        return ret.netPositionGroups[groupIndex];
      } else {
        const mount = SunSailLogic.calcMountPositions(sunSail, 0, options);
        return SunSailLogic.calcNetPositionsFromMountPositions(sunSail, groupIndex, mount);
      }
    }
  }

  static calcNetPositions(sunSail: ISunSailData, clothIndex: number, options?: INetPositionOptions): IVec3[] {
    const netGroups = SunSailLogic.calcNetPositionGroups(sunSail, options);
    return netGroups[clothIndex];
  }

  static #calcSunSailLogicalCenterForPositions(sunSailType: SunSailType, polePositions: IVec3[]): IVec3 {
    const axleIndices = PoleLogic.getAxleIndices(sunSailType);
    if (axleIndices) {
      return Vec3.mid(polePositions[axleIndices.first], polePositions[axleIndices.second]);
    } else {
      return Vec3.center(polePositions);
    }
  }

  static calcSunSailLogicalCenter(sunSail: ISunSailData): IVec3 {
    if (sunSail.specific?.type === "standard")
      return sunSail.specific?.center || SunSailLogic.#calcSunSailLogicalCenterForPositions(sunSail.sunSailType, SunSailLogic.calcPolePositions(sunSail));
    return SunSailLogic.#calcSunSailLogicalCenterForPositions(sunSail.sunSailType, SunSailLogic.calcPolePositions(sunSail));
  }

  static calcSunSailRotationYFromPoints(points: IVec3[]): number {
    const axleDir = Vec2.dir(Vec2.fromXZ(points[0]), Vec2.fromXZ(points[2]));
    const rotation = Math.atan2(-axleDir.y, axleDir.x);
    return rotation;
  }

  static calcSunSailRotationY(sunSail: ISunSailData): number {
    if (sunSail.specific?.type === "standard")
      return sunSail.specific?.rotationY || 0;
    return SunSailLogic.calcSunSailRotationYFromPoints(SunSailLogic.calcPolePositions(sunSail));
  }

  // static calcPolePositions = CacheUtil.memo(SunSailLogic.#calcPolePositions, "SunSailLogic.calcPolePositions", {
  //   idsFromArgs: (sunSail) => [sunSail.id],
  //   resultEqualFn: "level3",
  // })

  static calcPolePositions(sunSail: ISunSailData): IVec3[] {
    return sunSail.poles.map((_p, i) => SunSailLogic.getPolePos(sunSail, i))
  }

  // **** other helpers ****
  static fixSunSailData(old: ISunSailData): ISunSailData {
    if (old.articleSlots === undefined) {
      old = {
        ...old,
        articleSlots: {}
      }
    }

    old = SunSailReducers.setProductSlot(old, "revolvingRope", SunSailLogic.hasRevolvingRopeSST(old.sunSailType) ? SunSailLogic.getRevolvingRope(old) : undefined)

    // fix axle poles
    for (let i = 0; i < Poles.getCount(old); i++) {
      if (PoleLogic.isAxle(old.sunSailType, i))
        old = SunSailLogic.fixPoleData(old, i);
    }
    // fix other poles
    for (let i = 0; i < Poles.getCount(old); i++) {
      if (!PoleLogic.isAxle(old.sunSailType, i))
        old = SunSailLogic.fixPoleData(old, i);
    }



    return old;
  }

  static getDefaultPylonType(): PylonType {
    return "ZT1000";
  }

  static getPylonDornLength(poleMaterial: MaterialType, poleMount: PoleMountType): number {
    if (PoleLogic.isDroppole(poleMaterial)) {
      switch (poleMount) {
        case PoleMountType.BaseCube:
        case PoleMountType.ConcreteCube:
          return 0.4;
        case PoleMountType.GroundPlate:
          return 0.5;
        case PoleMountType.EarthScrew:
          return 0.4;
      }
      return 0.0;
    }
    return 0.0;
  }

  static getPylonInfo(pylonType: PylonType): {
    maxDistOut: number;
    length: number;
    defaultPylonRopeLength: number;
  } {
    switch (pylonType) {
      case "ZT1000": return { maxDistOut: 7.1, length: 2.5, defaultPylonRopeLength: 21 }
      case "ZT700": return { maxDistOut: 4.8, length: 1.8, defaultPylonRopeLength: 16 }
      case "ZT500": return { maxDistOut: 3.4, length: 1.5, defaultPylonRopeLength: 13 }
    }
    throw new Error("Pylon not supported so far!");
  }

  static fixPoleData(old: ISunSailData, poleId: PoleId): ISunSailData {
    const { sunSailType } = old;
    const { poleIndex, polePositionType } = Poles.fromPoleId(poleId);
    const polesProperty = Poles.getPolePositionProperty(polePositionType);
    {
      let pole = Poles.getPole(old, poleId);
      const pylonType = SunSailLogic.getPolePylonType(old, poleId);
      const poleType = SunSailLogic.getPoleType(old, poleId)
      if (!pylonType && PoleLogic.isPylonRequired(sunSailType, poleId, poleType)) {
        pole = ImmHelper.setProperty(pole, "pylonType", SunSailLogic.getDefaultPylonType());
      } else if (pylonType && !PoleLogic.isPylonValid(sunSailType, poleId, poleType)) {
        pole = ImmHelper.setProperty(pole, "pylonType", undefined);
      }
      old = ImmHelper.setPropertyNonNullable(old, polesProperty, poleIndex, pole);
    }

    {
      const poleMaterial = SunSailLogic.getPoleMaterial(old, poleId)
      const poleType = SunSailLogic.getPoleType(old, poleId)
      const poleMount = SunSailLogic.getPoleMount(old, poleId);
      const canbeBent = PoleLogic.canBeBent(poleType, poleMaterial, poleMount)
      if (SunSailLogic.getPoleLengthBEnabled(old, poleId) && !canbeBent) {
        old = SunSailReducers.setLengthBEnabled(old, poleId, canbeBent, { direct: true });
      }
    }

    {
      let pole = Poles.getPole(old, poleId);
      pole = ImmHelper.update(pole, {
        articleSlots: pole.articleSlots || {},
      })
      old = ImmHelper.setPropertyNonNullable(old, polesProperty, poleIndex, pole);
    }

    {
      const pole = Poles.getPole(old, poleId);
      const article = SunSailLogic.getWallPlateArticle(old, poleId);
      old = ImmHelper.setPropertyNonNullable(old, polesProperty, poleIndex, PoleReducers.setArticleSlot(pole, "wallMount", article));
    }

    {
      const pole = Poles.getPole(old, poleId);
      old = ImmHelper.setPropertyNonNullable(old, polesProperty, poleIndex, pole);
    }

    // if (old.sunSailType === SunSailType.SolidayC && Poles.getPoleCount(old).corner === 4) {
    //   newVal = SunSailReducers.equalizeDistOut(newVal, poleIndex);
    // }
    // if (initialValue !== old)
    //  console.log("Fix Pole", poleIndex);



    // slots: {
    //   wallMount?: ISlot;
    //   poleMountClamp?: ISlot;
    //   primaryMount?: ISlot;
    //   coilUp?: ISlot;
    //   heightChange?: ISlot;
    //   corner?: ISlot<CornerType>
    //   earthScrew?: ISlot;
    // }

    return old;
  }

  static normalizeArticle(articleId: string | undefined): string | undefined {
    return articleId ? articleId : undefined;
  }

  // static fixDist(old: ISunSailData, poleIndex: number): IDistInOut {
  //   const oldDist = old.poles[poleIndex].dist;
  //   if (!oldDist)
  //     return oldDist;

  //   if (SunSailLogic.isTargetDist(old.sunSailType, poleIndex, old.alignDistOut))
  //     return oldDist; // no correction for targetDists...

  //   const { minDistIn, maxDistIn, maxAllowedDistOut, minDistOut } = SunSailLogic.calcMaxDistInOut(old, poleIndex);
  //   if (oldDist.type === "in") {
  //     const oldVal = oldDist.val;
  //     if (Array.isArray(oldVal)) {
  //       let newVal = oldVal;
  //       for (let i = 0; i < oldVal.length; i++) {
  //         newVal = ImmHelper.setArrayItem(newVal, i, MathUtil.clamp(oldVal[i], minDistIn, maxDistIn))
  //       }
  //       return ImmHelper.setProperty(oldDist, "val", newVal)
  //     } else {
  //       return ImmHelper.setProperty(oldDist, "val", MathUtil.clamp(oldDist.val as number, minDistIn, maxDistIn))
  //     }
  //   } else {
  //     return ImmHelper.setProperty(oldDist, "val", MathUtil.clamp(oldDist.val, minDistOut, maxAllowedDistOut))
  //   }
  // }

  static isTwistingSet(sunSailType: SunSailType, poleCount: number): "required" | "editable" | undefined {
    if (SST.is(sunSailType, SST.One))
      return "required";
    else if (SST.is(sunSailType, SST.AMX) && poleCount === 4)
      return "editable";
    return undefined;
  }

  static isTwistingSetRequired(sunSail: ISunSailData): boolean {
    const ts = SunSailLogic.isTwistingSet(sunSail.sunSailType, Poles.getCount(sunSail));
    if (ts === "required")
      return true;
    else if (ts === "editable") {
      const mountPositions = SunSailLogic.calcMountPositions(sunSail, 0, undefined);
      const twistingSetParams = SunSailLogic.getTwistingSetParamsFromMountPositions(sunSail, mountPositions)
      if (twistingSetParams.heightChangeCount > 0)
        return true;
      if (twistingSetParams.deltaZ > 20)
        return true;
      if (twistingSetParams.deltaH > 10)
        return true;
    }
    return false;
  }

  static getTwistingSetParamsFromMountPositions(sunSail: ISunSailData, mountPositions: IVec3[]): { sunSailType: SunSailType, poleCount: number; heightChangeCount: number; deltaZ: number; deltaH: number; } {
    return {
      sunSailType: sunSail.sunSailType,
      poleCount: Poles.getCount(sunSail),
      heightChangeCount: SunSailLogic.calcHeightChangeCount(sunSail),
      deltaZ: SunSailLogic.calcFactorZ(sunSail),
      deltaH: SunSailLogic.calcDeltaH(mountPositions)
    }
  }

  static hasTwistingSetForSunSail(sunSail: ISunSailData | ISCSunSailData): boolean {
    const sunSailType = PD.productToSunSailType(sunSail)
    return SunSailLogic.hasTwistingSet(sunSailType, Poles.getCount(sunSail), sunSail.twistingSet)
  }

  static hasTwistingSet(sunSailType: SunSailType, poleCount: number, twistingSet: boolean | undefined): boolean {
    const ts = SunSailLogic.isTwistingSet(sunSailType, poleCount);
    if (ts === "required")
      return true;
    else if (ts === "editable") {
      return !!twistingSet;
    }
    return false;
  }

  static hasTwistingSetOnPole(twistingSet: boolean | undefined, sunSailType: SunSailType, poleIndex: number): boolean {
    return !!twistingSet && PoleLogic.isAxle(sunSailType, poleIndex);
  }

  static getTwistingSetPoleExtension(sunSailType: SunSailType, poleIndex: PoleIndex): number {
    switch (sunSailType) {
      case SunSailType.SolidayA:
      case SunSailType.SolidayM:
      case SunSailType.SolidayFlex: // todo: Soliday-Flex
      case SunSailType.SolidayX: {
        if (poleIndex === PoleIndex.A || poleIndex === PoleIndex.C)
          return 0.14; // old 0.16
        return 0;
      }
      case SunSailType.SolidayOne:
        if (poleIndex === PoleIndex.A || poleIndex === PoleIndex.C)
          return 0.25;
        return 0;
      default: {
        // old
        // if (poleIndex === PoleIndex.A)
        //   return 0.14;
        // else if (poleIndex === PoleIndex.C)
        //   return 0.1;
        return 0;
      }
    }
  }

  static calcTwistingSetMatrix(sunSail: ISunSailData, poleIndex: PoleIndex): IMat4 {
    const poleType = SunSailLogic.getPoleType(sunSail, poleIndex)
    const offset = poleType === PoleType.Pole ? SunSailLogic.getTwistingSetPoleExtension(sunSail.sunSailType, poleIndex) : 0;
    const topMountY = SunSailLogic.calcTopMountY(sunSail, poleIndex);
    return SunSailLogic.calcMatrixForPos(sunSail, poleIndex, topMountY - offset, "top")
  }

  static getTwistingSetMountArticle(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): IMountArticle | undefined {
    const pole = Poles.getPole(sunSail, poleIndex);
    const articleId = pole.articleSlots?.twistingSetMount;
    const validArticles = SunSailLogic.calcPossibleTwistingSetArticles(sunSail, poleIndex);

    return ArticleChooser.findValidArticleObject(articleId, validArticles);
  }

  static calcWallPlatesRequiredInternal(sunSailType: SunSailType, poleIndex: PoleIndex, hasHeightChange: boolean | undefined, hasSlideBar: boolean): number {
    if (hasHeightChange && hasSlideBar)
      return 0;
    return AxleLogic.getAxleCountForPole(sunSailType, poleIndex) || 1;
  }

  static calcWallPlatesRequired(sunSailType: SunSailType, poleIndex: number, heightChange: boolean | undefined, hasSlideBar: boolean): number {
    const plateCount = SunSailLogic.calcWallPlatesRequiredInternal(sunSailType, poleIndex, heightChange, hasSlideBar);
    return plateCount;
  }

  static calcPoleNiveauToMountHeight(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): number {
    const poleLength = SunSailLogic.getPoleLength(sunSail, poleIndex);
    return poleLength - SunSailLogic.calcBottomPoleExtension(sunSail, poleIndex) - SunSailLogic.calcTopPoleExtension(sunSail, poleIndex);
  }

  static calcTopPoleExtension(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleIndex: number): number {

    const sunSailType = PD.productToSunSailType(sunSail)

    const poleType = SunSailLogic.getPoleType(sunSail, poleIndex)

    if (poleType !== PoleType.Pole)
      return 0;

    let topAdd = IndirectPylonLogic.calcTopPoleExtension(sunSail, poleIndex);

    if (SunSailLogic.hasTwistingSetOnPole(!!sunSail.twistingSet, sunSailType, poleIndex))
      topAdd = Math.max(topAdd, SunSailLogic.getTwistingSetPoleExtension(sunSailType, poleIndex))
    return topAdd;
  }

  static calcBottomPoleExtension(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): number {
    const poleType = SunSailLogic.getPoleType(sunSail, poleId)
    const poleMaterial = SunSailLogic.getPoleMaterial(sunSail, poleId);
    const poleMount = SunSailLogic.getPoleMount(sunSail, poleId);

    if (poleType !== PoleType.Pole)
      return 0;

    let bottomAdd = 0;
    if (poleMount === PoleMountType.GroundSleeve) {
      if (!PoleLogic.isDroppole(poleMaterial)) {
        bottomAdd = Math.max(bottomAdd, 0.3);
      }
    } else if (poleMount === PoleMountType.EarthScrew) {
      const articleId = SunSailLogic.getEarthScrewArticle(sunSail, poleId);

      if (!PoleLogic.isDroppole(poleMaterial)) {
        switch (articleId) {
          case "00E13000228": { //1600
            bottomAdd = Math.max(bottomAdd, 0.78); // was 0.8
            break;
          }
          case "00E13000227": { //1400
            bottomAdd = Math.max(bottomAdd, 0.37); // was 0.365/0.3
            break;
          }
          case "00E13000756": { // 990
            bottomAdd = Math.max(bottomAdd, 0.35); // https://plaspack.atlassian.net/browse/SS-45
            break;
          }
          default: {
            throw new Error("Earthscrew not supported so far: " + articleId);
          }
        }
      }
    } else if (poleMount === PoleMountType.ConcreteCube) {
      const poleAngle = SunSailLogic.getPoleAngle(sunSail, poleId);
      const l = SunSailLogic.heightToPoleLength(0.45, poleAngle, SunSailLogic.isPoleBent(sunSail, poleId));
      if (SunSailLogic.hasBaseCubeDorn(sunSail, poleId)) {
        bottomAdd = -l;
      } else {
        bottomAdd = -l + 0.3;
      }
    } else if (poleMount === PoleMountType.BaseCube) {
      const poleAngle = SunSailLogic.getPoleAngle(sunSail, poleId);
      const l = SunSailLogic.heightToPoleLength(0.5, poleAngle, SunSailLogic.isPoleBent(sunSail, poleId));
      if (SunSailLogic.hasBaseCubeDorn(sunSail, poleId)) {
        // bottomAdd = -l;
        // bottomAdd = 0; // calcPoleOffsetY
        bottomAdd = 0;
      } else {
        bottomAdd = l;
      }
    }

    return bottomAdd;
  }


  static heightToPoleLength(heightY: number, angle: number, isBent: boolean): number {
    if (isBent)
      return heightY;
    return heightY / Math.cos(angle)
  }

  static calcPoleOffsetYCorrected(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleIndex: number): number {
    const poleOffsetY = SunSailLogic.calcPoleOffsetY(sunSail, poleIndex);
    return SunSailLogic.heightToPoleLength(poleOffsetY, SunSailLogic.getPoleAngle(sunSail, poleIndex), SunSailLogic.isPoleBent(sunSail, poleIndex))
  }

  static calcPoleOffsetY(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleIndex: number): number {
    const poleType = SunSailLogic.getPoleType(sunSail, poleIndex)
    if (poleType !== PoleType.Pole)
      return 0;

    // const hasBaseCubeDorn = SunSailLogic.hasBaseCubeDorn(sunSail, poleIndex);
    const poleMount = SunSailLogic.getPoleMount(sunSail, poleIndex);
    // const poleAngle = SunSailLogic.getPoleAngle(sunSail, poleIndex);
    switch (poleMount) {
      case PoleMountType.BaseCube: {
        // if (hasBaseCubeDorn)
        //  return 0.51 - Math.tan(poleAngle) * 0.0625;
        //return 0;
        return 0.5;
      }
      // case PoleMountType.ConcreteCube: { // bottomPoleExtension
      //   if (hasBaseCubeDorn)
      //     return 0.45 - Math.tan(poleAngle) * 0.0625;
      //   return 0.0;
      // }
    }
    return 0;
  }

  static hasBaseCubeDorn(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): boolean {
    const poleType = SunSailLogic.getPoleType(sunSail, poleId)
    const poleMount = SunSailLogic.getPoleMount(sunSail, poleId);
    const poleMaterial = SunSailLogic.getPoleMaterial(sunSail, poleId);
    return poleType === PoleType.Pole && (poleMount === PoleMountType.BaseCube || poleMount === PoleMountType.ConcreteCube) && PoleLogic.isDroppole(poleMaterial);
  }

  static calcBaseCubeRotation(sunSail: ISunSailData, poleIndex: number): number {
    if (SST.is(sunSail.sunSailType, SST.CsXs) && Poles.getCount(sunSail) === 4) {
      // return [0.5, 0.0, 0.0, 0.5][poleIndex] * Math.PI;
      return [0.5, 0.0, 0.5, 0.0][poleIndex] * Math.PI;
    } else if (SST.is(sunSail.sunSailType, SST.Twin)) {
      if (LogicS.isQuadFrontPole(sunSail.sunSailType, Poles.getCount(sunSail), poleIndex)) {
        return ((poleIndex + (poleIndex >= 4 ? 1 : 0)) % 2) === 0 ? 0.5 * Math.PI : 0.0 * Math.PI;
      }
    }
    return 0.25 * Math.PI;
  }

  static calcPoleBasePos(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleIndex: number): number {
    const bottomPoleExtension = SunSailLogic.calcBottomPoleExtension(sunSail, poleIndex);
    const correctedPoleOffsetY = SunSailLogic.calcPoleOffsetYCorrected(sunSail, poleIndex);
    return bottomPoleExtension - correctedPoleOffsetY;
  }

  static calcPoleLengthOptions(sunSail: ISunSailData | ISCSunSailData, poleIndex: number, mode: "top" | "bottom" | "mount" | "niveau"): IPoleLengthPosOptions {
    const poleLength = SunSailLogic.getPoleLength(sunSail, poleIndex);
    switch (mode) {
      case "bottom":
        return {
          basePos: 0,
          up: true
        }
      case "niveau": {
        return {
          basePos: SunSailLogic.calcPoleBasePos(sunSail, poleIndex),
          up: true
        }
      }
      case "mount": {
        // const topPoleExtension = SunSailLogic.calcTopPoleExtension(sunSail, poleIndex);
        return {
          basePos: poleLength - SunSailLogic.calcTopMountY(sunSail, poleIndex),
          up: false,
        }
      }
      case "top": {
        return {
          basePos: poleLength,
          up: false
        }
      }
    }
  }

  static isDroppoleCut(length: number): boolean {
    const roundedLength = MathUtil.round(length, 2);
    const roundedLength2 = roundedLength % 1;
    const isCut = roundedLength2 > 0.001;
    // console.log("IsCut: ", isCut);
    return ltc(roundedLength, 3) || gtc(roundedLength, 5) || isCut;
  }

  static calcTwistingSetPoleCount(sunSail: ISunSailData): number {
    return (SunSailLogic.getPoleType(sunSail, 0) === PoleType.Pole ? 1 : 0) + (SunSailLogic.getPoleType(sunSail, 2) === PoleType.Pole ? 1 : 0);
  }

  static calcTwistingSetType(sunSail: ISunSailData): TwistingSetType {
    if (!SunSailLogic.hasTwistingSetForSunSail(sunSail))
      return TwistingSetType.None;
    const twistingSetPoleCount = SunSailLogic.calcTwistingSetPoleCount(sunSail);
    return [TwistingSetType.Wall, TwistingSetType.WallPole, TwistingSetType.Pole][twistingSetPoleCount];
  }

  static hasGreenSymmetricSides(sunSail: ISunSailData, poleIndex: PoleIndex): boolean {
    const { sunSailType } = sunSail;
    if (!SST.is(sunSailType, SST.CsTwin))
      return false;
    if (PoleLogic.isAxle(sunSailType, poleIndex))
      return false;
    if (!SunSailLogic.isPolyPositioningForPole(sunSail, poleIndex))
      return false;
    if (SunSailLogic.hasGreenCoilUpArea(sunSail, poleIndex))
      return false;
    return true;
  }

  static hasGreenCoilUpArea(sunSail: ISunSailData, poleIndex: PoleIndex): boolean {
    if (!SunSailLogic.isPolyPositioningForPole(sunSail, poleIndex))
      return false;
    if (PoleLogic.isAxle(sunSail.sunSailType, poleIndex))
      return false;
    const axlePoleIndex = (poleIndex === PoleIndex.C || poleIndex === PoleIndex.E) ? PoleIndex.B : PoleIndex.A;
    return !!SunSailLogic.getSailCoilUpArticle(sunSail, axlePoleIndex);
  }

  static isPolyPositioningForPole(sunSail: ISunSailData | ISCSunSailData, poleIndex: PoleIndex): boolean {
    const sunSailType = PD.productToSunSailType(sunSail)

    if (PoleLogic.isAxle(sunSailType, poleIndex))
      return false;

    const groupIndex = poleIndex >= PoleIndex.E ? 1 : 0;
    return SunSailLogic.isPolyPositioningForGroup(sunSail, groupIndex).enabled;
  }

  static hasCSMountPylons(sunSail: ISunSailData): boolean {
    const calculationType = sunSail.form?.calculationType || "diagonals";
    return calculationType === "diagonals";
  }

  static calcCS4PositionMode(sunSail: ISunSailData | ISCSunSailData): CSPositioning[] {
    const g1 = SunSailLogic.isPolyPositioningForGroup(sunSail, 0);
    const g2 = SunSailLogic.isPolyPositioningForGroup(sunSail, 1);
    const ret: CSPositioning[] = [];
    if (g1.possible)
      ret.push(g1.enabled ? "flexible" : "rigid")
    if (g2.possible)
      ret.push(g2.enabled ? "flexible" : "rigid")
    return ret;
  }

  static isPolyPositioningForGroup(sunSail: ISunSailData | ISCSunSailData, groupIndex: number): { possible: boolean; enabled: boolean; required: boolean } {
    const sunSailType = PD.productToSunSailType(sunSail)
    const poleCount = Poles.getCount(sunSail);

    return {
      possible: SunSailLogic.isPolyPositioningPossible(sunSailType, poleCount, groupIndex),
      required: SunSailLogic.isPolyPositioningRequired(sunSailType, poleCount),
      enabled: SunSailLogic.isPolyPositioningEnabled(sunSailType, poleCount, groupIndex, sunSail.polyPositioning),
    }
  }

  static isPolyPositioningPossible(sunSailType: SunSailType, poleCount: number, groupIndex: number): boolean {
    if (SST.is(sunSailType, SST.CsXsTwin) && poleCount >= 4) {
      if (groupIndex >= 1 && poleCount < 6)
        return false;
      return true;
    }
    return false;
  }

  static isPolyPositioningEnabled(sunSailType: SunSailType, poleCount: number, groupIndex: number, polyPositioning: boolean[] | boolean | undefined): boolean {
    if (!SunSailLogic.isPolyPositioningPossible(sunSailType, poleCount, groupIndex))
      return false;
    if (SST.is(sunSailType, SST.CsXsTwin) && poleCount >= 4) {
      if (groupIndex >= 1 && poleCount < 6)
        return false;
      return SunSailLogic.isPolyPositioningRequired(sunSailType, poleCount) || (Array.isArray(polyPositioning) ? polyPositioning?.[groupIndex] : !!polyPositioning);
    }
    return false;
  }

  static isPolyPositioningRequired(sunSailType: SunSailType, poleCount: number): boolean {
    if (SST.is(sunSailType, SST.Xs) && poleCount === 4)
      return true;
    return false;
  }

  private static calcCross(prev: IVec3, current: IVec3, next: IVec3): IVec3 {
    return Vec3.cross(Vec3.xz(Vec3.to(current, prev)), Vec3.xz(Vec3.to(current, next)));
  }

  static isConvex(points: IVec3[]): boolean {
    const poleCount = points.length;
    const crossVector = SunSailLogic.calcCross(points[poleCount - 1], points[0], points[1]);
    for (let i = 1; i < points.length; i++) {
      const cv = SunSailLogic.calcCross(points[i - 1], points[i], points[(i + 1) % poleCount]);
      const d = Vec3.dot(cv, crossVector);
      if (d < 0)
        return false;
    }
    return true;
  }

  static getKnotOuterDist(): number {
    return 0.12;
  }

  static getCSKnotY(sunSailType: SunSailType): 0.3 | 0.45 | number {
    switch (sunSailType) {
      case SunSailType.SolidayCS:
      case SunSailType.SolidayTwin:
        return 0.3;
      case SunSailType.SolidayXS:
        return 0.45;
    }
    return 0;
  }

  static getAxleName(sunSailType: SunSailType): SunSailSides {
    const axleIndices = PoleLogic.getAxleIndices(sunSailType);
    if (!axleIndices)
      throw new Error("SunSail has no axle!");
    return getSideName(axleIndices.first, axleIndices.second);
  }

  static isSlideBar(articleNumber: string | undefined): boolean {
    if (!articleNumber)
      return false;
    return !!HeightChangeLogic.getSlideNameForArticle(articleNumber);
  }

  static hasWallPlate1385(sunSailType: SunSailType, poleCount: number, poleIndex: number, coilUp: boolean | undefined, heightChange: boolean | undefined, hasSlideBar: boolean): boolean {
    if (hasSlideBar)
      return false;

    const isAxlePole = PoleLogic.isAxle(sunSailType, poleIndex);
    const has1385X = sunSailType === SST.X && !isAxlePole && coilUp && !heightChange;
    const has1385XS = sunSailType === SST.Xs && poleCount === 3 && !isAxlePole && coilUp && !heightChange;
    const has1385 = has1385X || has1385XS;
    return !!has1385;
  }

  static calcPossibleWallPlates(sunSailType: SunSailType, poleCount: number, poleId: PoleId, poleType: PoleType, coilUp: boolean | undefined, heightChange: boolean | undefined, slideBar: boolean, featureLevel: FeatureLevel): string[] {
    const wallPlates = [];
    if (poleType !== PoleType.Wall)
      return [];

    if (heightChange && slideBar)
      return [];

    if (sunSailType === SunSailType.SolidayOne && PoleLogic.isAxle(sunSailType, poleId))
      return ONE_AXLE_WALL_PLATES//  https://plaspack.atlassian.net/browse/API-39

    const plateCount = SunSailLogic.calcWallPlatesRequired(sunSailType, poleId, heightChange, slideBar);
    const withPolished = FL.ge(featureLevel, "dealer");
    if (plateCount > 0) {
      if (SunSailLogic.hasWallPlate1385(sunSailType, poleCount, poleId, coilUp, heightChange, slideBar)) {
        wallPlates.push("00E13001385"); // Standard-Wandplatte für X-Anlage mit Lasche f. 2 Anbindungen
      } else {
        if (SST.is(sunSailType, SST.CsXs) && PoleLogic.isAxle(sunSailType, poleId)) {
          wallPlates.push("00E13000046"); // Wandplatte f. CS/C 150/156/6 LH innen = 80mm
          if (withPolished) wallPlates.push("00E13001862"); // Wandplatte f. CS/C 150/156/6 LH innen = 80mm
        } else if (SST.is(sunSailType, SST.AMXOne) && poleCount === 3 && PoleLogic.isAxle(sunSailType, poleId)) {
          wallPlates.push("00E13001095"); // Wandplatte f. A&M 3-Eck 150/156/6 LH innen = 100
        }
        wallPlates.push("00E13000709"); // Standardwandplatte 150x150x6mm
        if (withPolished) wallPlates.push("00E13001855"); // Standardwandplatte 150x150x6mm hochglanz

        wallPlates.push("00E13000710"); // Wandhalterung 270x50x8mm waagerecht
        if (withPolished) wallPlates.push("00E13001856"); // Wandhalterung 270x50x8mm waagerecht hochglant

        wallPlates.push("00E13000711"); // Wandhalterung 50x270x8mm senkrecht
        if (withPolished) wallPlates.push("00E13001857"); // Wandhalterung 50x270x8mm senkrecht hochglant

        wallPlates.push("00E13000713"); // Eckwandhalterung 130x130x150x6mm für Inneneck
        if (withPolished) wallPlates.push("00E13001859"); // Eckwandhalterung 130x130x150x6mm für Inneneck hochglanz


        wallPlates.push("00E13000712"); // Eckwandhalterung 150x150x170x6mm für Außeneck
        if (withPolished) wallPlates.push("00E13001858"); // Eckwandhalterung 150x150x170x6mm für Außeneck hochglanz

        wallPlates.push("00E13000714"); // Eckwandhalterung 300x300x150x6mm Außeneck gross
        if (withPolished) wallPlates.push("00E13001861"); // Eckwandhalterung 300x300x150x6mm Außeneck gross hochglanz

        wallPlates.push("00E13000716"); // Wandhalterung rund D=180mm
        if (withPolished) wallPlates.push("00E13001888"); // Wandhalterung rund D=180mm hochglanz
      }

      if (SST.is(sunSailType, SST.CoSF)) {
        wallPlates.push("00E13000063"); // Ringschraube 8 mm für Holz/Wand
        wallPlates.push("00E13000066"); // Ringbolzen M80x80
        wallPlates.push("00E13000127"); // Ringbolzen M80x100
      }

      // wallPlates.push("00E13000118") ???

      wallPlates.push("00E13001910"); // Decken-Wandhalter 150x150mm Höhe 149mm
      if (withPolished) wallPlates.push("00E13001889"); // Decken-Wandhalter 150x150mm Höhe 149mm


      if (FL.ge(featureLevel, "dealer"))
        wallPlates.push("00E13001025"); // Sonderwandplatte Skizze erforderlich
    }

    return wallPlates;
  }

  static getClampArticlesForMaterial(material: MaterialType): string[] {
    return ClampLogic.calcClampArticleIds(material);
  }

  static getRingAdapterArticleForSystem(sunSailType: SunSailType): string {
    if (SST.is(sunSailType, SST.CsTwin))
      return "00E13001937";
    else
      return "00E13000206";
  }

  static isRingAdapterArticle(articleId: string): boolean {
    return ["00E13001937", "00E13000206"].includes(articleId)
  }

  static getCenterRingschraube(material: MaterialType | undefined): IRingschraube | undefined {
    if (PoleLogic.isAlu(material)) return Ringschrauben.M8x100_Double;
    else if (material === "Iron48") return Ringschrauben.M8x60_Double;
    throw new Error("getCenterRingschraube - not supported so far!")
  }

  static getRingschraube(material: MaterialType | undefined): IRingschraube | undefined {
    if (PoleLogic.isSteel(material)) return Ringschrauben.M8x80;
    else if (PoleLogic.isAlu(material)) return Ringschrauben.M8x100;
    else if (PoleLogic.isIron89(material)) return Ringschrauben.M10x110;
    else if (material === "Iron114") return Ringschrauben.M10x140;
    else if (material === "Iron48") return Ringschrauben.M8x60;
    return undefined;
  }

  static getRingSchraubenForMaterial(material: MaterialType): string[] {
    const rs = this.getRingschraube(material)
    if (rs)
      return [rs.articleNumber];
    return [];
  }

  static getOfferListForAxisArticle(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): "H" | "KH" {
    const sunSailType = PD.productToSunSailType(sunSail)
    if (!PoleLogic.isAxle(sunSailType, poleIndex))
      return "H";
    const offerList = "H";
    return offerList;
  }

  /** @deprecated https://plaspack.atlassian.net/browse/APP-871 */
  static getOfferListForFirstBolzen(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): "H" | "KH" {
    const material = SunSailLogic.getPoleMaterial(sunSail, poleIndex);
    if (PoleLogic.isIron(material))
      return "H";
    // return "KH";
    return "H"; // https://plaspack.atlassian.net/browse/APP-871
  }

  static calcPossiblePrimaryMountArticles(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): { articles: string[], mountArticles: IMountArticle[], accesories: IArticleWithCount[] } {
    let articles: string[] = [];
    let mountArticles: IMountArticle[] = [];
    const accesories: IArticleWithCount[] = [];

    if (SunSailLogic.getPoleType(sunSail, poleIndex) !== PoleType.Pole)
      return { articles, mountArticles, accesories };

    const sunSailType = PD.productToSunSailType(sunSail)
    const isAxle = PoleLogic.isAxle(sunSailType, poleIndex);
    const poleMaterial = SunSailLogic.getPoleMaterial(sunSail, poleIndex);
    if (isAxle) {
      const mountInfos = SunSailLogic.calcMountTypes(sunSail, poleIndex);
      for (const mountInfo of mountInfos) {
        let _articles: string[] = [];
        let _mountArticles: IMountArticle[] = [];
        const mountType = mountInfo.mountType;
        if (mountType === "clamp") {
          _articles = this.getClampArticlesForMaterial(poleMaterial);
          const offerList = SunSailLogic.getOfferListForAxisArticle(sunSail, poleIndex);
          _mountArticles = _articles.map<IMountArticle>((articleNumber) => ({
            articleNumber, mountType, offerList
          }));
        } else if (mountType === "bolt") {
          _articles = this.getRingSchraubenForMaterial(poleMaterial);
          _mountArticles = _articles.map<IMountArticle>((articleNumber) => ({ articleNumber, mountType, offerList: SunSailLogic.getOfferListForFirstBolzen(sunSail, poleIndex) }));  // Ringbolzen ist beim Mast dabei? https://plaspack.atlassian.net/browse/APP-1259
        } else if (mountType === "threaded") {
          _mountArticles.push({ articleNumber: "00E13000468", mountType, offerList: "KH" }); // https://plaspack.atlassian.net/browse/APP-1275
        } else if (mountType === "ringAdapter") {
          _articles = [SunSailLogic.getRingAdapterArticleForSystem(sunSailType)];
          _mountArticles = _articles.map<IMountArticle>((articleNumber) => ({ articleNumber, mountType, offerList: "H" }));
        }

        if (articles.length === 0) {
          articles = _articles;
          mountArticles = _mountArticles;
        } else {
          articles = [...articles, ..._articles];
          mountArticles = [...mountArticles, ..._mountArticles];
        }
      }
    } else {
      const mountInfos = SunSailLogic.calcMountTypes(sunSail, poleIndex);
      for (const mountInfo of mountInfos) {
        let _articles: string[] = [];
        let _mountArticles: IMountArticle[] = [];
        const mountType = mountInfo.mountType;
        switch (mountType) {
          case "clamp": {
            _articles = this.getClampArticlesForMaterial(poleMaterial);
            const offerList = SunSailLogic.getOfferListForAxisArticle(sunSail, poleIndex); // remove? offerList = "H"
            _mountArticles = _articles.map<IMountArticle>((articleNumber) => ({ articleNumber, mountType, offerList }));
            break;
          }
          case "bolt": {
            _articles = this.getRingSchraubenForMaterial(poleMaterial);
            _mountArticles = _articles.map<IMountArticle>((articleNumber) => ({ articleNumber, mountType, offerList: SunSailLogic.getOfferListForFirstBolzen(sunSail, poleIndex) })); // Ringbolzen ist beim Mast dabei? https://plaspack.atlassian.net/browse/APP-1259
            break;
          }
        }
        if (articles.length === 0) {
          articles = _articles;
          mountArticles = _mountArticles;
        } else {
          articles = [...articles, ..._articles];
          mountArticles = [...mountArticles, ..._mountArticles];
        }
      }
    }

    return { articles, mountArticles, accesories };
  }

  static calcPossiblePoleMountClamps(mount: PoleMountType, material: MaterialType, poleType: PoleType): { articles: IArticleWithCount[], accesories: IArticleWithCount[] } {
    const articles: IArticleWithCount[] = [];
    const accesories: IArticleWithCount[] = [];

    if (poleType === PoleType.Pole) {
      if (mount === PoleMountType.Clamp) {
        if (PoleLogic.isAlu(material)) {
          articles.push({ articleNumber: "00E13000243", count: 2 }) // aus Edelstahl zur Befestigung von Masten an Wänden - mit 2 Bohrungen (Ø13) - Aluminium Mast
          articles.push({ articleNumber: "00E13001864", count: 0 }) // aus Edelstahl zur Befestigung von Masten an Wänden - mit 2 Bohrungen (Ø13) - Aluminium Mast

          // articles.push({ articleNumber: "00E13000245", count: 0 }) // Abstandhalter Wand/Mast 5cm für Aluminium-Mast inkl. Schelle
          articles.push({ articleNumber: "00E13000249", count: 0 }) // Abstandhalter Design Wand/Mast 5cm für Aluminium-Mast
          articles.push({ articleNumber: "00E13000246", count: 0 }) // Abstandhalter Design Wand/Mast 10cm für Aluminium-Mast
          articles.push({ articleNumber: "00E13000247", count: 0 }) // Abstandhalter Design Wand/Mast 20cm für Aluminium-Mast

          accesories.push({ articleNumber: "00E13001940", count: 0 }) // Abstandhalter Wand/Mast 5cm für Aluminium-Mast inkl. Schelle
          accesories.push({ articleNumber: "00E13000110", count: 0 }) // Gegenplatte für Wandschelle Aluminium- und Edelstahl-Mast
          accesories.push({ articleNumber: "00E13001863", count: 0 }) // Gegenplatte für Wandschelle Aluminium- und Edelstahl-Mast hochglanz
        } else if (PoleLogic.isSteel(material)) {
          // articles.push("00E13000103") // Mastadapter Rundschelle für Edelstahl-Mast D70mm
          // articles.push("00E13001734") // Soliday Design-Mastschelle DM 70 mm // 21.2.2022 removed  https://plaspack.atlassian.net/browse/APP22-227
          articles.push({ articleNumber: "00E13000143", count: 2 }) // Wandschelle für Edelstahl-Mast DM70mm
          articles.push({ articleNumber: "00E13001860", count: 0 }) // Wandschelle für Edelstahl-Mast DM70mm hochglanz

          // articles.push({ articleNumber: "00E13000120", count: 0 }) // Abstandhalter Wand/Mast 5cm für Edelstahl-Mast inkl. Schelle
          articles.push({ articleNumber: "00E13000248", count: 0 }) // Abstandhalter Design Wand/Mast 5cm für Edelstahl-Mast
          articles.push({ articleNumber: "00E13000121", count: 0 }) // Abstandhalter Design Wand/Mast 10cm für Edelstahl-Mast
          articles.push({ articleNumber: "00E13000122", count: 0 }) // Abstandhalter Design Wand/Mast 20cm für Edelstahl-Mast

          accesories.push({ articleNumber: "00E13001940", count: 0 }) // Abstandhalter Wand/Mast 5cm für Aluminium-Mast inkl. Schelle
          accesories.push({ articleNumber: "00E13000110", count: 0 }) // Gegenplatte für Wandschelle Aluminium- und Edelstahl-Mast
          accesories.push({ articleNumber: "00E13001863", count: 0 }) // Gegenplatte für Wandschelle Aluminium- und Edelstahl-Mast hochglanz

        } else if (PoleLogic.isDroppole(material)) {
          articles.push({ articleNumber: "00E13000320", count: 2 }) // Abstandhalter Design Wand/Mast 5cm für Droppole
          articles.push({ articleNumber: "00E13000321", count: 0 }) // Abstandhalter Design Wand/Mast 10cm für Droppole
          articles.push({ articleNumber: "00E13000322", count: 0 }) // Abstandhalter Design Wand/Mast 20cm für Droppole
        } else if (PoleLogic.isTeleProfil(material)) {
          articles.push({ articleNumber: "00E13000243", count: 2 })
          articles.push({ articleNumber: "00E13001345", count: 0 })
          articles.push({ articleNumber: "00E13000245", count: 0 })
          articles.push({ articleNumber: "0E13001222", count: 0 })
        }
      }
    }

    return {
      articles,
      accesories
    }
  }

  static calcPossibleEarthScrews(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId, strict: boolean = true): { articles: PoleMountArticle[], accesories: IArticleWithCount[] } {
    const articles: PoleMountArticle[] = [];
    const accesories: IArticleWithCount[] = [];

    const poleMaterial = SunSailLogic.getPoleMaterial(sunSail, poleId)

    if (strict) {
      const poleType = SunSailLogic.getPoleType(sunSail, poleId);
      const poleMount = SunSailLogic.getPoleMount(sunSail, poleId);
      if (!(poleMount === PoleMountType.EarthScrew && poleType === PoleType.Pole)) {
        return { articles, accesories }
      }
    }

    const sunSailType = PD.productToSunSailType(sunSail);
    if (SST.is(sunSailType, SST.Sandy)) {
      if (PoleLogic.isMaterial(poleMaterial, "iron48")) {
        articles.push("00E13000756");
      } else if (PoleLogic.isMaterial(poleMaterial, "alu")) {
        articles.push("00E13000756");
      }
    } else {
      if (PoleLogic.isTeleProfil(poleMaterial)) {
        articles.push("00E13000756"); // Schraubfundament 114
      } if (PoleLogic.isIron(poleMaterial)) {
        // no earthScrews
      } else {
        articles.push("00E13000227"); // Schraubfundament KSF E 140x1400mm mit Einlegescheibe
        articles.push("00E13000228"); // Schraubfundament KSF E 140x1600mm
        if (PoleLogic.isDroppole(poleMaterial)) {
          accesories.push({ articleNumber: "00E13000735", count: 1 }); // Exzentersatz für Schraubfundament Droppole-Mast
        } else if (PoleLogic.isAlu(poleMaterial)) {
          accesories.push({ articleNumber: "00E13000648", count: 1 }); // Exzentersatz E-76 für Schraubfundament Edelstahl-Mast
        } else if (PoleLogic.isSteel(poleMaterial)) {
          accesories.push({ articleNumber: "00E13000649", count: 1 }); // Exzentersatz E-76 für Schraubfundament Edelstahl-Mast
        }
      }
    }

    return { articles, accesories }
  }

  static _calcPossibleCoilUpsAM(group: MaterialGroup | undefined): string[] {
    const articles: string[] = [];
    articles.push(seilStoppWinchArticleId); // Seilstopp-Winsch mit Kurbel inklusive Masthalterung
    articles.push("00E13001320"); // Snap-Basic Winsch inkl. Kurbel & Adapterplatte
    articles.push("00E13000591"); // Snap-Design Winsch inkl. Kurbel & Adapterplatte
    if (group) {
      articles.push(fallstoppMap[group]); // Snap-Grundplatte mit Fallstoppklemme
      articles.push(sicherheitsKlemmenMap[group]); // Autorelease/Sicherheits-Seilklemme
    }
    return articles.filter((a) => !!a)
  }

  static isCoilUpValid(sunSail: ISunSailData, poleIndex: number): boolean {
    return SunSailLogic.calcPossibleCoilUps(sunSail, poleIndex).length > 0;
  }

  static calcPossibleCoilUps(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleIndex: number): string[] {
    const sunSailType = PD.productToSunSailType(sunSail)
    const poleCount = Poles.getCount(sunSail);
    const poleType = SunSailLogic.getPoleType(sunSail, poleIndex);
    if (poleType !== PoleType.Pole && poleType !== PoleType.Wall) // SpecialConsole?
      return [];

    const materialGroup = SunSailLogic.getPoleMaterialGroup(sunSail, poleIndex);

    const articles: string[] = [];
    if (SST.is(sunSailType, SST.AM)) {
      if (poleIndex === PoleIndex.B || poleIndex === PoleIndex.D)
        articles.push(...SunSailLogic._calcPossibleCoilUpsAM(materialGroup));
    } else if (
      (SST.is(sunSailType, SST.X) && (poleIndex === PoleIndex.B || poleIndex === PoleIndex.D)) ||
      (SST.is(sunSailType, SST.Xs) && ((poleCount === 3 && poleIndex === PoleIndex.C) || (poleCount === 4 && poleIndex >= PoleIndex.C)))
    ) {
      // snap winch basic and fallstopp included..
      articles.push("00E13001320"); // Snap-Basic Winsch inkl. Kurbel & Adapterplatte
      articles.push("00E13001322"); // Aufpreis Snap-Design Winsch inkl. Kurbel & Adapterplatte
      articles.push("00E13001323"); // Aufpreis Snap-Premium Winsch inkl. Kurbel & Adapterplatte
    }
    // else if (SST.isCSF(sunSailType)) {
    //   articles.push(...snapWinches); // Snap-Basic Winsch inkl. Kurbel & Adapterplatte
    // }
    return articles;
  }

  static calcPossibleHeightChanges(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): string[] {

    if (!SunSailLogic.isHeightChangeValid(sunSail, poleId))
      return [];

    const articles: string[] = [];
    const sunSailType = PD.productToSunSailType(sunSail);

    let material: MaterialType | undefined = SunSailLogic.getPoleMaterial(sunSail, poleId);
    if (SST.is(sunSailType, SST.Sandy)) {
      MaterialMap.push(articles, heightChangeMaterialMap.sandyPatent, material)
      MaterialMap.push(articles, heightChangeMaterialMap.sandyWire, material)
      return articles;
    }


    const poleCount = Poles.getCount(sunSail);
    const poleType = SunSailLogic.getPoleType(sunSail, poleId)
    const pylon = SunSailLogic.getPolePylonType(sunSail, poleId);
    const coilUp = !!SunSailLogic.getSailCoilUpArticle(sunSail, poleId);

    if (poleType !== PoleType.Pole)
      material = undefined;
    if (SST.is(sunSailType, SST.One)) {
      articles.push(HeightChangeLogic.getSlideBarArticle("Schlitten3", material)!);
    } if (SST.is(sunSailType, SST.AM)) {
      articles.push(HeightChangeLogic.getSlideBarArticle("Schlitten3", material)!);
      if (coilUp)
        articles.push(HeightChangeLogic.getSlideBarArticle("Schlitten2", material)!);
    } else if (sunSailType === SunSailType.SolidayX) {
      if (coilUp) {
        articles.push(HeightChangeLogic.getSlideBarArticle("Schlitten1", material)!);
      } else {
        articles.push(HeightChangeLogic.getSlideBarArticle("Schlitten3", material)!);
      }
    } else if (sunSailType === SunSailType.SolidayXS) {
      if (coilUp) {
        articles.push(HeightChangeLogic.getSlideBarArticle(poleCount === 3 ? "Schlitten1" : "Schlitten3", material)!);
      } else {
        articles.push(HeightChangeLogic.getSlideBarArticle("Schlitten4", material)!);
      }
    } else if (SST.is(sunSailType, SST.CoSF, SST.Flex)) {
      articles.push(HeightChangeLogic.getSlideBarArticle("Schlitten3", material)!);  // Soliday Gleitschiene 1,5m komplett, inkl. Mastmontage
    } else if (SST.is(sunSailType, SST.C, SST.CsTwin)) {
      // 1132
      if (PoleLogic.isAluOrSteel(material)) {
        if (pylon) {
          MaterialMap.push(articles, heightChangeMaterialMap.pulley, material)
          MaterialMap.push(articles, heightChangeMaterialMap.c12r40Plus, material)
          MaterialMap.push(articles, heightChangeMaterialMap.c12r40, material)
          MaterialMap.push(articles, heightChangeMaterialMap.e12r40, material)
        }
      } else if (PoleLogic.isDroppole(material)) {
        if (pylon) {
          MaterialMap.push(articles, heightChangeMaterialMap.customWinch, material)
          MaterialMap.push(articles, heightChangeMaterialMap.customWinch, material === "Droppole_Black" ? "Droppole" : "Droppole_Black")
          MaterialMap.push(articles, heightChangeMaterialMap.c12r40Plus, material)
          MaterialMap.push(articles, heightChangeMaterialMap.c12r40, material)
          MaterialMap.push(articles, heightChangeMaterialMap.e12r40, material)
        }
      }
    }
    return articles;
  }

  static calcClothTextureType(sunSailType: SunSailType, poleCount: number, groupIndex: number, cloth: IClothData | undefined, colorId: string): ClothTextureTypes {

    if (colorId.endsWith("999")) return "rays";
    else if (colorId.endsWith("998")) return "linear";

    if (SST.is(sunSailType, SST.Sandy)) {
      if (poleCount === 3)
        return "simple3";
      return "simple";
    }

    if (SST.is(sunSailType, SST.CoSF)) {
      if (cloth?.clothType === ClothType.ChillSail) {
        return "curtainChillSail"
      }
      if (poleCount === 3) {
        return "simple3";
      }
      return "simple";
    }

    if (SST.is(sunSailType, SST.CsXsTwin)) {
      if (poleCount === 3 || (groupIndex === 1 && poleCount === 5))
        return "rays";
      return "quad";
    }

    if (cloth?.textureType)
      return cloth.textureType;

    const clothType = cloth?.clothType || ClothType.Austrosail_Nano
    if (clothType === ClothType.Lotus || clothType === ClothType.Concept || clothType === ClothType.Austrosail_Nano || clothType === ClothType.Austrosail_Nano_Protect || clothType === ClothType.Soltis92 || clothType === ClothType.Soltis86)
      return "rays";

    return "linear";
  }

  static getSunSailTextureType(sunSailType: SunSailType, poleCount: number, clothType: ClothType | undefined, colorId: string): SunSailTextureType[] {
    // const clothType = cloth?.clothType || ClothType.Austrosail_Nano;
    // SunSailTextureType SailParticles::CalcTextureType
    if (colorId.endsWith("999")) return ["rays"];
    else if (colorId.endsWith("998")) return ["linear"];

    if (SST.is(sunSailType, SST.C, SST.A, SST.X, SST.One, SST.Flex)) {
      if (clothType === ClothType.Concept) return ["rays"];
    }
    if (SST.is(sunSailType, SST.CsTwin)) {
      if (poleCount === 3) return ["rays"];
      else if (poleCount === 5) return ["linear", "rays"];
      else return ["linear"];
    } else if (SST.is(sunSailType, SST.CoSF)) return ["simple"];
    else if (clothType === ClothType.Austrosail_Nano || clothType === ClothType.Austrosail_Nano_Protect || clothType === ClothType.Soltis92 || clothType === ClothType.Soltis86) return ["rays"];

    return ["linear"];
  }


  static applyAxleSize(sunSail: ISunSailData, poleIndex: PoleIndex, newPos: IVec3): ISunSailData {
    const { sunSailType } = sunSail;
    const axles = PoleLogic.getAxleIndices(sunSailType);
    if (!axles)
      return sunSail;

    const otherAxle = Poles.getOtherPole(sunSailType, poleIndex)

    const targetSunSail = SunSailReducers.setPolePosition(sunSail, poleIndex, newPos, { direct: true });
    const logicalAxleLength = SunSailLogic.calcAxleLength(targetSunSail, "logical");

    const axleMinMax = SunSailLogic.getLogicalMinMaxAxle(sunSail);
    // const targetAxleLength = sunSail.sunSailType === SunSailType.SolidayFlex ? logicalAxleLength : MinMax.clamp(logicalAxleLength, axleMinMax); // https://plaspack.atlassian.net/browse/APP-1572
    const targetAxleLength = MinMax.clamp(logicalAxleLength, axleMinMax);
    if (targetAxleLength === logicalAxleLength)
      sunSail = SunSailReducers.setPolePosition(targetSunSail, poleIndex, newPos, { direct: true })
    else {
      const slopeA = SunSailLogic.calcMountSlope(sunSail, axles.first);
      slopeA.y += Poles.getPole(sunSail, axles.first).position.y;
      const slopeB = SunSailLogic.calcMountSlope(sunSail, axles.second);
      slopeB.y += Poles.getPole(sunSail, axles.second).position.y;
      const deltaSlope = SST.is(sunSail.sunSailType, SST.CsXs) ?
        Vec3.sub(slopeB, slopeA) :
        Vec3.from(-(slopeA.z + slopeB.z), slopeB.y - slopeA.y, -(slopeA.x + slopeB.x));

      // added distance from mount points to axle start position
      const tal = SunSailLogic.calcAxleMountDistFromLogicalLength(sunSail, targetAxleLength);
      // calculate target ground length from target axle length
      const a = Math.sqrt(tal * tal - deltaSlope.y * deltaSlope.y)
      // add width from pole to mount point
      const ax = a + deltaSlope.x;
      // calculate length from ground pole positions
      const ground = Math.sqrt(deltaSlope.z * deltaSlope.z + ax * ax)

      const otherGround = Vec3.xz(Poles.getPole(targetSunSail, otherAxle).position);
      const poleGround = Vec3.xz(Poles.getPole(targetSunSail, poleIndex).position);
      const finalPos = Vec3.addScaled(otherGround, Vec3.dir(otherGround, poleGround), ground);
      finalPos.y = newPos.y;

      sunSail = SunSailReducers.setPolePosition(targetSunSail, poleIndex, finalPos, { direct: true })
    }
    // sunSail = SunSailReducers.setDist(sunSail, poleIndex, SunSailLogic.fixDist(sunSail, poleIndex), { direct: true })
    // sunSail = SunSailReducers.setDist(sunSail, otherAxle, SunSailLogic.fixDist(sunSail, otherAxle), { direct: true })

    return sunSail;
  }


  static calcKnotPoints(poleIndex: number, nets: IVec3[], mounts: IVec3[]): IKnotPoints {
    const c = poleIndex < 4 ? 2 : 4;
    const d = poleIndex < 4 ? 3 : 5;

    const frontNetLength = Vec3.len(nets[c], nets[d]);
    const frontNetDir = Vec3.dir(nets[c], nets[d]);
    const knotDist = SunSailLogic.getKnotOuterDist();
    const k6 = (frontNetLength - knotDist * 2) / 6;

    const dir = poleIndex % 2 === 0 ? 1 : -1;
    const mc = mounts[poleIndex];
    const pc = nets[poleIndex];
    const kOuter = Vec3.addScaled(pc, frontNetDir, dir * knotDist);
    const kInner = Vec3.addScaled(pc, frontNetDir, dir * k6 * 2);
    const kFrontMid = Vec3.mid(kOuter, kInner);
    const mkMid = Vec3.to(mc, kFrontMid);
    const axleToMount = Vec3.dir(SunSailMath.calcNearestAxlePoint(mounts[0], mounts[1], mc), mc);
    const projectedLength = Vec3.len(Vec3.project(mkMid, axleToMount));
    const kMid = Vec3.addScaled(kFrontMid, mkMid, -0.2 / projectedLength);
    return {
      outer: kOuter,
      inner: kInner,
      mid: kMid,
      mount: mc
    }
  }
  static getEdgeSideMul(edgeName: EdgeName, sunSailType: SunSailType, poleCount: number): number {
    const side = SunSailLogic.calcClothIndexFromEdge(sunSailType, poleCount, edgeName) === 0 ? 1 : -1;
    const edgeDir = SunSailLogic.getEdgeDirectionMul(edgeName, sunSailType, poleCount);
    return side * edgeDir;
  }

  static getEdgeDirectionMul(edgeName: EdgeName, _sunSailType: SunSailType, poleCount: number): number {
    return edgeName[0] === "a" && edgeName[1] === getPointNameSmall(poleCount - 1) ? - 1 : 1;
  }

  static findPoleIndex(poleIndices: number[], poleIndex: number): number {
    for (let i = 0; i < poleIndices.length; i++) {
      if (poleIndices[i] === poleIndex) return i;
    }
    return -1;
  }

  static calcAngleForIndex(index: number, poleIndices: number[], positions: IVec3[]): number {
    const nextIndex = (index + 1) % poleIndices.length;
    const lastIndex = (index + poleIndices.length - 1) % poleIndices.length;
    const angle = Vec3.calcAngle(positions[poleIndices[lastIndex]], positions[poleIndices[index]], positions[poleIndices[nextIndex]]);
    return angle;
  }
  static getSailCoilUpPoleIndex(sunSail: ISunSailData | ISCSunSailData, defaultValue: number = -1): number {
    for (let i = 0; i < Poles.getCount(sunSail); i++) {
      if (SunSailLogic.getSailCoilUpArticle(sunSail, i))
        return i;
    }
    return defaultValue;
  }

  static calcOneOperatingRopeLength(sunSail: ISunSailData): number {
    return sunSail.operatingRope?.length || SunSailLogic._calcOneOperatinRopeLengthAuto(sunSail);
  }

  // https://plaspack.atlassian.net/browse/API-80
  static calcRope1Length(sunSail: ISunSailData): number { // Verspannungsseil
    if (sunSail.sunSailType === SunSailType.SolidayC) {
      return OnePylonSystemLogic.getRope1Length(sunSail)
    } else if (sunSail.sunSailType === SunSailType.SolidayOne) {

      const deflectionPoint = SunSailLogic.getOperatingDeflectionPosition(sunSail);
      if (deflectionPoint !== undefined) {
        const mountPositions = SunSailLogic.calcMountPositions(sunSail, 0, {});
        const groupIndex = deflectionPoint === PoleIndex.D ? 1 : 0
        const netPositions = SunSailLogic.calcNetPositionGroups(sunSail, {})[groupIndex];

        const otherPoint = (deflectionPoint + 2) % 4
        const pointNet = netPositions[deflectionPoint];
        const total = SunSailLogic.calcTotalMaxDistInOut(sunSail, deflectionPoint);
        const distOutDeflection = SunSailLogic.calcDistanceOut(sunSail, deflectionPoint);
        const distOutOther = SunSailLogic.calcDistanceOut(sunSail, otherPoint);
        const distOut = Math.max(distOutDeflection, distOutOther);
        const distInDeflection = SunSailLogic.calcDistanceInFromOut(distOut, total);
        const R = SunSailLogic.calcNetPos(sunSail, deflectionPoint, distInDeflection, mountPositions);
        // const R2 = Vec3.addScaled(mountPositions[deflectionPoint], Vec3.dir(mountPositions[deflectionPoint], U), distInDeflection);
        const W = SunSailMath.calcNearestAxlePoint(mountPositions[0], mountPositions[2], R)
        const l1 = Vec3.len(pointNet, R);
        const l2 = Vec3.len(R, W);
        const l3 = Vec3.len(mountPositions[otherPoint], W)
        const l4 = SunSailLogic.calcDistanceIn(sunSail, otherPoint);
        const l = l1 + l2 + l3 + l4 + 2;
        return Math.ceil(l);
      }
      return 0;
    } else {
      const netPositions = SunSailLogic.calcNetPositionGroups(sunSail, {});

      const _B = netPositions[0][PoleIndex.B];
      const _D = netPositions[1][PoleIndex.D];

      const distIn = SunSailLogic.calcDistanceIn(sunSail, PoleIndex.B) + SunSailLogic.calcDistanceIn(sunSail, PoleIndex.D);
      let len = Vec3.len(_B, _D) + distIn * 2 + 2;

      if (SunSailLogic.hasTwistingSetForSunSail(sunSail)) {
        if (sunSail.sunSailType === SunSailType.SolidayX)
          len += 5;
        else
          len += 3;
      }
      return Math.ceil(len);
    }
  }

  static calcRope2Length(sunSail: ISunSailData): number { // Spannseil
    const sunSailType = sunSail.sunSailType;
    const poleCount = Poles.getCount(sunSail);
    if (sunSailType === SunSailType.SolidayXS && poleCount === 4) {
      const mountPositions = SunSailLogic.calcMountPositions(sunSail, 0, undefined);
      const ad = Vec3.len(mountPositions[PoleIndex.A], mountPositions[PoleIndex.D]);
      const bc = Vec3.len(mountPositions[PoleIndex.B], mountPositions[PoleIndex.C]);
      const ab = Vec3.len(mountPositions[PoleIndex.A], mountPositions[PoleIndex.B]);
      return Math.ceil(ad + bc + ab + 3.5);
    } else if (sunSailType === SunSailType.SolidayOne && poleCount === 4) {
      const mountPositions = SunSailLogic.calcMountPositions(sunSail, 0, {});
      const deflectionPointIndex = SunSailLogic.getOperatingDeflectionPosition(sunSail);
      if (deflectionPointIndex !== undefined) {
        const maxDistOut = Vec3.len(mountPositions[deflectionPointIndex], Line3.getNearestPoint(mountPositions[0], mountPositions[2], mountPositions[deflectionPointIndex], true));
        const AB = Vec3.len(mountPositions[deflectionPointIndex], mountPositions[PoleIndex.A]);
        return Math.ceil(AB + maxDistOut + 2);
      }
    } else {
      const coilUpPoleIndex = SunSailLogic.getSailCoilUpPoleIndex(sunSail);
      if (coilUpPoleIndex !== -1) {
        const ret = SunSailLogic.calcTotalMaxDistInOut(sunSail, coilUpPoleIndex);

        if (sunSailType === SunSailType.SolidayX) {
          if (ret.totalMaxDistOut !== undefined) {
            return Math.ceil(ret.totalMaxDistOut * 3 + 3.5);
          }
        } else if (sunSailType === SunSailType.SolidayXS && poleCount === 4) {
          throw new Error("Use calcLinkageRopeLength2_XS")
        } else {
          if (ret.totalMaxDistOut !== undefined) {
            return Math.ceil(ret.totalMaxDistOut + 3.5); // https://plaspack.atlassian.net/browse/APPSK-121
          }
        }
        return 0;
      }
    }
    return 0;
  }

  static _getAMXConcavityPercent(ssd: ISunSailData): number {
    const clothType = SunSailLogic.getCloth(ssd)?.clothType;
    if (this.getSunSailTextureType(ssd.sunSailType, Poles.getCount(ssd), clothType || ClothType.None, ssd.colorId)[0] === "rays") return 2;

    if (clothType !== undefined) {
      switch (clothType) {
        case ClothType.Austronet940: return 5;
        case ClothType.Austronet950: return 5;
        case ClothType.Austronet960: return 5;
        case ClothType.Austrosail_PES: return 3;
        case ClothType.Lotus: return 3; // https://plaspack.atlassian.net/browse/API-2
        case ClothType.Austrosail_Nano: return 2;
        case ClothType.Concept: return 2;
        case ClothType.Austrosail_Acryl: return 3; // https://plaspack.atlassian.net/browse/APPSK-162
      }
    }
    return 1;
  }

  static getConcavityPercent(ssd: ISunSailData, edgeName: EdgeName): number {
    const concavityType = this.getConcavity(ssd, edgeName);
    if (concavityType === undefined || concavityType === "straight")
      return 0;

    // https://plaspack.atlassian.net/browse/APPSK-147

    switch (ssd.sunSailType) {
      case SunSailType.SandyPoly:
      case SunSailType.SandyRect:
        return 0; // https://plaspack.atlassian.net/browse/APP-1510
      case SunSailType.SolidayC:
        return 1;
      case SunSailType.SolidayCS:
        return 1; // wird nicht benötigt?
      case SunSailType.SolidayXS:
        return Poles.getCount(ssd) === 3 ? this._getAMXConcavityPercent(ssd) : 1;
      case SunSailType.SolidayFlex:
      case SunSailType.SolidayM:
      case SunSailType.SolidayOne:
      case SunSailType.SolidayA:
      case SunSailType.SolidayX: {
        return this._getAMXConcavityPercent(ssd);
      }
      case SunSailType.Concept: {
        return 6; // (this.getEdgeConcavityInfo(ssd.sunSailType, Poles.getPoleCount(ssd).corner, SunSailLogic.getCloth(ssd)?.clothType)?.value || 0) + 3
      }
      case SunSailType.Standard:
      case SunSailType.Fixed: {
        const clothType = SunSailLogic.getCloth(ssd)?.clothType;
        const poleCount = Poles.getCount(ssd);

        switch (clothType) {
          case ClothType.Austronet950: return poleCount === 3 ? 6 : 3;
          case ClothType.Austronet960: return poleCount === 3 ? 6 : 3;
          case ClothType.ChillSail: return 0;
          case ClothType.Soltis92: return 2;
          case ClothType.Austrosail_PES: return 3;
          case ClothType.Austrosail_Acryl: return MathUtil.clamp(Math.floor(SunSailLogic.calcNetSideLength(ssd, edgeName)), 0, 6); // https://plaspack.atlassian.net/browse/APPSK-147
        }

        return 3;
      }
    }

    return 1;
  }

  static getHeightChangeArticle(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): string {
    if (!Poles.isCornerPole(poleId))
      return "";
    const pole = Poles.getPole(sunSail, poleId);
    const articleId = pole.articleSlots?.heightChange;
    if (articleId) {
      const validArticles = SunSailLogic.calcPossibleHeightChanges(sunSail, poleId);
      return ArticleChooser.findValidArticle(articleId, validArticles, heightChangeAlternativeMap) || "";
    }
    return "";
  }

  static calcPossibleHeightChangeMotorCables(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): string[] {
    const motorArticle = SunSailLogic.getHeightChangeArticle(sunSail, poleId);
    if (!HeightChangeLogic.hasHeightChangeMotor(motorArticle))
      return [];
    return ArticleFeatureMap.getShopFeatureArticles("motorCable");
  }
  static getHeightChangeMotorCable(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): string | undefined {
    const motorArticle = SunSailLogic.getHeightChangeArticle(sunSail, poleId);
    if (!HeightChangeLogic.hasHeightChangeMotor(motorArticle))
      return "";
    return getChecked({
      selection: Poles.getPole(sunSail, poleId).articleSlots?.heightChangeMotorCable,
      validItems: SunSailLogic.calcPossibleHeightChangeMotorCables(sunSail, poleId),
      emptySelection: "__selectionRequired__"
    })
  }


  static getEarthScrewArticle(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): string {
    const pole = Poles.getPole(sunSail, poleId);
    const articleId = pole?.articleSlots?.earthScrew;
    const validArticles = SunSailLogic.calcPossibleEarthScrews(sunSail, poleId);

    if (!articleId || !validArticles.articles.includes(articleId)) {
      if (validArticles.articles.length > 0)
        return validArticles.articles[0];
      return "";
    }
    return articleId;
  }

  static getPrimaryMountArticle(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): IMountArticle {
    const pole = Poles.getPole(sunSail, poleIndex);
    const articleId = pole.articleSlots?.primaryMount;
    const validArticles = SunSailLogic.calcPossiblePrimaryMountArticles(sunSail, poleIndex);

    if (articleId) {
      const index = validArticles.articles.indexOf(articleId);
      if (index !== -1) {
        return validArticles.mountArticles[index];
      }
    }
    if (validArticles.mountArticles.length > 0)
      return validArticles.mountArticles[0];

    return { articleNumber: "", mountType: "direct" };
  }

  static calcPossibleTwistingSetArticles(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): IMountArticle[] {
    if (SunSailLogic.getPoleType(sunSail, poleIndex) !== PoleType.Pole)
      return [];

    const sunSailType = PD.productToSunSailType(sunSail);
    if (!SunSailLogic.hasTwistingSetOnPole(sunSail.twistingSet, sunSailType, poleIndex))
      return [];

    const poleMaterial = SunSailLogic.getPoleMaterial(sunSail, poleIndex);

    const preferClamp = (sunSail.buildTime || 0) >= 2406030715 && [SunSailType.SolidayOne, SunSailType.SolidayA].includes(sunSailType) && Pvi.is(sunSail.version, "2024");

    const clampArticles = this.getClampArticlesForMaterial(poleMaterial).map<IMountArticle>((articleNumber) => ({ articleNumber, mountType: "clamp", offerList: "H" }));
    const boltArticles = this.getRingSchraubenForMaterial(poleMaterial).map<IMountArticle>((articleNumber) => ({ articleNumber, mountType: "bolt", offerList: "H" }));
    if (preferClamp)
      return [...clampArticles, ...boltArticles];
    return [...boltArticles, ...clampArticles];
  }

  static getWallPlateArticle(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): string {
    const pole = Poles.getPole(sunSail, poleIndex);
    const poleType = SunSailLogic.getPoleType(sunSail, poleIndex)
    if (poleType !== PoleType.Wall)
      return ""
    const articleId = pole.articleSlots?.wallMount;
    const heightChange = SunSailLogic.getHeightChangeArticle(sunSail, poleIndex);
    const sunSailType = PD.productToSunSailType(sunSail)
    const validArticles = SunSailLogic.calcPossibleWallPlates(sunSailType, Poles.getCount(sunSail), poleIndex, poleType, SunSailLogic.hasRopeFixArticle(sunSail, poleIndex), !!heightChange, SunSailLogic.isSlideBar(heightChange), FL.fromProduct(sunSail))
    if (!articleId || !validArticles.includes(articleId)) {
      if (validArticles.length > 0)
        return validArticles[0];
      return "";
    }
    return articleId;
  }

  static isCoilUpIncludedInSystem(sunSailType: SunSailType, article: string): boolean {
    return (SST.is(sunSailType, SST.XXs) && article === "00E13001320")
  }

  static isFallstoppIncluded(sunSailType: SunSailType): boolean {
    return SST.is(sunSailType, SST.XXs);
  }


  static getSailCoilUpArticle(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): string | undefined {
    return getChecked({
      selection: Poles.getPole(sunSail, poleId).articleSlots?.coilUp,
      validItems: SunSailLogic.calcPossibleCoilUps(sunSail, poleId),
      emptySelection: ""
    })
  }

  static getOneControlArticle(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId): string | undefined {
    if (poleId === PoleIndex.A) {
      const materialGroup = SunSailLogic.getPoleMaterialGroup(sunSail, poleId);
      return "one_" + materialGroup;
    }
    return undefined;
  }

  static getRopeFixArticle(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId): string | undefined {
    const sunSailType = PD.productToSunSailType(sunSail)
    if (SST.is(sunSailType, SST.One)) {
      return SunSailLogic.getOneControlArticle(sunSail, poleId);
    } if (SST.is(sunSailType, SST.CoSF, SST.Flex)) {
      if (SunSailLogic.getSailRopeFixType(sunSail, poleId) === "indirect") {
        const mountArticle = SunSailLogic.getSailMountArticle(sunSail, poleId);
        const winchArticle = SunSailLogic.getSailMountWinch(sunSail, poleId);
        return winchArticle || mountArticle;
      }
      return undefined;
    } else {
      return SunSailLogic.getSailCoilUpArticle(sunSail, poleId);
    }
  }

  static hasRopeFixArticle(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId): boolean {
    return !!SunSailLogic.getRopeFixArticle(sunSail, poleId);
  }

  static getWinchHeightChangePriorityMode(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): WinchHeightChangePriority | undefined {
    const hasWinch = SunSailLogic.hasRopeFixArticle(sunSail, poleIndex);
    const slideInfo = HeightChangeLogic.getSlideInfoForSunSail(sunSail, poleIndex);

    if (hasWinch && !!slideInfo) {
      if (Product.isProductSC(sunSail))
        return "heightChange";
      return Poles.getPole(sunSail, poleIndex).winchHeightChangePriority || "winch"; // winch is old system
    }
    return undefined;
  }

  static isTargetDist(sunSailType: SunSailType, poleCount: number, poleId: PoleId, alignDistOut: boolean | undefined): boolean {
    if (!Poles.isCornerPole(poleId))
      return false;

    if (PoleLogic.isAxle(sunSailType, poleId))
      return false;
    if (SST.is(sunSailType, SST.CsXsTwin) && LogicS.isQuadFrontPole(sunSailType, poleCount, poleId))
      return true;
    return !!alignDistOut;
  }

  static getMaxHeightForWalls(): number {
    return 10;
  }

  static getMaxPointHeight(poleType: PoleType, material: MaterialType): number {
    const maxLength = poleType === PoleType.Pole ? PoleLogic.getMaxPoleLength(material) : SunSailLogic.getMaxHeightForWalls();
    return maxLength;
  }

  static getMountItemHeight(item: PoleMountType, poleType: PoleType): number {
    if (poleType === PoleType.Pole) {
      switch (item) {
        case PoleMountType.BaseCube:
          return 0.56;
        case PoleMountType.ConcreteCube:
          return 0.5;
        case PoleMountType.EarthScrew:
          return 0.28;
        case PoleMountType.Clamp:
          return 0.25;
      }
    }
    return 0;
  }

  static calcLengthMinMax(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleIndex: number): IMinMax {

    const poleType = SunSailLogic.getPoleType(sunSail, poleIndex)
    const poleMaterial = SunSailLogic.getPoleMaterial(sunSail, poleIndex);
    const poleMount = SunSailLogic.getPoleMount(sunSail, poleIndex);
    const poleAngle = SunSailLogic.getPoleAngle(sunSail, poleIndex);
    const pylonType = SunSailLogic.getPolePylonType(sunSail, poleIndex);
    if (poleType !== PoleType.Pole) {
      return {
        min: 0,
        max: SunSailLogic.getMaxHeightForWalls()
      }
    }

    const bottomPoleExtension = SunSailLogic.calcBottomPoleExtension(sunSail, poleIndex);
    const bottomMountItemHeight = this.getMountItemHeight(poleMount, poleType);
    const topPoleExtension = SunSailLogic.calcTopPoleExtension(sunSail, poleIndex);
    const topPoleHeight = SunSailLogic.isDoubleMount(sunSail, poleIndex) ? 0.16 : 0;

    const pylonLength = pylonType ? SunSailLogic.getPylonInfo(pylonType).length : 0;

    let minLength = 0;
    if (SunSailLogic.getPoleLengthBEnabled(sunSail, poleIndex) /* PoleLogic.isBent(pole.angle, pole.lengthB)*/) {
      const minBendHeightOverNiveau = PoleLogic.getMinBendHeightOverGround(poleMaterial, poleMount);
      if (minBendHeightOverNiveau === undefined)
        throw new Error("minBendHeightOverNiveau is not defined for bent pole!");

      const minBendLength = PoleLogic.calcMinBendLength(poleMaterial, poleAngle);
      const minLengthOverBend = 0.2;

      const lengthNonPylon = Math.max(bottomPoleExtension + minBendHeightOverNiveau + minBendLength * 2 + minLengthOverBend + topPoleExtension + topPoleHeight, 0.5);
      const lengthWithPylon = bottomPoleExtension + minBendHeightOverNiveau + minBendLength * 2 + pylonLength;
      minLength = Math.max(lengthNonPylon, lengthWithPylon);
    } else {
      const minOverGround = Math.max(0.2, bottomMountItemHeight);
      const lengthNonPylon = Math.max(bottomPoleExtension + minOverGround + topPoleExtension + topPoleHeight, 0.5);
      const dornLength = SunSailLogic.getPylonDornLength(poleMaterial, poleMount)
      // const poleOffsetY = SunSailLogic.calcPoleOffsetY(sunSail, poleIndex);
      minLength = Math.max(lengthNonPylon, pylonLength + dornLength);
    }

    const maxPointLength = SunSailLogic.getMaxPointHeight(poleType, poleMaterial);

    return {
      min: minLength, // (poleAttributes.lengthAMin || 0) + (lengthB ? lb.min + bendLength : 0),
      max: maxPointLength
    }
  }

  static calcLengthBMinMax(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleIndex: number): IMinMax {
    const poleType = SunSailLogic.getPoleType(sunSail, poleIndex)
    const poleMaterial = SunSailLogic.getPoleMaterial(sunSail, poleIndex);
    const poleMount = SunSailLogic.getPoleMount(sunSail, poleIndex);
    if (PoleLogic.canBeBent(poleType, poleMaterial, poleMount)) {
      {
        const minBendHeightOverNiveau = PoleLogic.getMinBendHeightOverGround(poleMaterial, poleMount);
        if (minBendHeightOverNiveau === undefined)
          throw new Error("minBendHeightOverNiveau is not defined for bent pole!");

        const poleAngle = SunSailLogic.getPoleAngle(sunSail, poleIndex);
        const minBendLength = PoleLogic.calcMinBendLength(poleMaterial, poleAngle);
        const minLengthOverBend = 0.2;

        const poleLength = SunSailLogic.getPoleLength(sunSail, poleIndex);
        const pylonType = SunSailLogic.getPolePylonType(sunSail, poleIndex);
        const pylonLength = pylonType ? SunSailLogic.getPylonInfo(pylonType).length : 0;
        const maxPointLength = poleLength - minBendLength - Math.max(pylonLength, minLengthOverBend); //  SunSailLogic.getMaxPointHeight(pole.poleType, pole.material);
        const minLen = minBendHeightOverNiveau + SunSailLogic.calcPoleBasePos(sunSail, poleIndex) + minBendLength;

        return {
          min: Math.max(Math.min(minLen, maxPointLength), 0), // Biegungsmitte
          max: Math.max(maxPointLength, 0)
        }
      }
    }

    return {
      min: 0,
      max: 100
    }

    // const poleAttributes = SunSailLogic.getPoleAttributes(sunSail, poleIndex);

    // const { length } = pole;
    // const minMax = { min: 0, max: 4 } // PoleLogic.getMinMax(poleAttributes.lengthB);

    // const bendLength = 0; //PoleLogic.calcMinBendLength(pole.material, pole.angle, PoleLogic.calcLengthB(pole.lengthB) > 0)

    // return {
    //   min: minMax.min,
    //   max: minMax.max > length - bendLength ? length - bendLength : minMax.max
    // }
  }

  static #revolvingRopeArticles: IRevolvingRopeInfo[] = [
    { id: "00E13000803", length: 2, color: "grey" },
    { id: "00E13000082", length: 3, color: "grey" },
    { id: "00E13000145", length: 4, color: "grey" },
    { id: "00E13000146", length: 5, color: "grey" },
    { id: "00E13000423", length: 6, color: "grey" },
    { id: "00E13000421", length: 7, color: "grey" },
    { id: "00E13000422", length: 8, color: "grey" },

    { id: "00E13002052", length: 2, color: "black" },
    { id: "00E13002049", length: 3, color: "black" },
    { id: "00E13002050", length: 4, color: "black" },
    { id: "00E13002051", length: 5, color: "black" },
    { id: "00E13002053", length: 6, color: "black" },
    { id: "00E13002054", length: 7, color: "black" },
    { id: "00E13002055", length: 8, color: "black" },
  ]

  static getRevolvingRope(sunSail: ISunSailData | ISCSunSailData | ISail2dForm): string | undefined {
    return sunSail.articleSlots?.revolvingRope || SunSailLogic.getInitialRevolvingRopeArticle(PD.productToSunSailType(sunSail));
  }
  static getPossibleRevolvingRopeArticles(sunSailType: SunSailType): string[] {
    if (!SunSailLogic.hasRevolvingRopeSST(sunSailType))
      return [];
    return SunSailLogic.#revolvingRopeArticles.map((a) => a.id);
  }

  static getInitialRevolvingRopeArticle(sunSailType: SunSailType): string | undefined {
    const revolvingRopeArticles = SunSailLogic.getPossibleRevolvingRopeArticles(sunSailType);
    if (revolvingRopeArticles.length === 0)
      return undefined;
    let initialId = "00E13000082";
    if (sunSailType === SunSailType.SolidayOne)
      initialId = "00E13002050";
    else if (sunSailType === SunSailType.SolidayFlex)
      initialId = "00E13002050";
    return revolvingRopeArticles.includes(initialId) ? initialId : revolvingRopeArticles[0];
  }

  static getRevolvingRopeInfo(articleId: string | undefined): IRevolvingRopeInfo | undefined {
    if (!articleId)
      return undefined;

    const item = SunSailLogic.#revolvingRopeArticles.find((a) => a.id === articleId);
    return item;
  }

  static getRevolvingRopeLengthForArticle(articleId: string | undefined): number {
    return SunSailLogic.getRevolvingRopeInfo(articleId)?.length || 0;
  }

  static #calcMatrixForPos(sunSail: ISunSailData, poleId: PoleId, heightPos: number, mode: "top" | "bottom" | "mount" | "niveau"): IMat4 {
    const options = SunSailLogic.calcPoleLengthOptions(sunSail, poleId, mode);
    const poleAngle = SunSailLogic.getPoleAngle(sunSail, poleId);
    const poleLength = SunSailLogic.getPoleLength(sunSail, poleId);
    const poleLengthB = SunSailLogic.getPoleLengthB(sunSail, poleId);
    const bottomPoleExtension = SunSailLogic.calcBottomPoleExtension(sunSail, poleId);
    const segments = PoleHelper.calcPoleSegmentsExtended(sunSail.id, poleId, poleLength, poleAngle, poleLengthB, bottomPoleExtension, SunSailLogic.calcPoleOffsetY(sunSail, poleId))
    return PoleHelper.calcMatrixForLengthPosFromSegments(segments, heightPos, options)
    //     name: "matrixForLengthPosSelector",
    // resultEqualFn: "shallow",
    // getId: (p) => p.sunSailId + "_" + p.poleIndex + "_" + p.heightPos + "_" + p.mode + "_" + p.withExtensions
  }

  static calcMatrixForPos = CacheUtil.memo(SunSailLogic.#calcMatrixForPos, "calcMatrixForPos", { // matrixForLengthPosSelector
    idsFromArgs: (sunSail, poleIndex, heightPos, mode) => [sunSail.id, poleIndex, heightPos, "" + mode],
    resultEqualFn: "shallow"
  })

  static calcMatrixForRopeFix(sunSail: ISunSailData, poleIndex: number): IMat4 {
    return SunSailLogic.calcMatrixForPos(sunSail, poleIndex, SunSailLogic.getRopeFixHeight(sunSail, poleIndex), "niveau")
  }

  static calcTwistingSetRopeOffset(sunSail: ISunSailData, poleIndex: number): IVec3 {
    const poleType = SunSailLogic.getPoleType(sunSail, poleIndex)
    const poleMaterial = SunSailLogic.getPoleMaterial(sunSail, poleIndex);
    if (poleType === PoleType.Pole) {
      return Vec3.from(0.0, 0, -PoleLogic.getPoleDiameterMeter(poleMaterial, poleType) * 0.5 - 0.035);
    } else if (poleType === PoleType.Wall) {
      const mountArticle = SunSailLogic.getWallPlateArticle(sunSail, poleIndex);
      if (ONE_AXLE_WALL_PLATES.includes(mountArticle)) {
        return Vec3.from(0.0, 0.25, -0.0) // Soliday-One Wandplatte
      }
      return Vec3.from(0.0, 0.133, -0.06) // Bügel
    } else {
      return Vec3.from(0.0, 0.133, -0.06) //
    }
  }

  static calcTwistingSetRopePositions(sunSail: ISunSailData): {
    first: IVec3,
    second: IVec3
  } | undefined {
    const { sunSailType, twistingSet } = sunSail;
    if (!twistingSet)
      return undefined;

    const indices = PoleLogic.getAxleIndices(sunSailType)!;

    const m1 = Mat4.mul(SunSailLogic.calcPoleGroundMatrix(sunSail, indices.first), SunSailLogic.calcTwistingSetMatrix(sunSail, indices.first))
    const m2 = Mat4.mul(SunSailLogic.calcPoleGroundMatrix(sunSail, indices.second), SunSailLogic.calcTwistingSetMatrix(sunSail, indices.second))

    const offset1 = SunSailLogic.calcTwistingSetRopeOffset(sunSail, indices.first);
    const offset2 = SunSailLogic.calcTwistingSetRopeOffset(sunSail, indices.second);

    return {
      first: Vec3.applyMatrix4(offset1, m1),
      second: Vec3.applyMatrix4(offset2, m2)
    }
  }

  static calcMountPositionsForAxlePoles(sunSail: ISunSailData, axleIndex: number): IAxlePos<IVec3> | undefined {
    const indices = PoleLogic.getAxleIndices(sunSail.sunSailType);
    if (!indices)
      return undefined;

    return {
      first: SunSailLogic.calcMountPositionForAxlePole(sunSail, indices.first, axleIndex),
      second: SunSailLogic.calcMountPositionForAxlePole(sunSail, indices.second, axleIndex),
    };
  }

  static calcFromToMatrix(from: IVec3, to: IVec3, options?: { skipScale?: boolean }): IMat4 {
    const dir = Vec3.to(from, to);
    const matrices = [Mat4.createTranslation(from.x, from.y, from.z), Mat4.createLookAt(Vec3.ZERO, dir, Vec3.Y), Mat4.createRotationY(Math.PI * 0.5)];
    if (!options?.skipScale) {
      matrices.push(Mat4.createScale(Vec3.len(dir), 1, 1));
    }
    return Mat4.mulsArray(matrices);
  }

  static calcAxleMatrixFromPositions(from: IVec3, to: IVec3): IMat4 {
    const dir = Vec3.to(from, to);

    const lookAt = Mat4.createLookAt(Vec3.ZERO, dir, Vec3.Y);
    const rotation = Mat4.mul(lookAt, Mat4.createRotationY(Math.PI * 0.5));
    const mNew = Mat4.mul(Mat4.createTranslation(from.x, from.y, from.z), rotation);
    return mNew;
  }

  static calcAxleMatrix(sunSail: ISunSailData, axleIndex: number): IMat4 | undefined {
    const axlePositions = SunSailLogic.calcMountPositionsForAxlePoles(sunSail, axleIndex);
    if (!axlePositions)
      return undefined;
    return SunSailLogic.calcAxleMatrixFromPositions(axlePositions.first, axlePositions.second);
  }

  static calcTopMountY(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId): number {
    const topMountMinMax = SunSailLogic.calcTopMountMinMax(sunSail, poleId);
    const pole = Poles.getPole(sunSail, poleId);

    if (pole.topMountY === undefined || pole.topMountY === "auto") { // isAuto

      const sunSailType = PD.productToSunSailType(sunSail);
      if (SST.is(sunSailType, SST.Sandy)) {
        return MathUtil.clamp(0.5, topMountMinMax.min, topMountMinMax.max);
      }

      const hasHeightSlide = HeightChangeLogic.getSlideInfoForSunSail(sunSail, poleId);
      const coilUpArticle = SunSailLogic.hasRopeFixArticle(sunSail, poleId);
      if (hasHeightSlide && coilUpArticle) {
        const priority = SunSailLogic.getWinchHeightChangePriorityMode(sunSail, poleId);
        if (priority === "winch") {

          const bottom = SunSailLogic.calcPossibleBottomSlideBarPos(sunSail, poleId);
          const availableLength = SunSailLogic.calcAvailableHeightChangeSlideLength(sunSail, poleId, bottom, topMountMinMax.min);
          const stepLength = SunSailLogic.#calcSlideBarStepLength(availableLength);
          // const slideBarLength = SunSailLogic.calcSlideBarLength(sunSail, poleIndex)

          const v = SunSailLogic.calcAvailableHeightChangeSlideLength(sunSail, poleId, bottom, stepLength);
          return MathUtil.clamp(v, topMountMinMax.min, topMountMinMax.max);
        } else {
          return topMountMinMax.min;
        }
      }
      return MathUtil.clamp(0, topMountMinMax.min, topMountMinMax.max);
    }

    return MathUtil.clamp(pole.topMountY, topMountMinMax.min, topMountMinMax.max);
  }

  static calcSlideBarLength(sunSail: ISunSailData, poleIndex: number): number {
    const topMountY = SunSailLogic.calcTopMountY(sunSail, poleIndex);
    const bottom = SunSailLogic.calcPossibleBottomSlideBarPos(sunSail, poleIndex);
    const availableLength = SunSailLogic.calcAvailableHeightChangeSlideLength(sunSail, poleIndex, bottom, topMountY);
    const calculatedLength = Math.min(1.5, availableLength);
    return SunSailLogic.#calcSlideBarStepLength(calculatedLength);
  }

  static #calcSlideBarStepLength(length: number): number {
    const possibleSteps = [1.5, 1.425, 1.325, 1.225, 1.125, 1.025, 0.925, 0.825, 0.725, 0.625, 0.525, 0.425, 0];
    for (const pl of possibleSteps) {
      if (gec(length, pl)) {
        return pl;
      }
    }
    return 0;
  }

  static calcPossibleBottomSlideBarPos(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): number {
    const offsets = { bent: 0, mount: 0 };
    const coh = SunSailLogic.getRopeFixHeight(sunSail, poleIndex);
    if (coh > 0) {
      const space = SunSailLogic.calcSpaceBetweenWinchAndSlideBar(sunSail, poleIndex);
      const above = coh + space.box;
      const crank = coh + space.crank;
      return Math.max(above, crank);
    } else if (SunSailLogic.isPoleBent(sunSail, poleIndex)) {
      return SunSailLogic.calcPossibleBottomPolePosIfBent(sunSail, poleIndex) + (offsets?.bent || 0);
    } else {
      return SunSailLogic.calcPossibleBottomPolePosForMount(sunSail, poleIndex) + (offsets?.mount || 0);
    }
  }

  static calcSpaceBetweenWinchAndSlideBar(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): { crank: number; box: number } {
    const coilUpArticle = SunSailLogic.getRopeFixArticle(sunSail, poleIndex);
    const sizeInfo = SunSailLogic.getCoilUpSizeInfo(coilUpArticle);
    const crankRadiusAbove = (sizeInfo.crankRadius ? sizeInfo.crankRadius - crankRadiusReserve : 0) || sizeInfo.above;
    return {
      crank: crankRadiusAbove - 0.020,
      box: sizeInfo.above + 0.02
    }
  }

  static calcRopeFixSize(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId): ICoilUpSizeInfo {
    const coilUpArticle = SunSailLogic.getRopeFixArticle(sunSail, poleId);
    const sizeInfo = SunSailLogic.getCoilUpSizeInfo(coilUpArticle);
    return sizeInfo;
  }

  static calcHeightChangeCoilUpSize(sunSail: ISunSailData, poleIndex: number): ICoilUpSizeInfo {
    const coilUpArticle = SunSailLogic.getHeightChangeArticle(sunSail, poleIndex);
    const sizeInfo = SunSailLogic.getCoilUpSizeInfo(coilUpArticle);
    return sizeInfo;
  }


  static calcPossibleBottomPolePosIfBent(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): number {
    const poleMaterial = SunSailLogic.getPoleMaterial(sunSail, poleIndex);
    const poleAngle = SunSailLogic.getPoleAngle(sunSail, poleIndex);
    const poleLengthB = SunSailLogic.getPoleLengthB(sunSail, poleIndex);
    return poleLengthB + PoleLogic.calcMinBendLength(poleMaterial, poleAngle);
  }

  static calcPossibleBottomPolePosForMount(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): number {
    const poleMaterial = SunSailLogic.getPoleMaterial(sunSail, poleIndex);
    const poleMount = SunSailLogic.getPoleMount(sunSail, poleIndex);

    switch (poleMount) {
      case PoleMountType.GroundSleeve:
        return 0.05;
      case PoleMountType.GroundPlate:
        return PoleLogic.isDroppole(poleMaterial) ? 0.01 : 0.3;
      case PoleMountType.EarthScrew:
        return 0.05;
      case PoleMountType.Clamp:
        return 0.25;
      case PoleMountType.BaseCube:
        return 0.55;
      case PoleMountType.ConcreteCube:
        return PoleLogic.isDroppole(poleMaterial) ? 0.45 : 0.45;
    }
    return 0;
  }

  static calcRopeFixHeightMinMax(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId): IMinMax {
    const coilUpSize = SunSailLogic.calcRopeFixSize(sunSail, poleId);
    return SunSailLogic.#calcCoilUpHeightMinMax(sunSail, poleId, coilUpSize);
  }

  static calcHeightChangeCoilUpHeightMinMax(sunSail: ISunSailData, poleIndex: number): IMinMax {
    const coilUpSize = SunSailLogic.calcHeightChangeCoilUpSize(sunSail, poleIndex);
    return SunSailLogic.#calcCoilUpHeightMinMax(sunSail, poleIndex, coilUpSize);
  }

  static #calcCoilUpHeightMinMax(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId, coilUpSize: ICoilUpSizeInfo): IMinMax {
    const poleMount = SunSailLogic.getPoleMount(sunSail, poleId);
    const poleLength = SunSailLogic.getPoleLength(sunSail, poleId);
    // const poleY = SunSailLogic.calcPoleOffsetY(sunSail, poleIndex);

    const bentY = SunSailLogic.isPoleBent(sunSail, poleId) ? SunSailLogic.calcPossibleBottomPolePosIfBent(sunSail, poleId) : 0;
    const mountY = SunSailLogic.calcPossibleBottomPolePosForMount(sunSail, poleId);

    const crankRadiusBelow = coilUpSize.crankRadius || coilUpSize.below;
    const min = Math.max(crankRadiusBelow + 0.05, mountY + ((poleMount === PoleMountType.BaseCube || poleMount === PoleMountType.ConcreteCube) ? crankRadiusBelow : coilUpSize.below), bentY + coilUpSize.below)
    const crankRadiusAbove = coilUpSize.crankRadius || (coilUpSize.above + 0.05); // safety
    const max = poleLength - crankRadiusAbove - SunSailLogic.calcPoleBasePos(sunSail, poleId);
    return {
      min,
      max
    }
  }


  static hasBackenklemme(sunSailType: SunSailType, poleIndex: number): boolean {
    return SST.is(sunSailType, SST.AM) && poleIndex === 0;
  }

  static isSnapWinch(articleId: string | undefined): boolean {
    if (!articleId)
      return false;
    return allSnapWincheArticleNumbers.includes(articleId);
  }

  static isFallStopp(articleId: string | undefined): boolean {
    return !!articleId && allFallStoppArticles.includes(articleId)
  }

  static isDoubleMount(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleIndex: number): DoubleMountType | undefined {
    const sunSailType = PD.productToSunSailType(sunSail)
    if (!SunSailLogic.getSailCoilUpArticle(sunSail, poleIndex))
      return undefined;
    if (sunSailType === SunSailType.SolidayX && Poles.getCount(sunSail) === 4)
      return "x";
    if (sunSailType === SunSailType.SolidayXS && Poles.getCount(sunSail) === 3)
      return "xs";
    return undefined;
  }

  static getDoubleMountOffset(): number {
    return 0.1;
  }

  static calcRollArticle(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): "00E13001151" | "00E13001482" | "00E13000042" | "00E13001965" {
    const sunSailType = PD.productToSunSailType(sunSail)
    if (SST.is(sunSailType, SST.One)) {
      return "00E13001965";
    } else if (SST.is(sunSailType, SST.XXs)) {
      const rope2 = SunSailLogic.getRope2(sunSail);
      const ropeInfo = RopeHelper.getRopeInfo(rope2 || "");
      if (ropeInfo) {
        if (ropeInfo.diameter === 10)
          return "00E13001482";
        return "00E13000042";
      }
    } else if (SST.is(sunSailType, SST.AM)) {
      // https://plaspack.atlassian.net/browse/APP22-789;
      const seilStoppWinchOhneHV = SunSailLogic.getSailCoilUpArticle(sunSail, poleIndex) === seilStoppWinchArticleId /*&& !SunSailLogic.getHeightChange(sunSail, poleIndex)*/
      // height change not need since no roll is added...

      const coilUp = seilStoppWinchOhneHV ? "00E13001482" : "00E13000042";
      return coilUp;
    } if (SST.is(sunSailType, SST.CsTwin, SunSailType.SolidayC)) {
      return "00E13001151";
    }
    return "00E13000042";
  }

  static getRopeForCoilUpArticle(coilUpArticle: string | undefined): string {
    const ropeDiameter = winchInfos[coilUpArticle || ""].coilUpRopeDiameter || 8;
    const ropes: Record<number, string> = {
      6.5: "00E13000023",
      8: "00E13001359",
      10: "00E13000083"
    }
    return ropes[ropeDiameter] || ropes["6.5"];
  }

  static getSailBordersType(sunSail: ISunSailData | ISail2dForm | ISCSunSailData): SailBordersType {
    return getChecked({
      selection: sunSail.sailBordersType,
      validItems: SunSailLogic.calcPossibleBorderTypes(sunSail),
      emptySelection: "individual"
    }) || "individual";
  }

  static calcPossibleBorderTypes(sunSail: ISunSailData | ISail2dForm | ISCSunSailData): SailBordersType[] {
    const sunSailType = PD.productToSunSailType(sunSail);
    if (!SST.isSail(sunSailType))
      return [];

    switch (sunSailType) {
      case SunSailType.Fixed: return SunSailLogic.getCloth(sunSail)?.clothType === ClothType.ChillSail ? ["individual"] : ["individual", "orca"];
      case SunSailType.Concept: return ["orca"];
    }
    return [];
  }

  static isOrca(sunSail: ISunSailData | ISail2dForm | ISCSunSailData): boolean {
    return SunSailLogic.getSailBordersType(sunSail) === "orca";
  }

  static hasNetGroupKausche(sunSail: ISunSailData): boolean {
    if (sunSail.sunSailType === SunSailType.SolidayC && OnePylonSystemLogic.isOnePylonSystem(sunSail))
      return true;
    return false;
  }

  static calcNetGroupMount(sunSail: ISunSailData, poleIndex: number): "snapShackle" | "shackle" | "shackleAndRoll" | undefined {
    const poleCount = Poles.getCount(sunSail);
    if (sunSail.sunSailType === SunSailType.SolidayCS && poleCount === 3)
      return "snapShackle";
    if (sunSail.sunSailType === SunSailType.SolidayC)
      return "snapShackle";
    if (sunSail.sunSailType === SunSailType.SolidayXS && poleCount === 3) {
      if (SunSailLogic.hasRopeFixArticle(sunSail, poleIndex))
        return "shackleAndRoll";
      return "shackle";
    }
    return undefined;
  }

  static calcRollShackleConfig(sunSail: ISunSailData, poleIndex: number): IRollShackleConfig {
    const shackleArticle = "00E13000026";
    const mountInfo = SunSailLogic.calcPrimaryMountType(sunSail, poleIndex);
    const rollArticle = SunSailLogic.calcRollArticle(sunSail, poleIndex);

    const config: IRollShackleConfig = {}
    for (let i = 0; i < mountInfo.extendedMount.length; i++) {
      const mi = mountInfo.extendedMount[i];
      switch (mi.type) {
        case "rollOnly": {
          config[i === 0 ? "roll" : "roll2"] = rollArticle;
          break;
        }
        case "shackleOnly": {
          config[i === 0 ? "shackle" : "shackle2"] = shackleArticle;
          break;
        }
        case "shacklePlusRoll": {
          config[i === 0 ? "roll" : "roll2"] = rollArticle;
          config[i === 0 ? "shackle" : "shackle2"] = shackleArticle;
          break;
        }
      }
    }
    return config;
  }


  static calcKeder(ssd: ISunSailData | ISail2dForm): number[] {
    const axle = SunSailLogic.getAxleInfo(ssd);
    if (!axle)
      return [];

    const maxKeders = SST.is(ssd.sunSailType, SST.CsTwin) ? 1 : (Poles.getCount(ssd, "corner") === 3 ? 1 : 2);

    if (axle.keder.length === 2 && axle.keder[0] === axle.keder[1])
      return [axle.keder[0]]
    if (maxKeders < axle.keder.length) {
      return axle.keder.slice(0, maxKeders);
    }
    return axle.keder;
  }

  static calcDefaultAlignDistOut(sunSailType: SunSailType, poleCount: number): boolean {
    return SST.is(sunSailType, SST.C, SST.One) && poleCount === 4
  }

  static alignDistOutRequiredForGuarantee(sunSailType: SunSailType, poleCount: number): boolean {
    return sunSailType === SunSailType.SolidayC && poleCount === 4;
  }

  static alignDistOutChangable(sunSail: ISunSailData | ISCSunSailData | ISail2dForm): boolean {
    const sunSailType = PD.productToSunSailType(sunSail)
    const poleCount = PD.productToPoleCount(sunSail);
    return (
      SST.is(sunSailType, SST.One, SST.Flex, SST.AMX) ||
      (sunSailType === SunSailType.SolidayC && Product.calcSourceConfigurator(sunSail) === "2d")
    ) && poleCount === 4;
  }

  static calcAlignDistOut(sunSail: ISunSailData | ISCSunSailData | ISail2dForm): boolean {
    if (Product.isProductSC(sunSail)) {
      throw new Error("calcAlignDistOut not supported for scData so far...")
    }

    const sunSailType = PD.productToSunSailType(sunSail)
    const poleCount = PD.productToPoleCount(sunSail);

    if (SunSailLogic.alignDistOutChangable(sunSail))
      return sunSail.alignDistOut !== undefined ? sunSail.alignDistOut : SunSailLogic.calcDefaultAlignDistOut(sunSailType, poleCount);
    return SunSailLogic.calcDefaultAlignDistOut(sunSailType, poleCount);
  }

  // static calcTopMountOffsetMinMax(sunSail: ISunSailData, poleIndex: number): IMinMax {
  //   return {
  //     min: 0,
  //     max: 0.3
  //   }
  // }

  static calcTopMountMinMax(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId): IMinMax {
    const poleType = SunSailLogic.getPoleType(sunSail, poleId)
    const poleMaterial = SunSailLogic.getPoleMaterial(sunSail, poleId);
    const poleAngle = SunSailLogic.getPoleAngle(sunSail, poleId);
    if (poleType !== PoleType.Pole) {
      return {
        min: 0,
        max: 0
      }
    }

    const heightA = SunSailLogic.getPoleLengthA(sunSail, poleId);

    const sunSailType = PD.productToSunSailType(sunSail);
    if (SST.is(sunSailType, SST.Sandy)) {
      return { min: SandyLogic.isRopeOperatingPole(sunSail, poleId) === "operating" ? 0.2 : 0.05, max: heightA - 0.5 }
    }

    const topPoleExtension = SunSailLogic.calcTopPoleExtension(sunSail, poleId);

    const hasHeightSlide = HeightChangeLogic.getSlideInfoForSunSail(sunSail, poleId);

    const isBent = SunSailLogic.isPoleBent(sunSail, poleId);
    const minBendLength = isBent ? PoleLogic.calcMinBendLength(poleMaterial, poleAngle) : 0.03;

    if (PoleLogic.isDroppole(poleMaterial)) {
      const min = topPoleExtension + (hasHeightSlide ? HeightChangeSlideMeasures.minTopMount : 0.05);

      const bottomOffset = SunSailLogic.#calcPylonMountMin(sunSail, poleId);
      return {
        min,
        max: isBent ? heightA - minBendLength - min : heightA - bottomOffset
      }
    } else if (PoleLogic.isAluOrSteel(poleMaterial)) {
      const sunSailType = PD.productToSunSailType(sunSail)

      let min: number;
      if (sunSailType === SunSailType.SolidayM && poleId === 0 && !topPoleExtension) {
        min = 0.06;
      } else {
        min = topPoleExtension + (hasHeightSlide ? HeightChangeSlideMeasures.minTopMount : 0.03);
      }
      return {
        min,
        max: Math.max(min, isBent ? heightA - minBendLength - min : heightA - 0.5)
      }
    } else if (PoleLogic.isIron(poleMaterial)) {
      const min = topPoleExtension + (hasHeightSlide ? HeightChangeSlideMeasures.minTopMount : 0.05);
      const max = Math.max(min, isBent ? heightA - minBendLength - min : heightA - 0.5)
      return { min, max }
    } else if (PoleLogic.isTelescope(poleMaterial)) {
      const min = topPoleExtension + (hasHeightSlide ? HeightChangeSlideMeasures.minTopMount : 0.05);
      const max = Math.max(min, isBent ? heightA - minBendLength - min : heightA - 0.5)
      return { min, max }
    } else if (PoleLogic.isProfil(poleMaterial)) {
      const min = topPoleExtension + (hasHeightSlide ? HeightChangeSlideMeasures.minTopMount : 0.05);
      const max = Math.max(min, isBent ? heightA - minBendLength - min : heightA - 0.5)
      return { min, max }
    }

    return {
      min: topPoleExtension,
      max: topPoleExtension + 0.3
    }
  }

  static #calcPylonMountMin(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): number {
    const poleMount = SunSailLogic.getPoleMount(sunSail, poleIndex);
    switch (poleMount) {
      case PoleMountType.GroundPlate: //  https://plaspack.atlassian.net/browse/APP22-612
        return 0.3;
      case PoleMountType.BaseCube:
        return 0.56;
      case PoleMountType.ConcreteCube:
        return 0.5;
    }
    return 0.05;
  }

  static calcDefaultRopeFixHeight(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): number {
    const hasHeightSlide = HeightChangeLogic.getSlideInfoForSunSail(sunSail, poleIndex);
    return hasHeightSlide ? 0.68 : 0.92;
  }

  static getHeightChangeCoilUpHeight(sunSail: ISunSailData, poleId: PoleId): number {
    const article = SunSailLogic.getHeightChangeArticle(sunSail, poleId);
    if (!article)
      return 0;

    const pole = Poles.getPole(sunSail, poleId);
    const coilUpHeightMinMax = SunSailLogic.calcHeightChangeCoilUpHeightMinMax(sunSail, poleId);
    const targetHeight = (pole.heightChangeCoilUpHeight === undefined || pole.heightChangeCoilUpHeight === "auto") ? SunSailLogic.calcDefaultRopeFixHeight(sunSail, poleId) : pole.heightChangeCoilUpHeight;
    return MinMax.clamp(targetHeight, coilUpHeightMinMax);
  }

  static getRopeFixHeightRelativeToPole(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId): number {
    const rfx = SunSailLogic.getRopeFixHeight(sunSail, poleId);
    const ccpo = SunSailLogic.calcPoleOffsetYCorrected(sunSail, poleId);
    return rfx - ccpo;
  }

  static getRopeFixHeight(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId): number {
    if (!SunSailLogic.hasRopeFixArticle(sunSail, poleId))
      return 0;

    const pole = Poles.getPole(sunSail, poleId);
    const coilUpHeightMinMax = SunSailLogic.calcRopeFixHeightMinMax(sunSail, poleId);
    if (pole.coilUpHeight === undefined || pole.coilUpHeight === "auto") {
      const priority = SunSailLogic.getWinchHeightChangePriorityMode(sunSail, poleId);

      if (priority === "winch") {
        const targetHeight = SunSailLogic.calcDefaultRopeFixHeight(sunSail, poleId);
        return MinMax.clamp(targetHeight, coilUpHeightMinMax);
      } else if (priority === "heightChange") {
        const topY = SunSailLogic.calcTopMountY(sunSail, poleId);

        const availableLength = SunSailLogic.calcAvailableHeightChangeSlideLength(sunSail, poleId, coilUpHeightMinMax.min, topY)
        const stepLength = SunSailLogic.#calcSlideBarStepLength(availableLength);

        const space = SunSailLogic.calcSpaceBetweenWinchAndSlideBar(sunSail, poleId);
        const ropeFixDist = Math.max(space.box, space.crank);

        const targetHeight = SunSailLogic.calcAvailableHeightChangeSlideLength(sunSail, poleId, stepLength + ropeFixDist, topY)
        return MinMax.clamp(targetHeight, coilUpHeightMinMax);
      }
      return MinMax.clamp(SunSailLogic.calcDefaultRopeFixHeight(sunSail, poleId), coilUpHeightMinMax);
    }
    return MinMax.clamp(pole.coilUpHeight, coilUpHeightMinMax);
  }

  static calcAvailableHeightChangeSlideLength(sunSail: ISunSailData | ISCSunSailData, poleIndex: number, coilUpHeight: number, topY: number): number {
    const poleLength = SunSailLogic.getPoleLength(sunSail, poleIndex);
    const poleBasePosition = SunSailLogic.calcPoleBasePos(sunSail, poleIndex);
    const availableLength = poleLength - coilUpHeight - poleBasePosition - HeightChangeSlideMeasures.slideStart * 2 - topY;
    return availableLength;
  }

  static isPerpendicular(sunSail: ISunSailData | ISCSunSailData, poleIndex: number): boolean {
    const sunSailType = PD.productToSunSailType(sunSail)

    if (!PoleLogic.isAxle(sunSailType, poleIndex))
      return false;
    if (SST.is(sunSailType, SST.CsXs)) {
      if (PoleLogic.isDroppole(SunSailLogic.getPoleMaterial(sunSail, poleIndex))) {
        const mountType = SunSailLogic.getPrimaryMountArticle(sunSail, poleIndex).mountType;
        if (mountType === "clamp")
          return false;
      }
      return true;
    }
    return false;
  }

  static calcStandardSize(sunSail: ISunSailData | ISCSunSailData): StandardTemplateId | undefined{
    if (sunSail.specific?.type !== "standard")
      return undefined;
    return sunSail.specific?.sizeId || StandardTemplate.getDefaultStandardSize(Poles.getCount(sunSail)).id; // ssd.data.standardSize
  }

  static isValidEdge(sunSailType: SunSailType, poleCount: number, edgeName: EdgeName): boolean {
    const topology = SunSailTopology.calcTopology(sunSailType, poleCount);
    const ei = topology.edgeInfos[edgeName];
    if (!ei)
      return false;
    if (!ei.netBorder || ei.axle || ei.keder)
      return false;
    return true;
  }

  static calcPossibleConcavities(sunSail: ISunSailData | ISail2dForm | ISCSunSailData, edgeName: EdgeName): ConcavityType[] {
    const sunSailType = PD.productToSunSailType(sunSail);
    if (SST.is(sunSailType, SST.F)) {
      if (SunSailLogic.hasKeder(sunSail, edgeName))
        return ["straight"]
      if (SunSailLogic.isOrca(sunSail))
        return ["concave"]
      if (SunSailLogic.getCloth(sunSail)?.clothType === ClothType.ChillSail)
        return ["straight"];
      return ["concave", "straight",];
    }
    return ["concave"];
  }

  static getEdgeMountItemType(sunSail: ISunSailData | ISail2dForm | ISCSunSailData, edgeName: EdgeName): EdgeMountItemType | undefined {
    const seamType = this.getSeamType(sunSail, edgeName);
    switch (seamType) {
      case "pe": return "grommet";
      case "beltGrommet": return "grommet";
      case "beltStrap": return "strap";
      case "beltStrapDesign": return "strapDesign";
    }
    return undefined;
  }

  static calcPossibleGrommetTypes(sunSail: ISunSailData | ISail2dForm | ISCSunSailData, edgeName: EdgeName): GrommetType[] {
    const edgeMountItemType = this.getEdgeMountItemType(sunSail, edgeName);
    if (edgeMountItemType === "grommet")
      return ["zinc"]; // no "stainless"  https://plaspack.atlassian.net/browse/SS-62
    return [];
  }

  static calcPossibleEdgePositioningModes(sunSail: ISunSailData | ISail2dForm | ISCSunSailData, edgeName: EdgeName): EdgePositioningMode[] {
    const edgeMountItemType = SunSailLogic.getEdgeMountItemType(sunSail, edgeName);
    if (!edgeMountItemType)
      return []
    return ["30", "50", "userDist", "userCount"]
  }

  static getEdgePositioningMode(sunSail: ISunSailData | ISail2dForm | ISCSunSailData, edgeName: EdgeName): EdgePositioningMode | undefined {
    return getChecked({
      selection: sunSail.edges?.[edgeName]?.edgePositioning,
      validItems: SunSailLogic.calcPossibleEdgePositioningModes(sunSail, edgeName),
      emptySelection: "__selectionRequired__"
    });
  }

  static calcEdgeMountItemTypes(sunSail: ISunSailData): PartialRecord<EdgeName, EdgeMountItemType> {
    const ret: PartialRecord<EdgeName, EdgeMountItemType> = {};
    const edgeNames = SunSailEdges.getEdgeNames(sunSail.sunSailType, Poles.getCount(sunSail));
    for (const en of edgeNames) {
      const mit = SunSailLogic.getEdgeMountItemType(sunSail, en);
      if (mit)
        ret[en] = mit;
    }
    return ret;
  }

  static calcNetSideLength(sunSail: ISunSailData, edgeName: EdgeName): number {
    const netPositions = SunSailLogic.calcNetPositions(sunSail, SunSailLogic.calcClothIndexFromEdge(sunSail.sunSailType, Poles.getCount(sunSail), edgeName))
    const poleIndices = getEdgePoleIds(edgeName);
    const sideLength = Vec3.len(netPositions[poleIndices[0]], netPositions[poleIndices[1]]);
    return sideLength;
  }

  static calcNetSideLenForGrommets(sunSail: ISunSailData, edgeName: EdgeName): { sideLength: number, concaveLength: number } {
    if (!sunSail.poles?.length) {
      console.log("Shouldnät happen!")
      return { sideLength: 3, concaveLength: 3 };
    }
    const sideLength = SunSailLogic.calcNetSideLength(sunSail, edgeName);

    const concavityPercent = SunSailLogic.getConcavityPercent(sunSail, edgeName);
    // const stepArray = new ConcavityHelper(netPositions[poleIndices[0]], netPositions[poleIndices[1]], concavityPercent, 1, 16).calcStepArray();
    // let concaveLength = 0;
    // for (let i = 0; i < stepArray.length - 1; i++)
    //   concaveLength += Vec3.len(stepArray[i], stepArray[i + 1])
    // console.log("Len: ", sideLen, concaveLength, concaveLength / sideLen, concavityPercent);
    const concaveLength = sideLength * (1 + concavityPercent * concavityPercent / 3900); // rule of thumb
    return { sideLength, concaveLength };
  }

  static getEdgePositionOptions(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, edgeName: EdgeName): IEdgePositionOptions {
    const edgeMountItemType = SunSailLogic.getEdgeMountItemType(sunSail, edgeName)
    return {
      border: 0.0,
      withBorderItems: false,
      minDist: edgeMountItemType === "grommet" ? 0.2 : 0.5
    }
  }

  static calcEdgeMountItemReal(sunSail: ISunSailData, edgeName: EdgeName, params?: { overrideUserCount?: number }): IEdgeMountItemReal | undefined {
    const { overrideUserCount } = params || {};
    const edgePositioningMode = SunSailLogic.getEdgePositioningMode(sunSail, edgeName);
    if (!edgePositioningMode)
      return undefined;

    const edgeOptions = SunSailLogic.getEdgePositionOptions(sunSail, edgeName);

    const lengths = SunSailLogic.calcNetSideLenForGrommets(sunSail, edgeName);
    const countAndDist = EdgePositioningLogic.calcCountAndDistFromPositioning(edgePositioningMode, lengths.concaveLength, edgeOptions, { userDist: SunSailLogic.getEdgePositioningUserDist(sunSail, edgeName), userCount: overrideUserCount !== undefined ? overrideUserCount : SunSailLogic.getEdgePositioningUserCount(sunSail, edgeName) });
    const factors: number[] = [];
    for (let i = 0; i < countAndDist.count; i++) {
      const pos = (edgeOptions.withBorderItems ? edgeOptions.border : edgeOptions.border + countAndDist.dist) + i * countAndDist.dist;
      const factor = pos / lengths.concaveLength;
      factors.push(factor);
    }
    // todo
    return { itemType: SunSailLogic.getEdgeMountItemType(sunSail, edgeName) || "grommet", ...countAndDist, ...lengths, factors };
  }

  static getEdgePositioningUserDist(sunSail: ISunSailData | ISail2dForm | ISCSunSailData, edgeName: EdgeName): number {
    return sunSail.edges?.[edgeName]?.edgePositioningUserDist || 0.3;
  }

  static getEdgePositioningUserCount(sunSail: ISunSailData | ISail2dForm | ISCSunSailData, edgeName: EdgeName): number {
    return sunSail.edges?.[edgeName]?.edgePositioningUserCount || 4;
  }

  static getConcavity(sunSail: ISunSailData | ISail2dForm | ISCSunSailData, edgeName: EdgeName): ConcavityType | undefined {
    const sunSailType = PD.productToSunSailType(sunSail)
    if (!this.isValidEdge(sunSailType, Poles.getCount(sunSail), edgeName))
      return undefined;
    const ct = sunSail.edges?.[edgeName]?.concavity || (sunSail as any).concavities?.[edgeName] || "concave";
    const possibleConcavities = SunSailLogic.calcPossibleConcavities(sunSail, edgeName);
    if (possibleConcavities.includes(ct))
      return ct;
    else if (possibleConcavities.length > 0)
      return possibleConcavities[0];
    return undefined;
  }


  static getGrommetType(sunSail: ISunSailData | ISail2dForm | ISCSunSailData, edgeName: EdgeName): GrommetType | undefined {
    return getChecked({
      selection: sunSail.edges?.[edgeName]?.grommetType,
      validItems: SunSailLogic.calcPossibleGrommetTypes(sunSail, edgeName),
      emptySelection: "__selectionRequired__"
    });
  }

  static calcConcavities(sunSail: ISunSailData): Record<EdgeName, ConcavityType> | undefined {
    if (!SunSailEdges.hasEdgeData(sunSail.sunSailType))
      return undefined;
    const edgeNames = SunSailEdges.getEdgeNames(sunSail.sunSailType, Poles.getCount(sunSail));
    const ret: Record<EdgeName, ConcavityType> = {} as any;
    for (const en of edgeNames) {
      ret[en] = this.getConcavity(sunSail, en)!
    }
    return ret;
  }

  static calcConcavityPercents(sunSail: ISunSailData): Record<EdgeName, number> | undefined {
    // if (!SST.isCSF(sunSail.sunSailType))
    //   return undefined;
    const edgeNames = SunSailEdges.getEdgeNames(sunSail.sunSailType, Poles.getCount(sunSail));
    const ret: Record<EdgeName, number> = {} as any;
    for (const en of edgeNames) {
      const prercent = SunSailLogic.getConcavityPercent(sunSail, en);
      ret[en] = prercent
    }
    return ret;
  }


  static hasSameConcavities(sunSail: ISunSailData | ISail2dForm | ISCSunSailData): ConcavityType | undefined {
    const sunSailType = PD.productToSunSailType(sunSail)
    const netSidesNames = SunSailEdges.getEdgeNames(sunSailType, Poles.getCount(sunSail));
    let lastConcavity: ConcavityType | undefined;
    for (const edgeName of netSidesNames) {
      const concavity = SunSailLogic.getConcavity(sunSail, edgeName);
      if (!concavity)
        return undefined;
      if (lastConcavity) {
        if (lastConcavity !== concavity)
          return undefined;
      } else {
        lastConcavity = concavity;
      }
    }
    return lastConcavity;
  }

  // /** @deprecated */
  // static getConcavityCheckedDeprecated(sunSailType: number, poleCount: number, concavities: Record<string, ConcavityType> | undefined, edgeName: EdgeName): ConcavityType | undefined {
  //   if (!this.isValidEdge(sunSailType, poleCount, edgeName))
  //     return undefined;
  //   return concavities?.[edgeName] || "concave";
  // }

  // /** @deprecated */
  // static getConfectionDeprecated(confections: Record<string, SailConfectionType> | undefined, edgeName: EdgeName): SailConfectionType | undefined {
  //   const ct = confections?.[edgeName] || "default";
  //   return ct;
  // }

  // /** @deprecated */
  // static hasSameConfectionDeprecated(sunSailType: SunSailType, poleCount: number, confections: PartialRecord<EdgeName, SailConfectionType> | undefined): SailConfectionType | undefined {
  //   if (!confections)
  //     return undefined;
  //   const netSidesNames = SunSailLogic.getEdgeNames(sunSailType, poleCount);
  //   const sideValues = netSidesNames.map((e) => SunSailLogic.getConfectionDeprecated(confections, e))
  //   if (allSame(sideValues) && sideValues.length > 0)
  //     return sideValues[0];
  //   return undefined;
  // }

  static getPoleMountPositionModePos(sunSail: ISunSailData, poleId: PoleId, axleIndex: number): IVec3 {
    if (axleIndex === 1 && sunSail.sunSailType === SST.Twin && sunSail.form?.secondAxleDiff) {
      if (poleId === 0)
        return Vec3.addElements(SunSailLogic.getPolePos(sunSail, poleId), -sunSail.form?.secondAxleDiff * 0.5, 0, 0);
      if (poleId === 1)
        return Vec3.addElements(SunSailLogic.getPolePos(sunSail, poleId), sunSail.form?.secondAxleDiff * 0.5, 0, 0);
    }
    return SunSailLogic.getPolePos(sunSail, poleId);
  }

  static getEdgePosition(mountPositions: IVec3[], en: EdgeName, options?: { weight?: number; offsetSide?: number }): IVec3 {
    const { weight = 0.5, offsetSide = 0 } = options || {};
    const poleIndices = getEdgePoleIds(en)
    const ia = poleIndices[0];
    const a = mountPositions[ia]
    const ib = poleIndices[1];
    const b = mountPositions[ib];
    const ret = Vec3.lerp(a, b, weight);
    if (offsetSide) {
      const d = Vec3.dir(a, b);
      return Vec3.addScaled(ret, Vec3.from(d.z, d.y, -d.x), offsetSide);
    }
    return ret;
  }

  static getPoleNiveau(sunSail: ISunSailData, poleIndex: number): number {
    return SunSailLogic.getPolePos(sunSail, poleIndex).y;
  }

  static getSideVector(sunSail: ISunSailData, a: number, b: number): IVec3 {
    return Vec3.to(SunSailLogic.getPolePos(sunSail, a), SunSailLogic.getPolePos(sunSail, b));
  }

  static hasSpecialConsole(sunSail: ISunSailData, poleIndex: number): boolean {
    const poleType = SunSailLogic.getPoleType(sunSail, poleIndex)
    const poleMount = SunSailLogic.getPoleMount(sunSail, poleIndex);
    return poleType === "Special" || (poleType === "Pole" && poleMount === PoleMountType.Special) || (poleType === "Wall" && SunSailLogic.getWallPlateArticle(sunSail, poleIndex) === "00E13001025")
  }

  static isKederPossible(sunSail: ISunSailData | ISCSunSailData | ISail2dForm): boolean {
    const sunSailType = PD.productToSunSailType(sunSail)
    return SunSailEdges.hasEdgeData(sunSailType) && SunSailLogic.getSailBordersType(sunSail) === "individual" && SunSailLogic.getCloth(sunSail)?.clothType !== ClothType.ChillSail;
  }

  static getKederRail(sunSail: ISunSailData | ISail2dForm | ISCSunSailData, edgeName: EdgeName): string | undefined {
    return getChecked({
      selection: sunSail.edges?.[edgeName]?.kederRail,
      validItems: SunSailLogic.getPossibleKederRails(sunSail, edgeName),
      emptySelection: undefined
    });
  }

  static hasKeder(sunSail: ISunSailData | ISail2dForm | ISCSunSailData, edgeName: EdgeName): boolean {
    return !!SunSailLogic.getKederSize(sunSail, edgeName);
  }

  static hasKederOnPole(sunSail: ISunSailData | ISail2dForm | ISCSunSailData, poleId: PoleId): boolean {
    if (!Poles.isCornerPole(poleId))
      return false;
    const poleCount = Poles.getCount(sunSail);
    if (!poleCount)
      return false;
    return SunSailLogic.hasKeder(sunSail, getEdgeName(poleId, (poleId + 1) % poleCount)) || SunSailLogic.hasKeder(sunSail, getEdgeName(poleId, (poleId + poleCount - 1) % poleCount))
  }

  static getKederEdge(sunSail: ISunSailData): EdgeName | undefined {
    if (!SunSailLogic.isKederPossible(sunSail))
      return undefined;
    const edgeNames = SunSailEdges.getEdgeNames(sunSail.sunSailType, Poles.getCount(sunSail));
    for (const en of edgeNames) {
      if (SunSailLogic.hasKeder(sunSail, en))
        return en;
    }
    return undefined;
  }

  static getPossibleKeders(sunSail: ISunSailData | ISail2dForm | ISCSunSailData, edgeName: EdgeName): KederSize[] {
    const seamType = SunSailLogic.getSeamType(sunSail, edgeName);

    if (seamType !== "keder")
      return [];
    return kederSizes;
  }

  static getPossibleKederRails(sunSail: ISunSailData | ISail2dForm | ISCSunSailData, edgeName: EdgeName): string[] {
    const kederSize = SunSailLogic.getKederSize(sunSail, edgeName);
    if (!kederSize)
      return [];
    return kederInfos[kederSize]?.railArticles || [];
  }

  static getKederSize(sunSail: ISunSailData | ISail2dForm | ISCSunSailData, edgeName: EdgeName): KederSize | undefined {
    const sunSailType = PD.productToSunSailType(sunSail);
    if (!SST.isSail(sunSailType))
      return undefined;

    const validItems = SunSailLogic.getPossibleKeders(sunSail, edgeName);

    return getChecked({
      selection: sunSail.edges?.[edgeName]?.kederSize ?? "7.5",
      validItems,
      emptySelection: "__selectionRequired__"
    });
  }

  static getPossibleSeamTypes(_sunSail: ISunSailData | ISail2dForm | ISCSunSailData, _edgeName: EdgeName): EdgeSeamType[] {
    if (SunSailLogic.getCloth(_sunSail)?.clothType === ClothType.ChillSail)
      return ["chillsail"];

    if (SunSailLogic.isOrca(_sunSail) || SunSailLogic.getCloth(_sunSail)?.clothType === ClothType.ChillSail)
      return ["hemstitch"];

    const sunSailType = PD.productToSunSailType(_sunSail);
    if (SST.is(sunSailType, SST.F)) {
      const items: EdgeSeamType[] = [];
      items.push("belt");
      if (ClothData.isAustronet(SunSailLogic.getCloth(_sunSail)?.clothType))
        items.push("beltGrommet"); // https://plaspack.atlassian.net/browse/SS-62
      items.push("beltStrap");
      items.push("beltStrapDesign");
      items.push("hemstitch");
      items.push("keder");
      return items;
    }
    return ["belt"];
  }
  static getSeamType(sunSail: ISunSailData | ISail2dForm | ISCSunSailData, edgeName: EdgeName): EdgeSeamType | undefined {
    const sunSailType = PD.productToSunSailType(sunSail);
    if (!SST.isSail(sunSailType))
      return undefined;

    let seamType = sunSail.edges?.[edgeName]?.seamType;
    if (seamType === "keder") {
      const kederPriority = calcMaxKederPriority(sunSail.edges);
      if (kederPriority?.edge !== edgeName)
        seamType = undefined;
    }

    return getChecked({
      selection: seamType,
      validItems: SunSailLogic.getPossibleSeamTypes(sunSail, edgeName),
      emptySelection: "__selectionRequired__"
    });
  }

  static calcKederLength(sunSail: ISunSailData, edgeName: EdgeName | undefined): number {
    if (!edgeName)
      return 0;
    const mountPositions = SunSailLogic.calcMountPositions(sunSail, 0, undefined);
    const poleIndices = getEdgePoleIds(edgeName);
    return Vec3.len(mountPositions[poleIndices[0]], mountPositions[poleIndices[1]]) - 0.2;
  }

  static calcKederRailLength(sunSail: ISunSailData, edgeName: EdgeName | undefined): number {
    if (!edgeName)
      return 0;
    const mountPositions = SunSailLogic.calcMountPositions(sunSail, 0, undefined);
    const poleIndices = getEdgePoleIds(edgeName);
    return Vec3.len(mountPositions[poleIndices[0]], mountPositions[poleIndices[1]])
  }

  static getKederSailMount(sunSail: ISunSailData, edgeName: EdgeName): KederSailMountType | undefined {
    if (!SunSailLogic.hasKeder(sunSail, edgeName))
      return undefined;
    return sunSail.edges?.[edgeName]?.kederSailMountType || "inRail";
  }

  static isHemstitchPossible(sunSail: ISunSailData | ISail2dForm | ISCSunSailData, edgeName: EdgeName): boolean {
    const sunSailType = PD.productToSunSailType(sunSail)
    return SunSailEdges.hasEdgeData(sunSailType) && SunSailLogic.getSailBordersType(sunSail) === "individual" && !SunSailLogic.hasKeder(sunSail, edgeName) && SunSailLogic.getCloth(sunSail)?.clothType !== ClothType.ChillSail;
  }

  //*** @deprecated */
  static hasHemstitch(sunSail: ISunSailData | ISail2dForm | ISCSunSailData, edgeName: EdgeName): boolean {
    return SunSailLogic.isHemstitchPossible(sunSail, edgeName) && sunSail.edges?.[edgeName]?.seamType === "hemstitch";
  }

  static getPoleType(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): PoleType {
    if (SunSailLogic.isKederPossible(sunSail)) {
      if (SunSailLogic.hasKederOnPole(sunSail, poleId))
        return PoleType.Keder;
    }
    return getChecked({
      selection: Poles.getPole(sunSail, poleId)?.poleType,
      validItems: SunSailLogic.getPossiblePoleTypes(sunSail, poleId),
      emptySelection: PoleType.SailOnly
    })!;
  }

  static poleTypeCache: Record<string, PoleType[]> = {};
  static getPossiblePoleTypes(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): PoleType[] {
    if (Poles.isCenterPole(poleId))
      return cached("center", SunSailLogic.poleTypeCache, () => [PoleType.Pole]);

    const sunSailType = PD.productToSunSailType(sunSail);
    if (SST.is(sunSailType, SST.Sandy))
      return cached("sandy", SunSailLogic.poleTypeCache, () => [PoleType.Pole]);

    if (Product.isProduct2d(sunSail))
      return cached("product2d_Empty", SunSailLogic.poleTypeCache, () => [])

    if (SunSailLogic.isKederPossible(sunSail)) {
      if (SunSailLogic.hasKederOnPole(sunSail, poleId))
        return cached("keder", SunSailLogic.poleTypeCache, () => [PoleType.Keder]);
    }

    const pt = Poles.getPole(sunSail, poleId)?.poleType;
    return PD.productToSunSailType(sunSail) === SunSailType.Fixed || (pt === PoleType.SailOnly) // keep if sailOnly is selected... // https://plaspack.atlassian.net/browse/APP-718
      ? cached("fixed", SunSailLogic.poleTypeCache, () => [PoleType.Pole, PoleType.Wall, PoleType.SailOnly])
      : cached("rest", SunSailLogic.poleTypeCache, () => [PoleType.Pole, PoleType.Wall])
  }

  static calcDefaultPoleMaterial(sunSailType: SunSailType, featureLevel: FeatureLevel): MaterialType {
    if (SST.is(sunSailType, SST.Sandy))
      return "Iron48";
    if (FL.le(featureLevel, "user"))
      return "Telescope";
    return sunSailType === SunSailType.Concept ? "Droppole" : "Alu"
  }

  static getPoleMaterial(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): MaterialType {
    const pole = Poles.getPole(sunSail, poleId);
    const material = pole?.material;
    if (material)
      return material;

    const pd = PD.typedProduct(sunSail, "sunSail");
    const defaultPoleMaterial = SunSailLogic.calcDefaultPoleMaterial(pd.sunSailType, FL.fromProduct(sunSail));
    return defaultPoleMaterial;
  }

  static getPoleMaterialStrict(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId): MaterialType | undefined {
    if (SunSailLogic.getPoleType(sunSail, poleId) === PoleType.Pole)
      return SunSailLogic.getPoleMaterial(sunSail, poleId);
    return undefined;
  }

  static getPointMaterial(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId): MaterialType | undefined {
    const pole = Poles.getPole(sunSail, poleId);
    if (pole.poleType !== PoleType.Pole)
      return undefined;
    return SunSailLogic.getPoleMaterial(sunSail, poleId);
  }


  static materialGroupFromMaterial(material: MaterialType | undefined, poleType: PoleType | undefined): MaterialGroup | undefined {
    if (poleType === PoleType.Wall)
      return "wall";
    if (poleType === PoleType.Special)
      return "special";
    if (!material)
      return undefined;
    return materialInfos[material]?.group;
  }

  static getPoleMaterialGroup(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): MaterialGroup | undefined {
    const poleType = SunSailLogic.getPoleType(sunSail, poleId);
    const material = SunSailLogic.getPoleMaterial(sunSail, poleId);
    return SunSailLogic.materialGroupFromMaterial(material, poleType);
  }

  static getSailMount(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId): SailMount | undefined {
    return getChecked({
      selection: Poles.getPole(sunSail, poleId).sailMount,
      validItems: SunSailLogic.getPossibleSailMounts(sunSail, poleId),
      emptySelection: "__selectionRequired__"
    });
  }

  static calcSailMountRopeLength(sunSail: ISunSailData, poleId: PoleId): number {
    const length = Poles.getPole(sunSail, poleId).sailRope?.length;
    if (length !== undefined)
      return length;
    return Math.ceil(SunSailLogic.getPoleLength(sunSail, poleId) * 2 + SunSailLogic.calcDistanceIn(sunSail, poleId));
  }

  static getSailRopeFixType(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId): "indirect" | "direct" | false {
    const sailMount = SunSailLogic.getSailMount(sunSail, poleId);
    switch (sailMount) {
      case "snap": return "indirect";
      case "rope": {
        const article = SunSailLogic.getSailMountArticle(sunSail, poleId)
        return article !== "00E13000024" ? "indirect" : "direct";
      }
    }

    return false;
  }

  static getValidSailMountRopeArticles(sunSail: ISunSailData, poleId: PoleId): string[] {
    if (!SunSailLogic.getSailRopeFixType(sunSail, poleId))
      return [];

    const ropes = RopeHelper.getRopeArticles(["6.5", "8"]);
    return ropes.map((r) => r.id)
  }

  static getSailRopeArticle(sunSail: ISunSailData, poleId: PoleId): string | undefined {
    return getChecked({
      selection: Poles.getPole(sunSail, poleId).sailRope?.articleId,
      validItems: SunSailLogic.getValidSailMountRopeArticles(sunSail, poleId),
      emptySelection: "__selectionRequired__"
    })
  }

  static #internedSailMounts = {
    empty: [],
    snap: ["snap"],
    snapRope: ["snap", "rope"],
    snapRopeDirect: ["snap", "rope", "direct"],
    snapFloat: ["snap", "float"],
  } satisfies Record<string, SailMount[]>;
  static getPossibleSailMounts(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId): SailMount[] {
    const sunSailType = PD.productToSunSailType(sunSail)

    if (!SunSailLogic.isPoleWall(sunSail, poleId))
      return SunSailLogic.#internedSailMounts.empty;

    if (SST.is(sunSailType, SST.CoSF)) {
      const heightChange = SunSailLogic.getHeightChangeArticle(sunSail, poleId);
      if (heightChange) {
        return SunSailLogic.#internedSailMounts.snapRope;
      } else {
        return SunSailLogic.#internedSailMounts.snapRopeDirect;
      }
    } else if (SST.is(sunSailType, SST.Flex)) {
      if (poleId === PoleIndex.A)
        return SunSailLogic.#internedSailMounts.empty
      else if (poleId === PoleIndex.C) {
        if (SunSailLogic.getHeightChangeArticle(sunSail, poleId))
          return SunSailLogic.#internedSailMounts.snap
        else
          // return SunSailLogic.#internedSailMounts.snapFloat
          return SunSailLogic.#internedSailMounts.snap;
      }
      return SunSailLogic.#internedSailMounts.snapRope;
    } else {
      return SunSailLogic.#internedSailMounts.empty;
    }
  }

  static calcSailMountRopeArticles(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId): string[] {
    const heightChange = SunSailLogic.getHeightChangeArticle(sunSail, poleId);
    const materialGroup = SunSailLogic.getPoleMaterialGroup(sunSail, poleId);
    const sicherheitKlemme = materialGroup ? sicherheitsKlemmenMap[materialGroup] : "";
    const seilKlemme = materialGroup ? seilKlemmeMap[materialGroup] : "";
    const sunSailType = PD.productToSunSailType(sunSail);

    const articles: string[] = [
      sicherheitKlemme, // Autoreleaseklemme
      seilKlemme, // Seilklemme mit seitlichem Eingang]
      // "00E13000057"
    ]
    const backenKlemme = SunSailLogic.getBackenklemmenSet(sunSail, poleId);
    if (backenKlemme)
      articles.push(backenKlemme);
    if (!heightChange && !SST.is(sunSailType, SST.Flex)) {
      articles.push("00E13000024") // Profiseilspanner;
      // "60002A006", // Karabiner
    }
    return articles;
  }

  static calcSailMountDirectArticles(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId): string[] {
    const poleType = SunSailLogic.getPoleType(sunSail, poleId);
    const articles = []
    articles.push("60002A006"); // Karabinerhaken 70mm
    articles.push("00E13000801"); // Spanngurt mit Schäkel
    articles.push("00E13000129"); // Gurtbandratsche
    articles.push("00E13000130"); // Gewindespanner M10
    if (poleType === PoleType.Pole) {
      articles.push("00E13000126"); // Gewindespanner M8
    }
    return articles;
  }

  static calcFallstopArticles(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId): string[] {
    const materialGroup = SunSailLogic.getPoleMaterialGroup(sunSail, poleId);
    if (!materialGroup)
      return [];
    const articles = calcFallstoppArticles(materialGroup)
    return [articles.default, articles.lock].filter((f) => !!f);
  }

  static getPossibleSailMountArticles(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId): string[] {
    const sailMount = SunSailLogic.getSailMount(sunSail, poleId);
    switch (sailMount) {
      case "snap": return SunSailLogic.calcFallstopArticles(sunSail, poleId);
      case "rope": return SunSailLogic.calcSailMountRopeArticles(sunSail, poleId);
      case "direct": return SunSailLogic.calcSailMountDirectArticles(sunSail, poleId)
    }
    return [];
  }

  static #internedSailPulleyArticles = {
    empty: [],
    all: [
      "00E13001151", //  Schwerlastrolle 10
      "00E13000042", // Professionelle Seilrolle 8
      "00E13001482", // Professionelle Seilrolle 10
    ]
  } satisfies Record<string, string[]>
  static getPossibleSailPulleyArticles(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId): string[] {
    const heightChange = SunSailLogic.getHeightChangeArticle(sunSail, poleId);
    if (heightChange)
      return SunSailLogic.#internedSailPulleyArticles.empty;
    if (SunSailLogic.getSailRopeFixType(sunSail, poleId) !== "indirect")
      return SunSailLogic.#internedSailPulleyArticles.empty;

    return SunSailLogic.#internedSailPulleyArticles.all;
  }

  static #calcPossiblePoleAnglesfromMountArticles(articleIds: PoleMountArticle[], withZero: boolean): number[] {
    const possibleAngles: number[] = [];
    for (let i = 0; i < articleIds.length; i++) {
      const articleNumber = articleIds[i]
      const mountAngle = getPoleMountArticleInfo(articleNumber)?.mountAngle;
      if (mountAngle !== undefined)
        possibleAngles.push(mountAngle);
    }
    if (withZero && !possibleAngles.includes(0))
      return [0, ...possibleAngles];
    return possibleAngles;
  }

  static calcAllowedPoleAnglesFromPossibleMountArticles(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): number[] {
    if (SunSailLogic.getPoleType(sunSail, poleId) !== PoleType.Pole) return [];
    const mount = SunSailLogic.getPoleMount(sunSail, poleId);

    let articleIds: PoleMountArticle[] = [];
    if (mount === PoleMountType.GroundPlate) {
      articleIds = SunSailLogic.calcPossibleGroundPlateArticleIds(sunSail, poleId);
    } else if (mount === PoleMountType.GroundSleeve) {
      articleIds = SunSailLogic.calcPossibleGroundSleeveArticleIds(sunSail, poleId);
    }
    return SunSailLogic.#calcPossiblePoleAnglesfromMountArticles(articleIds, mount === PoleMountType.GroundSleeve);
  }

  static getAllowedPoleAngles(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): IMinMax | number[] | number {
    if (SunSailLogic.getPoleType(sunSail, poleId) !== PoleType.Pole) return 0;
    const mount = SunSailLogic.getPoleMount(sunSail, poleId);
    if (mount === PoleMountType.Special) return { min: 0, max: 50 }

    const possibleAngles = SunSailLogic.calcAllowedPoleAnglesFromPossibleMountArticles(sunSail, poleId);
    if (possibleAngles.length > 0)
      return possibleAngles;

    if (SST.is(PD.productToSunSailType(sunSail), SST.Sandy)) {
      return { min: 0, max: 5 }
    }

    const material = SunSailLogic.getPoleMaterial(sunSail, poleId);
    if (PoleLogic.isDroppole(material)) {
      switch (mount) {
        case "None": return { min: 0, max: 50 };
        // case "GroundPlate": return [2, 10];
        case "EarthScrew": return { min: 0, max: 40 };
        case "ConcreteCube": return 5
        case "BaseCube": return [0, 10];
      }
    } else if (PoleLogic.isSteel(material)) {
      switch (mount) {
        case "None": return SunSailLogic.hasPoleLengthB(sunSail, poleId) ? { min: 0, max: 50 } : { min: 0, max: 40 };
        case "GroundPlate": return { min: 0, max: 40 };
        case "Clamp": return SunSailLogic.hasPoleLengthB(sunSail, poleId) ? { min: 5, max: 40 } : { min: 0, max: 40 };
        case "EarthScrew": return { min: 0, max: 40 };
        case "GroundSleeve": return 10;
        case "ConcreteCube": return 5
        case "BaseCube": return SunSailLogic.hasPoleLengthB(sunSail, poleId) ? { min: 0, max: 40 } : 0;
      }
    } else if (PoleLogic.isAlu(material)) {
      switch (mount) {
        case "None": return SunSailLogic.hasPoleLengthB(sunSail, poleId) ? { min: 1, max: 20 } : { min: 0, max: 20 };
        case "Clamp": return SunSailLogic.hasPoleLengthB(sunSail, poleId) ? { min: 5, max: 20 } : 0;
        case "EarthScrew": return { min: 0, max: 40 };
        case "GroundSleeve": return 10;
        case "ConcreteCube": return 5;
        case "BaseCube": return SunSailLogic.hasPoleLengthB(sunSail, poleId) ? { min: 0, max: 20 } : 0;
      }
    } else if (PoleLogic.isMaterial(material, "iron48")) {
      switch (mount) {
        case "None": return { min: 0, max: 50 };
        case "EarthScrew": return { min: 0, max: 50 };
        case "GroundSleeve": return [0, 5];
        case "ConcreteCube": return { min: 0, max: 50 };
        case "BaseCube": return SunSailLogic.hasPoleLengthB(sunSail, poleId) ? { min: 0, max: 50 } : 0;
      }
    } else if (PoleLogic.isIron(material)) {
      switch (mount) {
        case "None": return { min: 0, max: 50 };
        case "EarthScrew": return { min: 0, max: 50 };
        case "GroundSleeve": return { min: 0, max: 50 };
        case "ConcreteCube": return { min: 0, max: 50 };
        case "BaseCube": return SunSailLogic.hasPoleLengthB(sunSail, poleId) ? { min: 0, max: 50 } : 0;
      }
    } else if (PoleLogic.isTeleProfil(material)) {
      switch (mount) {
        case "EarthScrew": return { min: 0, max: 50 }; // https://plaspack.atlassian.net/browse/SS-45
        case "GroundSleeve": return 10; // https://plaspack.atlassian.net/browse/SS-45
      }
    }
    return 0;
  }

  static calcPossibleGroundPlateArticleIds(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: number, strict: boolean = true): PoleMountArticle[] {
    if (strict && SunSailLogic.getPoleMount(sunSail, poleId) !== PoleMountType.GroundPlate)
      return [];

    const sunSailType = PD.productToSunSailType(sunSail);

    const poleMaterial = SunSailLogic.getPoleMaterial(sunSail, poleId);
    switch (poleMaterial) {
      case "Alu_Black": return SST.is(sunSailType, SST.Sandy) ? ["00E13001661", "00E13000501"] : ["00E13001661", "00E13001653"]; // 0 Grad, 10 Grad
      case "Alu": return SST.is(sunSailType, SST.Sandy) ? ["00E13001606", "00E13000501"] : ["00E13001606", "00E13001604"]; // 0 Grad, 10 Grad
      case "Steel_V4A": return ["00E13001782"];
      case "Steel": return ["00E13000102"];
      case "Iron89_Black": return ["00E13001660"];
      case "Iron89": return ["00E13001659"];
    }

    if (PoleLogic.isTeleProfil(poleMaterial)) return ["00E13001649"]

    if (PoleLogic.isDroppole(poleMaterial)) {
      return ["00E13001872", "00E13001870"] // " 2 Grad, 10 Grad"
    }

    return [];
  }

  static getPoleMount(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): PoleMountType {
    const sunSailType = PD.productToSunSailType(sunSail);
    if (SunSailLogic.getPoleType(sunSail, poleId) === PoleType.Pole) {
      const validItems = SunSailLogic.getValidPoleMountTypes(sunSail, poleId);
      return getChecked({
        selection: Poles.getPole(sunSail, poleId).mount,
        validItems,
        emptySelection: validItems.includes(SunSailLogic.calcInitialPoleMount(sunSailType)) ? SunSailLogic.calcInitialPoleMount(sunSailType) : validItems[0]
      }) || PoleMountType.None;
    }
    return PoleMountType.None;
  }

  // static getPoleAngle(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId): number {
  //   if (Poles.getPolePositionType(poleId) === "center")
  //     return 0;

  //   if (SunSailLogic.getPoleType(sunSail, poleId) !== PoleType.Pole)
  //     return 0;
  //   const sunSailType = PD.productToSunSailType(sunSail);

  //   const poleMaterial = SunSailLogic.getPoleMaterial(sunSail, poleId);
  //   switch (poleMaterial) {
  //     case "Alu_Black": return SST.is(sunSailType, SST.Sandy) ? ["00E13001661", "00E13000501"] : ["00E13001661", "00E13001653"]; // 0 Grad, 10 Grad
  //     case "Alu": return SST.is(sunSailType, SST.Sandy) ? ["00E13001606", "00E13000501"] : ["00E13001606", "00E13001604"]; // 0 Grad, 10 Grad
  //     case "Steel_V4A": return ["00E13001782"];
  //     case "Steel": return ["00E13000102"];
  //     case "Iron89_Black": return ["00E13001660"];
  //     case "Iron89": return ["00E13001659"];
  //   }

  //   if (PoleLogic.isTeleProfil(poleMaterial)) return ["00E13001649"]

  //   if (PoleLogic.isDroppole(poleMaterial)) {
  //     return ["00E13001872", "00E13001870"] // " 2 Grad, 10 Grad"
  //   }

  //   return [];
  // }

  static calcPossibleGroundSleeveArticleIds(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: number, strict: boolean = true): PoleMountArticle[] {
    if (strict) {
      const poleMount = SunSailLogic.getPoleMount(sunSail, poleId);
      if (poleMount !== PoleMountType.GroundSleeve)
        return [];
    }

    const sunSailType = PD.productToSunSailType(sunSail);


    const materialGroup = SunSailLogic.getPoleMaterialGroup(sunSail, poleId);
    switch (materialGroup) {
      case "steel": return ["00E13000144"];
      case "alu": return SST.is(sunSailType, SST.Sandy) ? ["00E13001970"] : ["00E13000244"];
      case "profile": return ["00E13000244"];
      case "telescope": return ["00E13000244"];
      case "iron48": return ["60004A001"];
    }
    return [];
  }

  static getGroundSleeveArticle(sunSail: ISunSailData, poleId: PoleId): string {
    const possibleGroundSleeves = SunSailLogic.calcPossibleGroundSleeveArticleIds(sunSail, poleId);
    if (possibleGroundSleeves.length < 2)
      return possibleGroundSleeves[0] || "";
    const angleDeg = MathUtil.rad2deg(SunSailLogic.getPoleAngle(sunSail, poleId));

    if (angleDeg < 0.01)
      return possibleGroundSleeves[0] || "";

    let smallestIndex = -1;
    let smallestDiff = Number.MAX_VALUE;
    for (let i = 0; i < possibleGroundSleeves.length; i++) {
      const angle = getPoleMountArticleInfo(possibleGroundSleeves[i])?.mountAngle || 0
      const diff = (angle - angleDeg) ** 2;
      if (diff < smallestDiff) {
        smallestIndex = i;
        smallestDiff = diff;
      }
    }
    return possibleGroundSleeves[smallestIndex] || ""
  }

  static calcGroundPlateArticleId(sunSail: ISunSailData, poleId: number): PoleMountArticle {
    const possibleGroundPlates = SunSailLogic.calcPossibleGroundPlateArticleIds(sunSail, poleId);
    if (possibleGroundPlates.length < 2)
      return possibleGroundPlates[0] || "";
    const angleDeg = MathUtil.rad2deg(SunSailLogic.getPoleAngle(sunSail, poleId));

    let smallestIndex = -1;
    let smallestDiff = Number.MAX_VALUE;
    for (let i = 0; i < possibleGroundPlates.length; i++) {
      const angle = getPoleMountArticleInfo(possibleGroundPlates[i])?.mountAngle || 0
      const diff = (angle - angleDeg) ** 2;
      if (diff < smallestDiff) {
        smallestIndex = i;
        smallestDiff = diff;
      }
    }
    return possibleGroundPlates[smallestIndex] || ""
  }

  static _getPoleAngle(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): number {
    if (Poles.getPolePositionType(poleId) === "center")
      return 0;

    if (SunSailLogic.getPoleType(sunSail, poleId) !== PoleType.Pole)
      return 0;
    const calcAllowedAngles = SunSailLogic.getAllowedPoleAngles(sunSail, poleId)
    const angleMinMax = MinMax.transform(PoleLogic.getMinMaxOrArray(calcAllowedAngles), MathUtil.deg2rad);
    const setAngle = Poles.getPole(sunSail, poleId).angle || 0;
    return MinMax.clamp(setAngle, angleMinMax)
  }

  static getPoleAngle = CacheUtil.memo(SunSailLogic._getPoleAngle, "_getPoleAngle", {
    idsFromArgs: (sunSail, poleId) => [sunSail.id, poleId],
    inputsEqualFn: "ref",
    resultEqualFn: "ref",
  });


  static _findNearestIndex(value: number, values: number[]): number {
    let nearestIndex = 0;
    let nearestValue = Number.MAX_VALUE;
    for (let i = 0; i < values.length; i++) {
      const delta = (values[i] - value) ** 2;
      if (delta < nearestValue) {
        nearestIndex = i;
        nearestValue = delta;
      }
    }
    return nearestIndex;
  }

  static getPoleLength(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): number {
    const storedPoleLength = Poles.getPole(sunSail, poleId).length ?? 3; // Shop
    const material = SunSailLogic.getPoleMaterial(sunSail, poleId);
    if (PoleLogic.isProfil(material)) {
      const index = SunSailLogic._findNearestIndex(storedPoleLength, possibleProfilePoleLengths);
      return possibleProfilePoleLengths[index];
    } else {
      if (Product.isProductSC(sunSail)) {
        return 3;
      }
      const lengthMinMax = SunSailLogic.calcLengthMinMax(sunSail, poleId);
      const length = MinMax.clamp(storedPoleLength, lengthMinMax);
      return length;
    }
  }

  static getPoleLengthBEnabled(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): boolean {
    return !!Poles.getPole(sunSail, poleId).lengthB?.enabled;
  }

  static hasPoleLengthB(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): boolean {
    const enabled = SunSailLogic.getPoleLengthBEnabled(sunSail, poleId);
    if (!enabled)
      return false;

    if (!Poles.getPole(sunSail, poleId).lengthB)
      return false;
    return true;
  }

  static getPoleLengthB(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): number {
    const enabled = SunSailLogic.getPoleLengthBEnabled(sunSail, poleId);
    if (!enabled)
      return 0;

    const minMax = SunSailLogic.calcLengthBMinMax(sunSail, poleId)

    const lengthBVal = Poles.getPole(sunSail, poleId).lengthB;
    const lengthB = typeof lengthBVal === "number" ? lengthBVal : (lengthBVal?.val || 0);
    return MathUtil.clamp(lengthB, minMax.min, minMax.max);
  }

  static getPoleLengthA(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId): number {
    if (SunSailLogic.isPoleBent(sunSail, poleId)) {
      return SunSailLogic.getPoleLength(sunSail, poleId) - SunSailLogic.getPoleLengthB(sunSail, poleId);
    }
    return SunSailLogic.getPoleLength(sunSail, poleId);
  }

  static isPoleBent(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): boolean {
    const poleAngle = SunSailLogic.getPoleAngle(sunSail, poleId)
    const hasPoleLengthB = SunSailLogic.hasPoleLengthB(sunSail, poleId);
    return poleAngle > 0.001 && hasPoleLengthB;
  }

  static calcPossiblePylons(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): (PylonType | undefined)[] {
    const sunSailType = PD.productToSunSailType(sunSail);
    if (!PoleLogic.isPylonValid(sunSailType, poleId, SunSailLogic.getPoleType(sunSail, poleId)))
      return []
    const required = PoleLogic.isPylonRequired(sunSailType, poleId, SunSailLogic.getPoleType(sunSail, poleId));
    const selections: (PylonType | undefined)[] = [];
    if (!required)
      selections.push(undefined);
    selections.push(...pylonTypes);
    return selections;
  }

  static getPolePylonType(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): PylonType | undefined {
    return Poles.getPole(sunSail, poleId).pylonType;
  }

  static getInfluencingIndirectPylonType(sunSail: ISunSailData, poleId: PoleId): PylonType | undefined {
    if (IndirectPylonLogic.isIndirectPylonPossible(sunSail.sunSailType)) {
      if (PoleLogic.isAxle(sunSail.sunSailType, poleId)) {
        return undefined;
      } else {
        const indirectPylons = IndirectPylonLogic.getIndirectPylons(sunSail);
        for (const ip of indirectPylons) {
          if (ip.indirectPylon === poleId) {
            return SunSailLogic.getPolePylonType(sunSail, ip.axlePylon);
          }
        }
      }

    }
    return SunSailLogic.getPolePylonType(sunSail, poleId)
  }

  static isPoleWall(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId): boolean {
    const poleType = SunSailLogic.getPoleType(sunSail, poleId);
    return poleType === PoleType.Wall || poleType === PoleType.Pole;
  }

  static getCoilUpSizeInfo(articleNumber: string | undefined): ICoilUpSizeInfo {
    const isSnapWinch = SunSailLogic.isSnapWinch(articleNumber);
    if (isSnapWinch)
      articleNumber = "00E13001367";
    const winchInfo = winchInfos[articleNumber || ""] || winchInfos[seilStoppWinchArticleId];
    return winchInfo;
  }

  static getBackenklemmenSet(sunSail: ISunSailData | ISCSunSailData, poleId: PoleId): string {
    const poleType = SunSailLogic.getPoleType(sunSail, poleId);
    if (poleType === PoleType.Wall)
      return backenklemmeLoseArticleId
    else if (poleType === PoleType.Pole) {
      const materialGroup = SunSailLogic.getPoleMaterialGroup(sunSail, poleId);
      switch (materialGroup) {
        case "alu": return "00E13001905";
        case "steel": return "00E13001906";
        case "droppole": return "00E13002092";
        case "telescope":
        case "profile":
          return "";
      }
      return backenklemmeLoseArticleId
    }

    return "";
  }

  static getCloth(sunSail: ISunSailData | ISail2dForm | ISCSunSailData): IClothData | undefined {
    // return ClothDataLogic.getCloth(sunSail.cloth, PD.productToPD(sunSail, false), FL.fromProduct(sunSail));
    return sunSail.cloth;
  }

  static calcInitialMountAngle(poleId: PoleId, mount: PoleMountType, material: MaterialType): { value: number, override: boolean } {
    if (Poles.fromPoleId(poleId).polePositionType === "center")
      return { value: 0, override: true }

    if (mount === PoleMountType.Clamp)
      return { value: MathUtil.deg2rad(0), override: true };
    if (mount === PoleMountType.GroundPlate)
      return { value: MathUtil.deg2rad(10), override: false };
    if (mount === PoleMountType.BaseCube && PoleLogic.isDroppole(material))
      return { value: MathUtil.deg2rad(10), override: true }
    return { value: MathUtil.deg2rad(0), override: false }
  }

  static calcNewInitialPoleAngle(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): number {
    const poleMount = SunSailLogic.getPoleMount(sunSail, poleId);
    const poleMaterial = SunSailLogic.getPoleMaterial(sunSail, poleId);
    const newValue = SunSailLogic.calcInitialMountAngle(poleId, poleMount, poleMaterial);
    if (newValue.override)
      return newValue.value;
    return Poles.getPole(sunSail, poleId).angle || 0;
  }

  static calcInitialPoleMount(sunSailType: SunSailType): PoleMountType {
    if (SST.is(sunSailType, SST.Sandy))
      return PoleMountType.GroundSleeve;
    return PoleMountType.GroundPlate
  }

  static hasChewplate(sunSailType: SunSailType, poleCount: number): boolean {
    return sunSailType === SunSailType.SolidayC || (sunSailType === SunSailType.SolidayCS && poleCount === 3) || (sunSailType === SunSailType.SolidayOne); // todo: Soliday-ONE: hängt von Auszug angleichen ab?
  }

  static hasSnapShackle(sunSailType: SunSailType, poleCount: number, polyPositioning: boolean, poleId?: PoleId): boolean {
    if (poleId !== undefined && PoleLogic.isAxle(sunSailType, poleId))
      return false;
    if (polyPositioning)
      return false;
    return (sunSailType === SunSailType.SolidayC || sunSailType === SunSailType.SolidayCS) && poleCount === 4;
  }

  static hasCSRopeRoll(sunSailType: SunSailType, _poleCount: number, poleId?: PoleId): boolean {
    if (poleId !== undefined && PoleLogic.isAxle(sunSailType, poleId))
      return false;
    return SST.is(sunSailType, SST.CsXsTwin);
  }

  static getEdgePoleIdsDirected(edgeName: EdgeName, sunSailType: SunSailType, poleCount: number): PoleId[] {
    if (SunSailLogic.getEdgeDirectionMul(edgeName, sunSailType, poleCount) < 0) {
      return [
        getPoleId(edgeName[1].toUpperCase() as PointName),
        getPoleId(edgeName[0].toUpperCase() as PointName),
      ]
    } else {
      return [
        getPoleId(edgeName[0].toUpperCase() as PointName),
        getPoleId(edgeName[1].toUpperCase() as PointName),
      ]
    }
  }

  static getDefaultHighestPoint(sunSailType: SunSailType, _poleCount: number): PoleId | undefined {
    switch (sunSailType) {
      case SunSailType.SolidayA: return PoleIndex.B;
      case SunSailType.SolidayM: return PoleIndex.B; // https://plaspack.atlassian.net/browse/APPSK-193
      case SunSailType.SolidayOne: return PoleIndex.B;
      // case SunSailType.SolidayFlex: return PoleIndex.B; // https://plaspack.atlassian.net/browse/APP-1592?linkSource=email
      case SunSailType.SolidayC: return PoleIndex.B;
      case SunSailType.SolidayX: return PoleIndex.B;
    }
    return undefined;
  }

  public static isHighestPointRequired(sunSailType: SunSailType, poleCount: number): boolean {
    if (poleCount <= 3)
      return false;
    return SunSailLogic.getDefaultHighestPoint(sunSailType, poleCount) !== undefined;
  }

  static calcHighestNonAxlePoint(ssd: ISunSailData): PoleId | undefined {
    const hp = ssd.form?.highestPoint || (ssd.form as any)?.heighestPoint;
    if (hp)
      return hp;

    if (Poles.getCount(ssd) >= 4) {
      switch (ssd.sunSailType) {
        case SunSailType.SolidayOne:
        case SunSailType.SolidayC: {
          const mountPositions = SunSailLogic.calcMountPositions(ssd, 0, undefined);
          const point = mountPositions[PoleIndex.B].y >= mountPositions[PoleIndex.D].y ? PoleIndex.B : PoleIndex.D;
          return point;
        }
      }
    }
    const poleId = SunSailLogic.getDefaultHighestPoint(ssd.sunSailType, Poles.getCount(ssd));
    return poleId;
  }

  static getHighestPointName(sunSailData: ISunSailData): PointName | undefined {
    const poleIndex = SunSailLogic.calcHighestNonAxlePoint(sunSailData);
    if (poleIndex === undefined)
      return undefined;
    return getPointName(poleIndex);
  }

  static getPossibleOperatingDeflectionPositions(_sunSailType: SunSailType, _poleCount: number): PoleIndex[] {
    return [PoleIndex.B, PoleIndex.D]
  }
  static hasOperatingDeflectionPosition(sunSailType: SunSailType, poleCount: number): boolean {
    return SST.is(sunSailType, SST.One) && poleCount === 4;
  }

  static getOperatingDeflectionPosition(ssd: ISunSailData | ISCSunSailData | ISail2dForm): PoleIndex | undefined {
    if (Product.isProductSC(ssd))
      throw new Error("getOperatingDeflectionPosition not supported for scData so far...")

    const sunSailType = PD.productToSunSailType(ssd)

    if (!SunSailLogic.hasOperatingDeflectionPosition(sunSailType, Poles.getCount(ssd)))
      return undefined;

    return ssd.coilUpDeflectionPosition || SunSailLogic.getDefaultCoilUpDeflectionPosition();
  }

  static getDefaultCoilUpDeflectionPosition(): PoleIndex {
    return PoleIndex.D;
  }

  static getCoilUpPoleIndex(): PoleIndex {
    return PoleIndex.A;
  }

  static _calcOnePulleySystemDistance(sunSail: ISunSailData): number {
    const poleIndex = SunSailLogic.getCoilUpPoleIndex();
    const fixHeight = SunSailLogic.getRopeFixHeight(sunSail, poleIndex);
    const topY = SunSailLogic.calcTopMountY(sunSail, poleIndex);
    const bottomAdd = SunSailLogic.calcBottomPoleExtension(sunSail, poleIndex);
    const poleLength = SunSailLogic.getPoleLength(sunSail, poleIndex);
    const l = poleLength - topY - bottomAdd - fixHeight;
    return l;
  }

  static _calcOneOperatinRopeLengthAuto(sunSail: ISunSailData): number {
    const l = SunSailLogic._calcOnePulleySystemDistance(sunSail);
    return Math.max(Math.ceil(l * 4) / 2 + 1, 3); // https://plaspack.atlassian.net/browse/API-28
  }

  static calcPossibleOperatingRopes(sunSail: ISunSailData): string[] {
    if (sunSail.sunSailType !== SunSailType.SolidayOne)
      return [];
    const ropes = RopeHelper.getRopeArticles(["8"]).map((r) => r.id)
    return ropes;
  }

  static getOneOperatingRope(sunSail: ISunSailData): string | undefined {
    const validItems = SunSailLogic.calcPossibleOperatingRopes(sunSail);
    const defaultRope = RopeHelper.findRope(validItems, { ropeType: "8", colorId: "black" });
    return getChecked({
      selection: sunSail.operatingRope?.articleId,
      validItems,
      emptySelection: defaultRope || "__selectionRequired__"
    });
  }

  static isHeightChangeValid(sunSail: ISunSailData | ISCSunSailData | ISail2dForm, poleId: PoleId): boolean {
    const pid = Poles.fromPoleId(poleId);
    if (pid.polePositionType === "center")
      return false;

    const poleType = SunSailLogic.getPoleType(sunSail, poleId);
    if (poleType !== PoleType.Pole && poleType !== PoleType.Wall) // SpecialConsole?
      return false;

    const sunSailType = PD.productToSunSailType(sunSail);
    if (SST.is(sunSailType, SST.Sandy))
      return true;

    const materialGroup = SunSailLogic.getPoleMaterialGroup(sunSail, poleId);
    if (!["steel", "alu", "droppole", "wall"].includes(materialGroup || "none"))
      return false;

    if (SST.is(sunSailType, SST.Flex)) {
      if (poleId === PoleIndex.A)
        return false;
      if (poleId === PoleIndex.C)
        return true;
      return true;
    }


    if (PoleLogic.isAxle(sunSailType, poleId)) {
      return false;
    }

    if (SST.is(sunSailType, SST.CoSF)) {
      return true;
    } else if (SST.is(sunSailType, SST.C, SST.CsTwin)) {
      return !!SunSailLogic.getPolePylonType(sunSail, poleId);
    } else if (SST.is(sunSailType, SST.One)) {
      return SunSailLogic.getOperatingDeflectionPosition(sunSail) !== poleId;
    } else if (SST.is(sunSailType, SST.AM, SST.XXs))
      return true;
    return false;
  }

  static calcFlexClampCount(sunSail: ISunSailData): number {
    const logicalAxleLength = SunSailLogic.calcAxleLength(sunSail, "logical");
    const clampCount = Math.ceil((logicalAxleLength - 0.2 * 2) / 1);
    return clampCount;
  }

  static hasDistInSlider(sunSailType: SunSailType | undefined, poleId: PoleId): boolean {
    if (SST.is(sunSailType, SST.Sandy))
      return false;
    if (sunSailType === SunSailType.SolidayFlex && PoleLogic.isAxle(sunSailType, poleId))
      return false;
    if (Poles.getPolePositionType(poleId) !== "corner")
      return false;
    return true;
  }

  static getSystemPackingArticle(sunSailType: SunSailType, version: ProductVersion | undefined): string {
    switch (sunSailType) {
      case SunSailType.SolidayOne: return "E9900001";
      case SunSailType.SolidayFlex: return "E9900002";
      case SunSailType.SolidayM: return Pvi.eq(version, "2024") ? "E9900003" : "";
      case SunSailType.SolidayA: return Pvi.eq(version, "2024") ? "E9900004" : "";
    }
    return "";
  }

  static getThreadedPoleIndex(sunSail: ISunSailData | ISCSunSailData): PoleIndex | undefined {
    const sunSailType = PD.productToSunSailType(sunSail);
    if (sunSailType === SunSailType.SolidayM) {
      if (Pvi.is(sunSail.version, "2024")) {
        if (SunSailLogic.getPoleType(sunSail, PoleIndex.A) !== PoleType.Pole)
          return PoleIndex.C;
        return PoleIndex.A;
      }
      return PoleIndex.A
    }
    return undefined;
  }

  static calcFormFinderAlignMode(ssd: ISunSailData): "Keine" | "Manuell" | "Automatisch" {
    const sunSailType = ssd.sunSailType;
    if (SST.is(sunSailType, SST.AMXOne))
      return "Manuell";

    if (AxleLogic.hasAxle(sunSailType) && !MotorLogic.hasMotor(sunSailType)) // https://plaspack.atlassian.net/browse/SA-158
      return "Automatisch";

    if (ssd.poles.length <= 3)
      return "Keine";

    try {
      const outB = SunSailLogic.calcDistanceOut(ssd, PoleIndex.B);
      const outD = SunSailLogic.calcDistanceOut(ssd, PoleIndex.D);
      const inB = SunSailLogic.calcDistanceIn(ssd, PoleIndex.B);
      const inD = SunSailLogic.calcDistanceIn(ssd, PoleIndex.D);

      const minB = SunSailLogic.getMinAllowedDistIn(ssd, PoleIndex.B)
      const minD = SunSailLogic.getMinAllowedDistIn(ssd, PoleIndex.D)

      const auszugAngleichen = Math.abs(outB - outD) < 0.001;
      const greaterB = inB >= minB + 0.00099;
      const greaterD = inD >= minD + 0.00099;
      const greaterCount = (greaterB ? 1 : 0) + (greaterD ? 1 : 0);
      const defaultValues = greaterCount === 0 || (greaterCount === 1 && auszugAngleichen);
      if (auszugAngleichen && defaultValues) return "Automatisch";
      if (auszugAngleichen || !defaultValues) return "Manuell";
    } catch (e) {
      Diagnostics.log(e, "calcAlignMode");
    }
    return "Keine";
  }

  static calcPoleShoppingArticle<T extends { affiliation?: IArticleAffiliation, articleNumber: string }>(shoppingList: T[], poleId: PoleId): T | undefined {
    for (const sl of shoppingList) {
      if (sl?.affiliation?.poleId === poleId && isPoleArticleId(sl.articleNumber)) {
        return sl;
      }
    }
    return undefined;
  }

  static getWaterDirection(sunSail: ISunSailData, groupIndex: number): WaterDirection {
    const waterDirection = sunSail.waterDirections?.[groupIndex];
    return waterDirection || "-"
  }

  static calcPossibleRainDrainPositions(pd: IProductDescriptor): (ISCSunSailData["rainDrainPosition"] | undefined)[] {
    if (PD.isSail(pd)) {
      const possibleValues: ISCSunSailData["rainDrainPosition"][] = [undefined];
      for (let i = 0; i < pd.poleCount; i++)
        possibleValues.push(getPointName(i));
      return possibleValues;
    }
    return [];
  }

  static getDroppoleHeightMode(sunSail: ISunSailData, poleId: PoleId): DroppoleLengthMode {
    const poleLength = SunSailLogic.getPoleLength(sunSail, poleId);
    if (SunSailLogic.isDroppoleCut(poleLength))
      return DroppoleLengthMode.Cut;
    return DroppoleLengthMode.Default;
  }

  static positionModes: PositionMode[] = ["mount", "net"]; // https://plaspack.atlassian.net/browse/SS-65
  static getPossiblePositionModes(): PositionMode[] {
    return SunSailLogic.positionModes;
  }

  static getPositionMode(sunSail: ISunSailData | ISCSunSailData | ISail2dForm): PositionMode {
    // const selection = (Product.isProduct3d(sunSail) ? "mount" : sunSail.positionMode) || "mount";
    const selection = sunSail.positionMode ?? "mount";
    return getChecked({
      selection,
      validItems: SunSailLogic.getPossiblePositionModes(),
      emptySelection: "__selectionRequired__"
    })!;
  }

  static getPolePos(sunSail: ISunSailData, poleId: PoleId, options?: { offset?: number }): IVec3 {
    if (options?.offset) {
      poleId = Poles.calcPoleIdWithOffset(poleId, options.offset, sunSail);
    }
    // if (Poles.getPolePositionType(poleId) === "corner") {
    //   if (sunSail.positionMode === "net") {
    //     SandyLogic.checkPositionMode(sunSail);
    //     // return SandyLogic.netToMountPoint3d(sunSail, poleId);
    //     return Poles.getPole(sunSail, poleId).position;
    //   }
    // }
    return Poles.getPole(sunSail, poleId).position;
  }
}